/**
 * Villain & henchman ability execution for the Legendary Arena game engine.
 *
 * executeVillainAbilities applies the locked MVP effect vocabulary for a card at
 * a given timing (onAmbush / onFight / onEscape). It mutates G directly via
 * existing zone helpers and returns a `VillainEffectResult[]` in dispatch order
 * (WP-316) — each result carries the reverse-mapped legacy keyword plus the card
 * ext_id targets the effect touched — so the three fire sites can narrate
 * per-target log lines. `results.map((r) => r.keyword)` reproduces the WP-200
 * keyword surface byte-identically. It is deliberately separate from the global
 * rule-hook pipeline (D-18501). Out-of-vocabulary effects safe-skip silently and
 * are NOT included in the return array.
 *
 * Imports no game framework and no registry package. No .reduce().
 * Uses existing helpers only: gainWound, koCard, moveCardFromZone,
 * attachBystanderToVillain, awardAttachedBystanders.
 */
import { isGreyHero, matchesGreyHeroFilter, offPlayCardStats, offPlayCardTraits, playedCardIdsStillInPlay, resolveSplitFacePair, } from '../hero/splitCard.logic.js';
import { cardHasClassWhenPlayed } from '../hero/sizeChanging.logic.js';
import { cardHasTeamWhenPlayed } from '../hero/effectiveTeams.logic.js';
import { getVillainHooksForCard, descriptorToLegacyKeyword, VILLAIN_EFFECT_PRIMITIVES, } from '../rules/villainAbility.types.js';
import { citySpaceNameForIndex } from '../board/citySpaceNames.js';
import { composeWoundOutcomeClause } from '../events/notableEvents.compose.js';
import { recordHollowEffect } from '../diagnostics/hollowEffect.record.js';
import { recordEffectTrace } from '../diagnostics/effectTrace.record.js';
import { gainWoundWithOutcome } from '../board/wounds.logic.js';
import { seatsInTurnOrderFrom } from '../turn/seatOrder.logic.js';
// why: WP-845 / D-24779 — the reveal-or-gain-a-Wound queue writer. The module reaches back to this
// file through phaseCard → playVillainTop.resolve → villainDeck.reveal; every binding is read
// inside function bodies only, so the cycle is runtime-safe.
import { enqueueRevealOrWoundChoice, formatRevealOrWoundPlayerNames, } from '../moves/revealOrWoundChoice.logic.js';
import { koCard } from '../board/ko.logic.js';
import { recordOwnedCardKoed } from '../hero/deferredConditionalGrants.js';
import { attachBystanderToVillain, awardAttachedBystanders, countVictoryPileBystanders, findFirstVictoryPileBystander, SAVIOR_BYSTANDER_THRESHOLD, } from '../board/bystanders.logic.js';
import { captureHeroFromHq } from '../board/heroCapture.logic.js';
// why: WP-522 / D-24335 — give-hq-hero-by-trait-to-current refills the vacated HQ slot
// from G.heroDeck after removing the gifted Hero, the same refill contract
// captureHeroFromHq uses (leaves null when the reservoir is empty).
import { refillHqSlot } from '../board/city.logic.js';
import { hauntHqSlot, selectUnhauntedHqIndex } from '../board/haunt.logic.js';
import { moveCardFromZone } from '../moves/zoneOps.js';
// why: WP-837 / D-24770 — Juggernaut's same-name discards go through the single
// hand→discard chokepoint so return-on-discard / teleport reactions fire.
import { discardFromHand } from '../moves/discardFromHand.js';
import { reshuffleDiscardIntoDeck, drawCardsIntoHand, HAND_SIZE } from '../moves/drawCards.logic.js';
import { pushLog } from '../log/logPush.js';
import { describeKoSourceZone } from '../log/logDisplay.js';
// why: WP-817 / D-24750 — Captain Zolandia counts Hero Classes with the hero side's one
// counter, and Primus's Escape narrates its costs with the shared cost-list formatter.
import { countDistinctHeroClassesYouHave } from '../hero/heroConditions.evaluate.js';
import { formatEligibleCostList } from '../moves/villainDefeatRequirement.logic.js';
import { WOUND_EXT_ID, SHIELD_AGENT_EXT_ID, SHIELD_TROOPER_EXT_ID, SHIELD_OFFICER_EXT_ID, } from '../setup/pilesInit.js';
/**
 * The zones an unqualified "KO one of your Heroes" may take from: the hand and
 * the cards played this turn (D-24687).
 *
 * The default meaning of a KO that names no zone — an absent `allowedZones` in
 * buildKoEligibleTargets / countKoableHeroes, and an absent `zones` on a
 * PendingKoHeroChoice. Read only as a `??` fallback; never written into G, so a
 * zoneless pending entry keeps its WP-242 shape.
 */
// why: rules v23 L721–L725 — "one of your Heroes" can be "a Hero you've already
// played this turn or a Hero still in your hand"; the discard pile is not one of
// "your Heroes" (D-24687). A printed zone (Juggernaut, Red Skull) still overrides it.
export const YOUR_HEROES_KO_ZONES = ['hand', 'inPlay'];
// ---------------------------------------------------------------------------
// executeVillainAbilities — main entry point
// ---------------------------------------------------------------------------
/**
 * Applies villain/henchman ability effects for a card at a given timing.
 *
 * Called from the Fight fire site (fightVillain.ts, 'onFight'), the Ambush
 * fire site (villainDeck.reveal.ts, 'onAmbush'), and the Escape fire site
 * (villainDeck.reveal.ts, 'onEscape' — WP-186). Looks up the card's hooks
 * for the timing and applies each effect in left-to-right array order.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param ctx - framework context passed as unknown to avoid a game-framework
 *   import. Only ctx.currentPlayer is read.
 * @param cardId - The villain/henchman card-instance ext_id that triggered.
 * @param timing - Which timing fired ('onAmbush', 'onFight', or 'onEscape').
 * @returns The applied effect results in dispatch order (post-safe-skip).
 *   WP-316 widening (D-24102): each result carries the reverse-mapped legacy
 *   `keyword` plus the card ext_id `targets` the effect touched (and `pending`
 *   for a parked interactive KO), so the three fire sites can narrate per-target
 *   log lines. `results.map((r) => r.keyword)` reproduces the WP-200
 *   `VillainEffectKeyword[]` return byte-identically, so the fightResolved /
 *   ambushResolved keyword surface is unchanged. Out-of-vocabulary effects
 *   safe-skip and are NOT included.
 */
export function executeVillainAbilities(G, ctx, cardId, timing, 
// why: WP-478 / D-24285 — the deterministic shuffle source, threaded down to the
// scry handler so it can reshuffle the current player's discard when their deck
// runs dry mid-look (the Legendary reveal-reshuffle rule). Optional: only the
// fire sites that can trigger a deck-reveal effect need supply it, and the scry
// handler no-ops the reshuffle on an empty discard before touching it. `ctx` does
// NOT carry `random` (it is the bare bgio ctx), so the provider is a separate arg.
shuffleContext, 
// why: WP-489 / D-24295 — the 0-based City index the villain was fought on, for
// the universal location gate (Abomination / the Lizard). Trailing-optional
// (mirroring shuffleContext?): only the Fight fire site knows a fought space;
// the Ambush/Escape reveal sites pass undefined, and the gate FAILS CLOSED on
// undefined so a location-gated effect never fires without a fought space.
cityIndex) {
    // why: WP-316 — accumulator captures a result per effect whose handler ran,
    // in dispatch order. Each result pairs the reverse-mapped legacy keyword with
    // the ext_ids the effect touched. Out-of-vocab effects (no handler) are NOT
    // appended; the fire sites see only effects whose mutation was attempted.
    const results = [];
    // why: guard against older test mocks (and pre-WP-185 G states) that lack
    // villainAbilityHooks — mirrors the WP-022 heroAbilityHooks guard. No hooks
    // means no effects.
    if (!G.villainAbilityHooks || G.villainAbilityHooks.length === 0) {
        return results;
    }
    // why: ctx is typed `unknown` and narrowed via `as` to the one field this
    // executor reads — the active player id. The executor is barred from
    // importing the framework's Ctx / FnContext types, exactly as
    // heroEffects.execute.ts narrows `ctx as ShuffleProvider`. All other
    // iteration derives from G.
    const currentPlayer = ctx.currentPlayer;
    // why: WP-257 — the turn number for any hollow record, read off the
    // unknown-typed ctx defensively. boardgame.io's ctx carries `turn`; the
    // villain test CTX object ({ currentPlayer }) does not, so a missing /
    // non-numeric value falls back to 0 — never a throw.
    const turn = readTurnNumber(ctx);
    // why: WP-257 — cardType for the record: a card classified `henchman` in
    // G.villainDeckCardTypes records as `henchman`, everything else (including an
    // absent classification in narrow test mocks) records as `villain`.
    const cardType = resolveVillainCardType(G, cardId);
    const hooks = getVillainHooksForCard(G.villainAbilityHooks, cardId, timing);
    for (const hook of hooks) {
        for (const descriptor of hook.effects) {
            // why: WP-489 / D-24295 — the universal location gate, checked BEFORE handler
            // dispatch. A descriptor carrying `requireCitySpaces` fires only when the
            // villain was fought on a listed space; otherwise skip + self-narrate "no
            // effect". This is NOT hollow (the handler exists — the location blocked it),
            // so it records neither a hollow nor an EffectTrace: the descriptor was never
            // dispatched. Fails CLOSED on an undefined / out-of-range cityIndex (the
            // Ambush/Escape fire sites, which have no fought space).
            if (descriptor.requireCitySpaces !== undefined &&
                !isCityGateSatisfied(descriptor.requireCitySpaces, cityIndex)) {
                narrateCityGateBlocked(G, timing, descriptor.requireCitySpaces);
                continue;
            }
            // why: WP-816 / D-24749 — the condition gate (rules v23 L1844-1853 Savior), checked
            // AFTER the location gate and BEFORE dispatch. It reads the CURRENT player at every
            // timing and fails closed (missing zones, or an unrecognized condition value).
            // Like the location gate it is NOT hollow (the handler exists — the condition
            // blocked it), so it records neither a hollow record nor an EffectTrace (the
            // D-24295 precedent): the descriptor was never dispatched.
            if (descriptor.requireCondition !== undefined &&
                !isConditionGateSatisfied(G, currentPlayer, cardId, timing, descriptor)) {
                continue;
            }
            const application = applyVillainEffect(G, currentPlayer, cardId, timing, descriptor, shuffleContext, cityIndex);
            if (application !== null) {
                // why: D-24023 — each result's `keyword` stays VillainEffectKeyword
                // (reverse-mapped from the dispatched descriptor) so notableEvents,
                // EFFECT_KEYWORD_LABELS, the replay state-hash, and the arena-client
                // projection are byte-identical (WP-316 adds only the hash-excluded
                // targets). Every dispatched descriptor came from a legacy marker, so
                // the reverse-map always resolves; an unresolvable descriptor (none in
                // this WP) is simply not recorded.
                const legacyKeyword = descriptorToLegacyKeyword(descriptor);
                if (legacyKeyword !== undefined) {
                    const result = {
                        keyword: legacyKeyword,
                        targets: application.targets,
                    };
                    // why: WP-316 — carry `pending` only when the handler parked an
                    // interactive KO, so the omit-when-absent shape matches the type's
                    // optional field (no `pending: undefined` noise).
                    if (application.pending === true) {
                        result.pending = true;
                    }
                    if (application.targetsByPlayer !== undefined) {
                        result.targetsByPlayer = application.targetsByPlayer;
                    }
                    // why: D-24693 — omit-when-empty, like `pending`: a KO that took nothing has no zones.
                    if (application.targetZones !== undefined && application.targetZones.length > 0) {
                        result.targetZones = application.targetZones;
                    }
                    // why: D-24673 — present only when a Diving Block reveal is pending.
                    if (application.woundOutcomes !== undefined) {
                        result.woundOutcomes = application.woundOutcomes;
                    }
                    // why: D-24783 — present only on a grey-filtered each-player KO (Destroyer).
                    if (application.greyFilter !== undefined) {
                        result.greyFilter = application.greyFilter;
                    }
                    results.push(result);
                }
            }
            else {
                // why: WP-257 / D-24033 — `application === null` is the out-of-vocabulary
                // skip site: applyVillainEffect reached NO handler for this descriptor.
                // Classify on handler REACHABILITY (never state-diff) and record a hollow
                // event. This is purely additive — results is byte-unchanged (a
                // non-applied descriptor was never recorded there).
                recordHollowEffect(G, buildVillainDescriptorHollowRecord(cardId, cardType, timing, descriptor, turn));
            }
            // why: WP-488 / D-24294 — emit a runtime EffectTrace for EVERY villain descriptor
            // dispatch (both the applied and the null/no-handler branch), from THIS caller loop
            // — it carries `turn` + `timing` + `cardId` + the descriptor + the dispatch result
            // together, which the inner applyVillainEffect does not. Inert + hash-excluded; a
            // no-handler dispatch now produces BOTH a hollow record (above, unchanged) and a trace.
            recordEffectTrace(G, buildVillainEffectTrace(cardType, cardId, timing, descriptor, application, turn));
        }
        // why: WP-257 / D-24034 — an unresolved `[effect:X]` marker the parser saw
        // but could not turn into a descriptor is `parse-unrecognized` hollow. These
        // never produced a descriptor, so they are not in hook.effects above.
        detectVillainUnresolvedMarkers(G, cardId, cardType, hook, turn);
        // why: D-24266 — the printed-but-unmarked gap. This hook exists ONLY because
        // its source ability line began with a recognized timing prefix
        // (Fight:/Ambush:/Escape:), which in Legendary always denotes a mechanical
        // effect — flavor text never carries those prefixes. A hook with zero
        // descriptors AND zero unresolved markers therefore means the data pipeline
        // never annotated the line with any `[effect:]` marker at all, so the printed
        // mechanic is entirely un-implemented (e.g. Doombot Legion's "Fight: Look at
        // the top two cards of your deck. KO one of them and put the other back.").
        // The WP-257 detector only classified markered/descriptored lines, so this
        // whole class fell through silently. Recording it as `no-handler` hollow gives
        // the operator log a breadcrumb instead of nothing. This supersedes WP-188's
        // implicit "marker-free line = silent safe-skip" for villain/henchman timing
        // lines (the parser still emits the hook; the difference is that we now
        // observe it rather than dropping it on the floor).
        detectVillainUnmarkedTimingLine(G, cardId, cardType, hook, turn);
    }
    return results;
}
// ---------------------------------------------------------------------------
// Fire-site name resolution (WP-316)
// ---------------------------------------------------------------------------
/**
 * Resolves each effect result's target ext_ids to display names for the log.
 *
 * Shared by all three fire sites (Fight / Ambush / Escape) — extracted at the
 * third identical copy per the duplicate-first rule. Reads `G.cardDisplayData`
 * (the fire site's to read, D-11106 pattern) so the pure composer stays free of
 * any `G` dependency. The returned `ResolvedEffectResult[]` feeds
 * `composeEffectResultLogLine`.
 *
 * @param G - Game state (read-only here; only `G.cardDisplayData` is consulted).
 * @param results - The executor's per-effect results in dispatch order.
 * @returns The results with each target ext_id resolved to a display name.
 */
export function resolveEffectResultNames(G, results) {
    const resolved = [];
    for (const result of results) {
        const targetNames = [];
        for (let targetIndex = 0; targetIndex < result.targets.length; targetIndex++) {
            targetNames.push(nameKoTarget(G, result.targets[targetIndex], result.targetZones?.[targetIndex]));
        }
        const resolvedResult = {
            keyword: result.keyword,
            targetNames,
        };
        if (result.pending === true) {
            resolvedResult.pending = true;
        }
        // why: D-24683 — carry the each-player KO's per-player split, names resolved the same way.
        if (result.targetsByPlayer !== undefined) {
            const playerTargetNames = [];
            for (const playerShare of result.targetsByPlayer) {
                const names = [];
                for (let targetIndex = 0; targetIndex < playerShare.targets.length; targetIndex++) {
                    names.push(nameKoTarget(G, playerShare.targets[targetIndex], playerShare.zones?.[targetIndex]));
                }
                const resolvedShare = { playerId: playerShare.playerId, names };
                if (playerShare.pending === true) {
                    resolvedShare.pending = true;
                }
                playerTargetNames.push(resolvedShare);
            }
            resolvedResult.playerTargetNames = playerTargetNames;
        }
        if (result.woundOutcomes !== undefined) {
            resolvedResult.woundOutcomes = result.woundOutcomes;
        }
        if (result.greyFilter !== undefined) {
            resolvedResult.greyFilter = result.greyFilter;
        }
        resolved.push(resolvedResult);
    }
    return resolved;
}
/**
 * Names one effect target for the log, adding the zone it was KO'd from when known
 * (D-24693): "S.H.I.E.L.D. Agent from their hand".
 *
 * @param G - Game state (read-only; only `G.cardDisplayData` is consulted).
 * @param targetId - The target card ext_id.
 * @param zone - The zone the card was KO'd from, or undefined for a non-KO target.
 * @returns The display name, with " from <zone>" when a zone is given.
 */
function nameKoTarget(G, targetId, zone) {
    const name = resolveCardDisplayName(G, targetId);
    if (zone === undefined) {
        return name;
    }
    return `${name} from ${describeKoSourceZone(zone)}`;
}
/**
 * Resolves a single card ext_id to its display name, falling back to the raw
 * ext_id when `G.cardDisplayData` has no usable entry.
 *
 * Defensive access mirrors the fightResolved / ambushResolved name resolution:
 * legacy test G states may leave `cardDisplayData` undefined; production setup
 * always builds it.
 *
 * @param G - Game state.
 * @param extId - The card-instance ext_id to name.
 * @returns The display name, or the raw ext_id when none is available.
 */
function resolveCardDisplayName(G, extId) {
    const display = G.cardDisplayData?.[extId];
    if (display && typeof display.name === 'string' && display.name.length > 0) {
        return display.name;
    }
    return extId;
}
// ---------------------------------------------------------------------------
// Hollow-effect detection (WP-257 / D-24033 + D-24034)
// ---------------------------------------------------------------------------
/**
 * Reads the boardgame.io turn number off the (unknown-typed) ctx, defaulting to
 * 0 when absent.
 *
 * Sibling of the helper in heroEffects.execute.ts, but NOT identical: this one
 * reads `ctx.turn` directly because executeVillainAbilities receives the BARE
 * boardgame.io Ctx (its top-level `turn` / `currentPlayer`), whereas the hero
 * executor receives the FnContext wrapper and must read the nested `ctx.ctx.turn`.
 * Duplicate-first per §16.1 — the two executors are independent dispatch surfaces;
 * a third appearance would justify extracting a shape-aware reader. The villain CTX
 * test object carries no `turn`, so a missing / non-numeric value falls back to 0,
 * never a throw. Exported for `defeatCityVillainCore` (D-24704), which reads the same
 * bare ctx to forward the turn into the RevealContext of a Fight villain-deck play.
 *
 * @param ctx - The boardgame.io context, typed unknown to avoid a framework import.
 * @returns The turn number, or 0 when unavailable.
 */
export function readTurnNumber(ctx) {
    if (ctx !== null && typeof ctx === 'object') {
        const turn = ctx.turn;
        if (typeof turn === 'number' && Number.isFinite(turn)) {
            return turn;
        }
    }
    return 0;
}
/**
 * Resolves the HollowEffectRecord cardType for a villain/henchman card.
 *
 * Reads G.villainDeckCardTypes: a card classified `henchman` records as
 * `henchman`; everything else — `villain`, or an absent classification (narrow
 * test mocks build G without the map) — records as `villain`. Never throws.
 *
 * @param G - Game state.
 * @param cardId - The triggering card-instance ext_id.
 * @returns The record cardType ('villain' or 'henchman').
 */
function resolveVillainCardType(G, cardId) {
    const types = G.villainDeckCardTypes;
    if (types !== undefined && types[cardId] === 'henchman') {
        return 'henchman';
    }
    return 'villain';
}
/**
 * Builds the HollowEffectRecord for a villain/henchman descriptor whose handler
 * was unreachable.
 *
 * Classifies the reason by REACHABILITY: a descriptor carrying a non-primitive
 * `primitive` value is `unsupported-keyword` (dispatch cannot execute it); a
 * descriptor with no primitive at all (an out-of-vocab legacy marker that
 * produced an empty descriptor) is `no-handler`. Never diffs G.
 *
 * @param cardId - The triggering card-instance ext_id.
 * @param cardType - The resolved record cardType.
 * @param timing - The timing that fired.
 * @param descriptor - The descriptor whose handler was unreachable.
 * @param turn - The turn number for the record.
 * @returns The hollow-effect record.
 */
function buildVillainDescriptorHollowRecord(cardId, cardType, timing, descriptor, turn) {
    // why: descriptor.primitive is typed VillainEffectPrimitive but a malformed
    // hook (or test cast) can carry an unknown string or none at all; read it
    // defensively to classify the reason.
    const primitiveValue = descriptor.primitive;
    let reason;
    let mechanic;
    if (typeof primitiveValue === 'string' && primitiveValue.length > 0) {
        // why: a present-but-unrecognized primitive is `unsupported-keyword`; a
        // recognized primitive would have had a handler and never reached here.
        reason = isVillainEffectPrimitive(primitiveValue) ? 'no-handler' : 'unsupported-keyword';
        mechanic = primitiveValue;
    }
    else {
        // why: no primitive on the descriptor — an out-of-vocab legacy marker that
        // produced an empty descriptor. No executable handler exists for it.
        reason = 'no-handler';
        mechanic = 'unknown';
    }
    return { cardId, cardType, timing, mechanic, reason, turn };
}
/**
 * Records a `parse-unrecognized` hollow event for each unresolved `[effect:X]`
 * marker on a villain/henchman hook.
 *
 * The parser surfaces markers it saw but could not resolve into a descriptor on
 * hook.unresolvedMarkers (WP-257 / D-24034). A line with no effect marker at all
 * leaves the field absent, so it never reaches here.
 *
 * @param G - Game state (mutated under Immer draft only via recordHollowEffect).
 * @param cardId - The triggering card-instance ext_id.
 * @param cardType - The resolved record cardType.
 * @param hook - The villain ability hook that fired.
 * @param turn - The turn number for the record.
 */
function detectVillainUnresolvedMarkers(G, cardId, cardType, hook, turn) {
    const unresolvedMarkers = hook.unresolvedMarkers ?? [];
    for (const marker of unresolvedMarkers) {
        recordHollowEffect(G, {
            cardId,
            cardType,
            timing: hook.timing,
            mechanic: marker,
            reason: 'parse-unrecognized',
            turn,
        });
    }
}
/**
 * Records a `no-handler` hollow event for a hook whose ability line carried a
 * recognized timing prefix but no machine-readable effect at all (D-24266).
 *
 * A villain/henchman hook is built only for an ability line that begins with
 * `Fight:` / `Ambush:` / `Escape:`, and those prefixes always denote a
 * mechanical effect. When the parser produced neither a descriptor nor an
 * unresolved `[effect:X]` marker, the line was never annotated — the printed
 * mechanic is un-implemented (e.g. Doombot Legion's deck-scry-and-KO Fight). The
 * `mechanic` label is `'unmarked-ability'` because there is no marker token to
 * name. A hook that DID carry an unresolved marker is handled by
 * detectVillainUnresolvedMarkers (`parse-unrecognized`) and is excluded here so a
 * single line never double-records.
 *
 * @param G - Game state (mutated under Immer draft only via recordHollowEffect).
 * @param cardId - The triggering card-instance ext_id.
 * @param cardType - The resolved record cardType.
 * @param hook - The villain ability hook that fired.
 * @param turn - The turn number for the record.
 */
function detectVillainUnmarkedTimingLine(G, cardId, cardType, hook, turn) {
    const hasDescriptor = hook.effects.length > 0;
    const hasUnresolvedMarker = (hook.unresolvedMarkers?.length ?? 0) > 0;
    if (hasDescriptor || hasUnresolvedMarker) {
        return;
    }
    recordHollowEffect(G, {
        cardId,
        cardType,
        timing: hook.timing,
        mechanic: 'unmarked-ability',
        reason: 'no-handler',
        turn,
    });
}
/**
 * Returns whether a string is a valid VillainEffectPrimitive.
 *
 * Splits `no-handler` (a recognized primitive that somehow lacks a handler —
 * unreachable in practice but future-proof) from `unsupported-keyword` (an
 * unrecognized primitive token).
 *
 * @param value - The candidate primitive string.
 * @returns Whether the value is a member of VILLAIN_EFFECT_PRIMITIVES.
 */
function isVillainEffectPrimitive(value) {
    for (const primitive of VILLAIN_EFFECT_PRIMITIVES) {
        if (primitive === value) {
            return true;
        }
    }
    return false;
}
// ---------------------------------------------------------------------------
// City-space location gate (WP-489 / D-24295)
// ---------------------------------------------------------------------------
/**
 * Returns whether the fought City index satisfies a descriptor's location gate.
 *
 * Fails CLOSED: an undefined / out-of-range `cityIndex` (the Ambush/Escape fire
 * sites, which have no fought space) resolves to `undefined` and matches no
 * listed space, so a location-gated effect never fires without a fought space.
 *
 * @param requireCitySpaces - The named spaces the effect is gated on.
 * @param cityIndex - The 0-based City index fought, or undefined at a non-fight site.
 * @returns True when the fought space is one of the listed spaces.
 */
function isCityGateSatisfied(requireCitySpaces, cityIndex) {
    const foughtSpace = citySpaceNameForIndex(cityIndex);
    if (foughtSpace === undefined) {
        return false;
    }
    for (const required of requireCitySpaces) {
        if (required === foughtSpace) {
            return true;
        }
    }
    return false;
}
/**
 * Self-narrates a "no effect — wrong space" line when a location-gated effect is
 * skipped (WP-489 / D-24295).
 *
 * The gated effect is keyword-less and never dispatched, so without this push the
 * skip would be invisible in the log. `G.messages` is hash-excluded (D-24081), so
 * this adds no replay surface. Outcome `blocked` per the WP-434 contract (the
 * effect touched no one).
 *
 * @param G - Game state (mutated only via the hash-excluded message log).
 * @param timing - The timing that fired (for the "<Fight|Ambush|Escape> effect:" label).
 * @param requireCitySpaces - The spaces the effect required (named in the line).
 */
function narrateCityGateBlocked(G, timing, requireCitySpaces) {
    const label = villainEffectTimingLabel(timing);
    pushLog(G, `${label} effect: not fought on ${formatCitySpaceList(requireCitySpaces)}; no effect.`, 'blocked');
}
/**
 * Evaluates a descriptor's `requireCondition` gate, and self-narrates the "no
 * effect" line when it blocks (WP-816 / D-24749).
 *
 * Exhaustive over `VillainEffectCondition`: `'savior'` passes when the current
 * player has at least `SAVIOR_BYSTANDER_THRESHOLD` Bystanders in their Victory
 * Pile (0 when their zones are missing); `'another-group-villain-in-city'`
 * (D-24770) passes when the City holds another Villain of the triggering card's
 * own group. The `never` default (a future union member before its arm exists, or
 * a hand-built hook) fails CLOSED. `G.messages` is hash-excluded (D-24081);
 * outcome `blocked` per the WP-434 contract.
 *
 * @param G - Game state (mutated only via the hash-excluded message log).
 * @param currentPlayer - The active player id the condition is read for.
 * @param cardId - The triggering Villain's ext_id (the group condition reads it).
 * @param timing - The timing that fired (for the "<Fight|Ambush|Escape> effect:" label).
 * @param descriptor - The gated descriptor (its `requireCondition` is defined).
 * @returns True when the condition holds and the descriptor may dispatch.
 */
function isConditionGateSatisfied(G, currentPlayer, cardId, timing, descriptor) {
    const label = villainEffectTimingLabel(timing);
    // why: the executor loop calls this only when `requireCondition` is defined, so the
    // switch below is exhaustive over the closed union (a new member fails to compile here).
    const condition = descriptor.requireCondition;
    switch (condition) {
        case 'savior': {
            const zones = G.playerZones[currentPlayer];
            const count = zones ? countVictoryPileBystanders(zones.victory) : 0;
            if (zones && count >= SAVIOR_BYSTANDER_THRESHOLD) {
                return true;
            }
            pushLog(G, `${label} effect: Player ${currentPlayer} is not a Savior (${count} of ${SAVIOR_BYSTANDER_THRESHOLD} Bystanders in their Victory Pile); no effect.`, 'blocked');
            return false;
        }
        case 'another-group-villain-in-city': {
            // why: WP-837 / D-24770 — "another Spider-Foes Villain" is a Villain of the
            // triggering card's OWN group (the D-24299 own-set precedent). The marker has no slot
            // for a group, so the group is the longest selected-group prefix of the card id; no
            // match fails closed (a scheme-added group, or a hand-built hook).
            const group = resolveVillainGroupPrefix(G, cardId);
            if (group === null) {
                pushLog(G, `${label} effect: the Villain's group is not among this match's Villain Groups; no effect.`, 'blocked');
                return false;
            }
            if (cityHoldsOtherGroupVillain(G, group.groupPrefix, cardId)) {
                return true;
            }
            pushLog(G, `${label} effect: no other ${group.groupSlug} Villain is in the City; no effect.`, 'blocked');
            return false;
        }
        default: {
            // why: fail closed — an unrecognized condition must never fire its effect.
            const unrecognized = condition;
            pushLog(G, `${label} effect: unrecognized condition '${String(unrecognized)}'; no effect.`, 'blocked');
            return false;
        }
    }
}
/**
 * Finds the triggering Villain's own group among the match's selected Villain Groups
 * (WP-837 / D-24770).
 *
 * Each two-part `set/slug` id in `G.selection.villainGroupIds` gives the anchored prefix
 * `${set}-villain-${slug}-`; the longest prefix the card id starts with wins, so a group
 * whose slug extends another's (`foes` vs `foes-elite`) resolves to the more specific one.
 *
 * @param G - Game state (read-only).
 * @param cardId - The Villain instance ext_id.
 * @returns The group slug and its anchored prefix, or null when no selected group matches.
 */
function resolveVillainGroupPrefix(G, cardId) {
    let bestMatch = null;
    for (const groupId of G.selection?.villainGroupIds ?? []) {
        const segments = groupId.split('/');
        if (segments.length !== 2) {
            continue;
        }
        const groupPrefix = `${segments[0]}-villain-${segments[1]}-`;
        if (!cardId.startsWith(groupPrefix)) {
            continue;
        }
        if (bestMatch === null || groupPrefix.length > bestMatch.groupPrefix.length) {
            bestMatch = { groupSlug: segments[1], groupPrefix };
        }
    }
    return bestMatch;
}
/**
 * Whether the City holds a Villain of the group other than the triggering card
 * (WP-837 / D-24770 — "another" excludes the card itself; a second copy counts).
 *
 * @param G - Game state (read-only).
 * @param groupPrefix - The anchored `${set}-villain-${slug}-` prefix.
 * @param cardId - The triggering Villain (excluded from the scan).
 * @returns True when another occupied City space starts with the prefix.
 */
function cityHoldsOtherGroupVillain(G, groupPrefix, cardId) {
    for (let cityIndex = 0; cityIndex < G.city.length; cityIndex++) {
        const occupant = G.city[cityIndex];
        if (occupant !== null && occupant !== undefined && occupant !== cardId && occupant.startsWith(groupPrefix)) {
            return true;
        }
    }
    return false;
}
/**
 * Formats a City-space list into readable prose with a single leading "the"
 * (e.g. `['streets','bridge']` → "the Streets or Bridge"; `['sewers']` → "the
 * Sewers").
 *
 * @param spaces - The named City spaces (already validated at parse time).
 * @returns The prose list for a log line.
 */
function formatCitySpaceList(spaces) {
    const capitalized = [];
    for (const space of spaces) {
        capitalized.push(space.charAt(0).toUpperCase() + space.slice(1));
    }
    if (capitalized.length === 1) {
        return `the ${capitalized[0]}`;
    }
    const last = capitalized[capitalized.length - 1];
    const rest = capitalized.slice(0, capitalized.length - 1).join(', ');
    return `the ${rest} or ${last}`;
}
// ---------------------------------------------------------------------------
// Effect-trace emission (WP-488 / D-24294)
// ---------------------------------------------------------------------------
// why: WP-488 / D-24294 — the FIXED allowlist of villain primitives whose handlers are
// DELIBERATE no-ops that mutate nothing (become-scheme-twist, D-24287 — the real twist
// fires at the escape site; gain-attached-hero, D-24270 — the award is the generic
// WP-431 awardAttachedHeroes at the fight site). A trace for either must read `no-op`,
// decided by PRIMITIVE IDENTITY — NEVER by `targets.length`, because many real-firing
// handlers (gain-wound, capture-bystander, reveal-or-wound, draw-cards-current,
// rescue-bystanders-current, scry-ko-own-deck) legitimately return empty `targets` and
// MUST read as `fired`. Adding a future deliberate-no-op handler requires adding it here,
// or its trace mislabels as `fired`.
// why: WP-542 / D-24351 — `play-villain-deck-cards` is the third deliberate no-op: its
// executor handler mutates nothing (the real reveal fires from the onAmbush / onFight fire
// sites, where the RevealContext + implementationMap are in scope). It MUST be listed here or
// its trace would mislabel `fired` — the same reason become-scheme-twist / gain-attached-hero
// are here.
const DELIBERATE_NO_OP_VILLAIN_PRIMITIVES = new Set([
    'become-scheme-twist',
    'gain-attached-hero',
    'play-villain-deck-cards',
]);
/**
 * Builds the per-dispatch `EffectTrace` for one villain/henchman descriptor
 * (WP-488 / D-24294).
 *
 * Status is decided by handler reachability + the fixed no-op allowlist, never by
 * mutation: a `null` application → `no-handler` (co-recorded as a hollow); a
 * deliberate-no-op primitive → `no-op`; any other handler that ran → `fired`. The
 * `handler` label is the primitive token (the VILLAIN_EFFECT_HANDLERS map key) when a
 * handler ran, `""` when none — a STRING label, never a function reference (G forbids
 * functions). `effect` is the primitive token verbatim, read defensively so a malformed
 * descriptor cannot throw before the guarded writer runs.
 *
 * @param cardType - The resolved scope ('villain' | 'henchman').
 * @param cardId - The triggering card-instance ext_id.
 * @param timing - The timing that fired ('onAmbush' | 'onFight' | 'onEscape').
 * @param descriptor - The dispatched descriptor.
 * @param application - The dispatch result (null when no handler was reached).
 * @param turn - The framework turn number for the trace (per readTurnNumber).
 * @returns The effect trace to record.
 */
function buildVillainEffectTrace(cardType, cardId, timing, descriptor, application, turn) {
    // why: descriptor.primitive is typed but a malformed hook / test cast can carry a
    // non-string; read it defensively so the trace build never throws (the emit is
    // best-effort — a trace bug must never break an ability execution).
    const primitiveValue = descriptor.primitive;
    const effectToken = typeof primitiveValue === 'string' ? primitiveValue : '';
    let status;
    let handler;
    if (application === null) {
        // why: applyVillainEffect reached no handler for this primitive.
        status = 'no-handler';
        handler = '';
    }
    else if (typeof primitiveValue === 'string' &&
        DELIBERATE_NO_OP_VILLAIN_PRIMITIVES.has(primitiveValue)) {
        // why: a deliberate-no-op handler ran — decided by primitive identity, never targets.length.
        status = 'no-op';
        handler = effectToken;
    }
    else {
        status = 'fired';
        handler = effectToken;
    }
    return {
        cardId,
        scope: cardType,
        timing,
        effect: effectToken,
        handler,
        status,
        fireSite: 'villain-executor',
        params: buildVillainEffectTraceParams(descriptor),
        turn,
    };
}
/**
 * Copies a villain descriptor's own SCALAR parameter fields into a trace `params`
 * snapshot, omitting `undefined` keys (WP-488 / D-24294).
 *
 * Explicit field-by-field copy — NEVER a spread-and-cast of the raw descriptor, which
 * would leak the non-scalar `primitive` (and break `exactOptionalPropertyTypes`). Every
 * copied value is `string | number | boolean`; the `primitive` token is carried as the
 * trace's `effect`, not here.
 *
 * @param descriptor - The dispatched descriptor.
 * @returns A shallow scalar snapshot of the descriptor's parameter fields.
 */
function buildVillainEffectTraceParams(descriptor) {
    const params = {};
    if (descriptor.target !== undefined) {
        params.target = descriptor.target;
    }
    if (descriptor.magnitude !== undefined) {
        params.magnitude = descriptor.magnitude;
    }
    if (descriptor.selector !== undefined) {
        params.selector = descriptor.selector;
    }
    if (descriptor.drawCount !== undefined) {
        params.drawCount = descriptor.drawCount;
    }
    if (descriptor.requireKind !== undefined) {
        params.requireKind = descriptor.requireKind;
    }
    if (descriptor.requireValue !== undefined) {
        params.requireValue = descriptor.requireValue;
    }
    if (descriptor.zone !== undefined) {
        params.zone = descriptor.zone;
    }
    return params;
}
/**
 * gain-wound primitive — every player or the current player gains 1 wound.
 *
 * `target: 'each'` is verbatim from the former gainWoundEachPlayer case;
 * `target: 'current'` is verbatim from gainWoundCurrentPlayer (WP-185 / WP-200).
 * Only the dispatch shape changed (WP-252).
 */
function villainEffectGainWound(G, currentPlayer, _cardId, timing, descriptor) {
    // why: WP-816 / D-24749 — Radioactive Man "each player who is not a Savior gains a
    // Wound". The exemption is per player, so it is a target (the `each-other`
    // precedent), not the current-player condition gate; its body is its own function.
    if (descriptor.target === 'each-non-savior') {
        return applyGainWoundEachNonSavior(G, currentPlayer, timing);
    }
    // why: WP-489 / D-24295 — the Lizard "each OTHER player gains a Wound": wound
    // every player EXCEPT the current one. Clockwise from the current player (D-24780),
    // supply-bounded, `magnitude` repetitions (default 1) mirroring ko-hero:each so a
    // future N-wound each-other line is data-only. Keyword-less (no legacy reverse-
    // map, see descriptorKey), so it MUST self-narrate — the generic
    // "<timing> effect:" line never fires for it.
    if (descriptor.target === 'each-other') {
        const repetitions = descriptor.magnitude ?? 1;
        const woundedPlayerIds = [];
        const gainedPlayerIds = [];
        const divingBlockPendingPlayerIds = [];
        // why: rules v23 L2247 — start with the current player, then clockwise (D-24780).
        for (const playerId of seatsInTurnOrderFrom(Object.keys(G.playerZones), currentPlayer)) {
            // why: "each OTHER player" — the current player is never wounded by this effect.
            if (playerId === currentPlayer)
                continue;
            const zones = G.playerZones[playerId];
            if (!zones)
                continue;
            let woundedThisPlayer = false;
            let isDivingBlockPending = false;
            for (let iteration = 0; iteration < repetitions; iteration++) {
                // why: supply-bounded — stop once the shared Wound pile is empty (a
                // reachable no-op, never a hollow record).
                if (G.piles.wounds.length === 0)
                    break;
                // why: WP-682 / D-24499 — route through the gainWoundForPlayer chokepoint so a
                // wounded player holding Diving Block gets the reveal window (Master Strike /
                // scheme wounds reach NON-active seats via the WP-684 seat-choice wave).
                if (gainWoundWithOutcome(G, playerId) === 'diving-block-pending') {
                    isDivingBlockPending = true;
                }
                woundedThisPlayer = true;
            }
            if (woundedThisPlayer) {
                woundedPlayerIds.push(playerId);
                if (isDivingBlockPending) {
                    divingBlockPendingPlayerIds.push(playerId);
                }
                else {
                    gainedPlayerIds.push(playerId);
                }
            }
        }
        // why: self-narrate (keyword-less). `G.messages` is hash-excluded (D-24081).
        // Honest outcome per the WP-434 contract: a wound landed → `applied`; no other
        // player could be wounded (solo, or empty supply) → `blocked`. woundsDrawn is
        // NOT bumped — the current player is never wounded by each-other.
        const label = villainEffectTimingLabel(timing);
        if (divingBlockPendingPlayerIds.length > 0) {
            // why: D-24673 — a Diving Block reveal is pending, so that Wound is not gained yet.
            pushLog(G, `${label} effect: each other player gains a Wound: ${composeWoundOutcomeClause(gainedPlayerIds, divingBlockPendingPlayerIds)}.`, 'applied');
        }
        else if (woundedPlayerIds.length > 0) {
            const names = woundedPlayerIds.map((playerId) => `Player ${playerId}`).join(', ');
            pushLog(G, `${label} effect: each other player gained a Wound (${names}).`, 'applied');
        }
        else {
            pushLog(G, `${label} effect: no other player gained a Wound.`, 'blocked');
        }
        return { targets: [] };
    }
    // why: WP-316 — wounds narrate via the generic keyword label, not a card
    // name; targets stays [] for both branches.
    if (descriptor.target === 'each') {
        const gainedPlayerIds = [];
        const divingBlockPendingPlayerIds = [];
        // why: every player gains 1 wound (subject to wound-pile availability),
        // mirroring the existing escape-wound no-op-on-empty semantics.
        // why: rules v23 L2247 — start with the current player, then clockwise (D-24780).
        for (const playerId of seatsInTurnOrderFrom(Object.keys(G.playerZones), currentPlayer)) {
            const zones = G.playerZones[playerId];
            if (!zones)
                continue;
            if (G.piles.wounds.length === 0)
                continue;
            // why: WP-682 / D-24499 — gainWoundForPlayer chokepoint so Diving Block sees
            // every "each player gains a Wound" source (non-active seats via WP-684).
            const woundOutcome = gainWoundWithOutcome(G, playerId);
            if (woundOutcome === 'diving-block-pending') {
                divingBlockPendingPlayerIds.push(playerId);
            }
            else if (woundOutcome === 'gained') {
                gainedPlayerIds.push(playerId);
            }
            if (playerId === currentPlayer) {
                // why: woundsDrawn projects the current player's wounds only (UI
                // economy), matching escape-wound and the deleted Ambush loop.
                G.turnEconomy.woundsDrawn += 1;
            }
        }
        // why: D-24673 — report the split only when a Diving Block reveal is pending, so
        // the keyword-labelled line and narrative are byte-identical otherwise.
        if (divingBlockPendingPlayerIds.length > 0) {
            return { targets: [], woundOutcomes: { gainedPlayerIds, divingBlockPendingPlayerIds } };
        }
        return { targets: [] };
    }
    // why: target === 'current'. WP-200 — mutation-guarded short-circuit still
    // counts as "applied" per the post-safe-skip contract (the dispatcher records
    // applied by primitive presence, not by mutation). The empty-pile /
    // missing-zone guards short-circuit body work, not the dispatch.
    const zones = G.playerZones[currentPlayer];
    if (!zones)
        return { targets: [] };
    if (G.piles.wounds.length === 0)
        return { targets: [] };
    // why: WP-682 / D-24499 — gainWoundForPlayer chokepoint (the current player may
    // hold Diving Block for a villain self-wound too).
    const woundOutcome = gainWoundWithOutcome(G, currentPlayer);
    G.turnEconomy.woundsDrawn += 1;
    // why: D-24673 — present only when a Diving Block reveal is pending.
    if (woundOutcome === 'diving-block-pending') {
        return { targets: [], woundOutcomes: { gainedPlayerIds: [], divingBlockPendingPlayerIds: [currentPlayer] } };
    }
    return { targets: [] };
}
/**
 * gain-wound `target: 'each-non-savior'` — every player who is not a Savior gains
 * one Wound (Radioactive Man, WP-816 / D-24749).
 *
 * Clockwise from the current player (rules v23 L2247, D-24780); a player with at least
 * `SAVIOR_BYSTANDER_THRESHOLD` Bystanders in their Victory Pile is skipped;
 * supply-bounded. Routes through `gainWoundWithOutcome` so Diving Block sees the
 * Wound (D-24499 / D-24673). Keyword-less (no legacy reverse-map), so it
 * self-narrates; `G.messages` is hash-excluded (D-24081).
 *
 * @param G - Game state (mutated: wounds supply, discards, woundsDrawn, messages).
 * @param currentPlayer - The active player id (for the woundsDrawn projection).
 * @param timing - The timing that fired (for the "<Fight|Escape> effect:" label).
 * @returns `{ targets: [] }` always — a keyword-less result is never recorded.
 */
function applyGainWoundEachNonSavior(G, currentPlayer, timing) {
    const woundedPlayerIds = [];
    const gainedPlayerIds = [];
    const divingBlockPendingPlayerIds = [];
    // why: rules v23 L2247 — start with the current player, then clockwise (D-24780).
    for (const playerId of seatsInTurnOrderFrom(Object.keys(G.playerZones), currentPlayer)) {
        const zones = G.playerZones[playerId];
        if (!zones)
            continue;
        // why: the per-player Savior exemption — each player's OWN Victory Pile decides.
        if (countVictoryPileBystanders(zones.victory) >= SAVIOR_BYSTANDER_THRESHOLD)
            continue;
        // why: supply-bounded — an empty Wound pile is a reachable no-op, never a throw.
        if (G.piles.wounds.length === 0)
            continue;
        const woundOutcome = gainWoundWithOutcome(G, playerId);
        if (playerId === currentPlayer) {
            // why: woundsDrawn projects the current player's wounds only, whatever the
            // outcome — the `'each'` branch rule verbatim.
            G.turnEconomy.woundsDrawn += 1;
        }
        if (woundOutcome === 'diving-block-pending') {
            woundedPlayerIds.push(playerId);
            divingBlockPendingPlayerIds.push(playerId);
        }
        else if (woundOutcome === 'gained') {
            woundedPlayerIds.push(playerId);
            gainedPlayerIds.push(playerId);
        }
    }
    const label = villainEffectTimingLabel(timing);
    if (divingBlockPendingPlayerIds.length > 0) {
        // why: D-24673 — a Diving Block reveal is pending, so that Wound is not final yet.
        pushLog(G, `${label} effect: each player who is not a Savior gains a Wound: ${composeWoundOutcomeClause(gainedPlayerIds, divingBlockPendingPlayerIds)}.`, 'applied');
    }
    else if (woundedPlayerIds.length > 0) {
        const names = woundedPlayerIds.map((playerId) => `Player ${playerId}`).join(', ');
        pushLog(G, `${label} effect: each player who is not a Savior gained a Wound (${names}).`, 'applied');
    }
    else {
        pushLog(G, `${label} effect: no player who is not a Savior gained a Wound.`, 'blocked');
    }
    return { targets: [] };
}
/**
 * ko-bystander-each-victory-pile primitive — each player KOs one Bystander from
 * their Victory Pile (Executioner's Escape, WP-816 / D-24749).
 *
 * For every player in sorted id order, the first Bystander in Victory-Pile order
 * moves to `G.ko` via `moveCardFromZone`; a player with none is untouched.
 * No-param, keyword-less, self-narrates; never throws.
 *
 * @param G - Game state (mutated: Victory Piles, the KO pile, messages).
 * @param _currentPlayer - Unused; the effect hits every player.
 * @param _cardId - Unused.
 * @param timing - The timing that fired (for the "<Fight|Escape> effect:" label).
 * @returns `{ targets: [] }` always — a keyword-less result is never recorded.
 */
function villainEffectKoBystanderEachVictoryPile(G, _currentPlayer, _cardId, timing) {
    const koedPlayerIds = [];
    for (const playerId of Object.keys(G.playerZones).sort()) {
        const zones = G.playerZones[playerId];
        if (!zones)
            continue;
        // why: every Victory-Pile Bystander is worth the same (1 VP, its rescue ability
        // already resolved), so pile order is a deterministic no-choice pick that needs no
        // non-active-seat prompt (the Whispers and Lies precedent).
        const bystanderId = findFirstVictoryPileBystander(zones.victory);
        if (bystanderId === undefined)
            continue;
        const moveResult = moveCardFromZone(zones.victory, G.ko, bystanderId);
        zones.victory = moveResult.from;
        G.ko = moveResult.to;
        koedPlayerIds.push(playerId);
    }
    const label = villainEffectTimingLabel(timing);
    if (koedPlayerIds.length > 0) {
        const names = koedPlayerIds.map((playerId) => `Player ${playerId}`).join(', ');
        pushLog(G, `${label} effect: KO'd a Bystander from the Victory Pile of ${names}.`, 'applied');
    }
    else {
        pushLog(G, `${label} effect: no player had a Bystander in their Victory Pile to KO.`, 'blocked');
    }
    return { targets: [] };
}
/**
 * ko-hero primitive — KO a hero for the current player (interactive) or for
 * every player (auto-resolved, magnitude-many).
 *
 * `target: 'current'` is verbatim from the former koHeroCurrentPlayer case (the
 * WP-242 interactive park). `target: 'each'` is the koHeroEachPlayer (magnitude
 * 1) and koHeroEachPlayerMag2 (magnitude 2) cases generalized to
 * descriptor.magnitude (WP-252 / D-24023).
 */
function villainEffectKoHero(G, currentPlayer, _cardId, timing, descriptor) {
    if (descriptor.target === 'current') {
        // why: WP-492 / D-24298 — magnitude-M interactive current-player KO. M = 1 is
        // the byte-identical WP-242 path (0 eligible → no-op; exactly 1 distinct option
        // → auto-KO, D-24007 decision C; ≥ 2 → park ONE bare {choiceType,playerID}
        // entry). M ≥ 2 (Whirlwind "KO two of your Heroes") KOs up to M: auto-KO every
        // FORCED step (distinct-eligible options O ≤ 1) and, once a genuine choice
        // remains (O ≥ 2), park ONE entry carrying the owed count — the resolve move
        // auto-resolves any later forced remainder, so a single-option pick is never
        // shown. Because a KO never GROWS O, one call is either all-auto (O ≤ 1
        // throughout) or an immediate park (O ≥ 2 at the first step).
        const zones = G.playerZones[currentPlayer];
        if (!zones)
            return { targets: [] };
        const magnitude = descriptor.magnitude ?? 1;
        const targets = [];
        // why: D-24693 — the zone each auto-KO took from, index-aligned with targets, for the log.
        const targetZones = [];
        let owed = magnitude;
        while (owed > 0) {
            const eligible = buildKoEligibleTargets(zones);
            if (eligible.length === 0)
                break; // why: no heroes left to KO.
            // why: a genuine choice of WHICH heroes to KO exists only when the player has
            // MORE KO-able heroes than the count owed (they get to spare some) AND ≥ 2
            // distinct options exist. Otherwise the KO is forced (every hero dies, or all
            // copies are identical), so auto-resolve it with no prompt.
            if (countKoableHeroes(zones) > owed && eligible.length >= 2)
                break;
            const koedId = koSingleTarget(G, zones, eligible[0], currentPlayer);
            if (koedId === null)
                break; // why: defensive — an unexpected move miss stops progress.
            targets.push(koedId);
            targetZones.push(eligible[0].zone);
            owed -= 1;
        }
        let parked = false;
        if (owed > 0 && countKoableHeroes(zones) > owed && buildKoEligibleTargets(zones).length >= 2) {
            if (!G.pendingKoHeroChoices)
                G.pendingKoHeroChoices = [];
            const entry = { choiceType: 'ko-hero', playerID: currentPlayer };
            // why: WP-492 / D-24298 — OMIT `remaining` when owed === 1 (absent ≡ 1), so
            // the M=1 park is the exact {choiceType,playerID} object the WP-242 shape
            // tests pin (byte-identical). It carries the owed count only for a M ≥ 2 park.
            if (owed >= 2) {
                entry.remaining = owed;
            }
            G.pendingKoHeroChoices.push(entry);
            parked = true;
        }
        // why: WP-492 / D-24298 — M ≥ 2 is keyword-less (descriptorKey includes
        // magnitude → no LEGACY_VILLAIN_KEYWORD_TO_DESCRIPTOR entry → reverse-maps to
        // undefined) so it self-narrates; M = 1 keeps the koHeroCurrentPlayer keyword
        // and narrates via the generic Fight-effect line — it MUST NOT self-narrate
        // (that would double-log). `G.messages` is hash-excluded (D-24081).
        if (magnitude >= 2) {
            const label = villainEffectTimingLabel(timing);
            if (parked) {
                pushLog(G, `${label} effect: KO ${String(owed)} of your Heroes — choose which.`, 'neutral');
            }
            else if (targets.length > 0) {
                const names = targets.map((koedId, targetIndex) => nameKoTarget(G, koedId, targetZones[targetIndex])).join(', ');
                pushLog(G, `${label} effect: KO'd ${String(targets.length)} of your Heroes (${names}).`, 'applied');
            }
            else {
                pushLog(G, `${label} effect: no Heroes to KO.`, 'blocked');
            }
        }
        // why: WP-316 / D-24102 — pending: true marks a parked interactive KO (targets
        // stays whatever was auto-KO'd before the park — none, since a park is immediate).
        return parked ? { targets, pending: true, targetZones } : { targets, targetZones };
    }
    // why: target === 'each'. Iteration order is Object.keys(G.playerZones).sort()
    // — default JavaScript string compare → lexical ascending (D-18902), NOT
    // insertion order and NOT a numeric sort. The shared per-player resolver owns
    // target selection AND the koCard mutation; this caller MUST NOT post-process
    // or modify the resolver's output (D-18902 mutation-location lock).
    // descriptor.magnitude drives the per-player repetition: 1 == the former
    // koHeroEachPlayer, 2 == koHeroEachPlayerMag2. The literal-2 inner loop is now
    // a descriptor param (D-24023 retiring D-20201's closed-union-per-magnitude);
    // a future Mag3 is data-only (magnitude: 3), no code change. Per-iteration
    // semantics are koOneHeroForPlayer's (hand + inPlay only per D-24687, starter-first
    // tie-break, silent no-op for zero eligible). Magnitude-N ≡ magnitude-1 N times —
    // pinned by the shared-resolver parity test on a single-player G.
    const repetitions = descriptor.magnitude ?? 1;
    const playerIds = Object.keys(G.playerZones).sort();
    // why: WP-316 — collect the KO'd ext_ids across every player/iteration in the
    // same order the resolver mutates G.ko; a zero-eligible iteration returns null
    // and contributes no target. This only reads the resolver's return — it does
    // NOT post-process the mutation (D-18902 mutation-location lock preserved).
    const targets = [];
    // why: D-24693 — the zone each KO took from, index-aligned with targets.
    const targetZones = [];
    // why: D-24683 — each player's share, in the same seat order, so the log line says who
    // lost which Heroes ("Player 0 KO'd …; Player 1 had no hero to KO") instead of a flat list.
    const targetsByPlayer = [];
    let parked = false;
    for (const playerId of playerIds) {
        const playerShare = { playerId, targets: [] };
        // why: D-24693 — the share's zones, attached only when this player lost a card.
        const playerZonesKoed = [];
        targetsByPlayer.push(playerShare);
        // why: D-24280 — a `zone`-bearing descriptor (Juggernaut's discard/hand
        // source-restricted KO) uses the zone-locked resolver, which KOs `repetitions`
        // heroes from ONLY that zone (no cross-zone fallback). Absent zone is the
        // "your Heroes" path (koOneHeroForPlayer, per-iteration; hand + inPlay, D-24687).
        if (descriptor.zone !== undefined) {
            // why: D-24644 — "Each player KOs two Heroes from their discard pile" is each
            // player's own choice, so the CURRENT player picks (the D-24386 Red Skull split);
            // other players still auto-pick, because pending choices are current-player
            // scoped (D-24284). A forced KO (no more Heroes than owed, or every copy
            // identical) auto-resolves with no prompt.
            // why: D-24769 — Destroyer's `greyFilter` ('non-grey') rides into both the park and
            // the auto-pick; absent, both take today's unfiltered path.
            if (playerId === currentPlayer &&
                parkZoneKoChoice(G, playerId, descriptor.zone, repetitions, descriptor.greyFilter)) {
                parked = true;
                playerShare.pending = true;
                continue;
            }
            const koedIds = koHeroesFromZoneForPlayer(G, playerId, descriptor.zone, repetitions, descriptor.greyFilter);
            for (const koedId of koedIds) {
                targets.push(koedId);
                targetZones.push(descriptor.zone);
                playerShare.targets.push(koedId);
                playerZonesKoed.push(descriptor.zone);
            }
        }
        else {
            // why: D-24669 — "Each player KOs two of their Heroes" (Destroyer's Escape) and
            // "…one of their Heroes" (Super-Skrull's Fight) are each player's own choice, the
            // reading D-24644 applied to Juggernaut and D-24386 to Red Skull. The current
            // player picks when they have a real choice; other players still auto-pick
            // (D-24284). A forced KO falls through to the auto-resolve below.
            if (playerId === currentPlayer && parkZoneKoChoice(G, playerId, undefined, repetitions)) {
                parked = true;
                playerShare.pending = true;
                continue;
            }
            for (let iteration = 0; iteration < repetitions; iteration++) {
                const koed = koOneHeroForPlayer(G, playerId);
                if (koed !== null) {
                    targets.push(koed.cardId);
                    targetZones.push(koed.zone);
                    playerShare.targets.push(koed.cardId);
                    playerZonesKoed.push(koed.zone);
                }
            }
        }
        if (playerZonesKoed.length > 0) {
            playerShare.zones = playerZonesKoed;
        }
    }
    // why: D-24644 — `targets` still names the other players' auto-KOs; pending marks
    // the current player's parked pick (named at resolve time by resolveKoHeroChoice).
    const application = parked
        ? { targets, pending: true, targetsByPlayer, targetZones }
        : { targets, targetsByPlayer, targetZones };
    // why: D-24783 — a grey-filtered KO (Destroyer's 'non-grey') says so in the log
    // ("had no non-grey Hero to KO"); absent, the application is byte-identical.
    if (descriptor.greyFilter !== undefined) {
        application.greyFilter = descriptor.greyFilter;
    }
    return application;
}
/**
 * Parks the current player's "KO N Heroes" choice for an each-player KO when they have
 * a real one: more Heroes than the count owed AND at least two distinct options.
 * Otherwise returns false and the caller auto-resolves the forced KO.
 *
 * With a `zone` it is the zone-locked "KO N Heroes from your <zone>" choice (D-24644,
 * Juggernaut). Without one it is the unqualified "KO N of your Heroes" choice
 * (D-24669, Destroyer's Escape / Super-Skrull's Fight), parked with no `zones` field, so
 * the existing resolve offers the Heroes in hand and in play (YOUR_HEROES_KO_ZONES,
 * D-24687).
 *
 * @param G - Game state (the pending queue is lazily created on a park).
 * @param playerId - The current player.
 * @param zone - The zone the printed effect restricts the KO to, or undefined for none.
 * @param magnitude - How many Heroes the player must KO.
 * @param greyFilter - An optional grey-Hero filter on the KO (D-24769, Destroyer).
 * @returns True when a pending choice was parked.
 */
function parkZoneKoChoice(G, playerId, zone, magnitude, greyFilter) {
    const zones = G.playerZones[playerId];
    if (!zones)
        return false;
    const allowedZones = zone === undefined ? undefined : [zone];
    const greyFilterRead = greyFilter === undefined ? undefined : { gameState: G, greyFilter };
    // why: mirror the current-player ko-hero parker (WP-492 / D-24298): a choice exists
    // only when the player can spare some Heroes AND the options are not all identical.
    // A KO never grows the option count, so a forced start stays forced throughout.
    if (countKoableHeroes(zones, allowedZones, greyFilterRead) <= magnitude)
        return false;
    if (buildKoEligibleTargets(zones, allowedZones, greyFilterRead).length < 2)
        return false;
    if (!G.pendingKoHeroChoices)
        G.pendingKoHeroChoices = [];
    const entry = { choiceType: 'ko-hero', playerID: playerId };
    // why: D-24669 — an unqualified KO carries no `zones` field (absent ≡ "your Heroes",
    // hand + inPlay per D-24687), the same shape the WP-242 current-player parker uses.
    if (allowedZones !== undefined) {
        entry.zones = allowedZones;
    }
    // why: omit `remaining` for a single KO (absent ≡ 1), matching the WP-492 parker.
    if (magnitude >= 2) {
        entry.remaining = magnitude;
    }
    // why: D-24769 — omit-when-absent, so every unfiltered park keeps its shape.
    if (greyFilter !== undefined) {
        entry.greyFilter = greyFilter;
    }
    G.pendingKoHeroChoices.push(entry);
    return true;
}
/**
 * capture-hq-hero primitive — capture one HQ hero by the descriptor's selector.
 *
 * Verbatim from the former captureHqHeroRightmost / captureHqHeroHighestCost /
 * captureHqHeroLowestCost cases. The hyphenated descriptor selector maps to
 * captureHeroFromHq's camelCase selector union.
 */
function villainEffectCaptureHqHero(G, _currentPlayer, cardId, _timing, descriptor) {
    if (descriptor.selector === 'rightmost') {
        // why: captures the rightmost non-null hero from the HQ (index 4 → 0)
        return applicationFromCapture(captureHeroFromHq(G, cardId, 'rightmost'));
    }
    if (descriptor.selector === 'highest-cost') {
        // why: captures the highest-cost hero from the HQ; ties resolved by
        // rightmost index per selector determinism contract (WP-214)
        return applicationFromCapture(captureHeroFromHq(G, cardId, 'highestCost'));
    }
    if (descriptor.selector === 'lowest-cost') {
        // why: captures the lowest-cost hero from the HQ; ties resolved by
        // rightmost index per selector determinism contract (WP-214)
        return applicationFromCapture(captureHeroFromHq(G, cardId, 'lowestCost'));
    }
    // why: an unknown selector matched no branch — the handler ran but touched
    // nothing (no capture). Still "applied" per the post-safe-skip contract.
    return { targets: [] };
}
/**
 * Builds the effect application from a `captureHeroFromHq` result: the captured
 * hero ext_id is the log target, or `targets: []` when the HQ was empty (null).
 *
 * @param captureResult - The `captureHeroFromHq` return, or null on empty HQ.
 * @returns The effect application carrying the captured hero, if any.
 */
function applicationFromCapture(captureResult) {
    if (captureResult === null) {
        return { targets: [] };
    }
    return { targets: [captureResult.capturedHeroId] };
}
/**
 * hero-deck-top-to-escape primitive — move the top hero-deck card to escaped.
 *
 * Verbatim from the former heroDeckTopToEscape case (WP-185).
 */
function villainEffectHeroDeckTopToEscape(G, _currentPlayer, _cardId, _timing, _descriptor) {
    // why: WP-185 §Scope wrote "G.piles.heroDeck[0]" but the engine's hero
    // reservoir is the top-level G.heroDeck (GlobalPiles has no heroDeck); this
    // moves the top of that reservoir to the escaped pile. Silent no-op when the
    // reservoir is empty.
    if (G.heroDeck.length === 0)
        return { targets: [] };
    const topCard = G.heroDeck[0];
    G.heroDeck = G.heroDeck.slice(1);
    G.escapedPile = [...G.escapedPile, topCard];
    // why: WP-316 — the escaped hero-deck card is the log target.
    return { targets: [topCard] };
}
/**
 * capture-bystander primitive — attach a bystander to the triggering card,
 * awarding immediately on the Fight fire site.
 *
 * Verbatim from the former captureBystander case (WP-185 / D-18506).
 */
function villainEffectCaptureBystander(G, currentPlayer, cardId, timing, descriptor) {
    const rescueCount = descriptor.magnitude;
    if (rescueCount === undefined) {
        // why: the un-counted variant — verbatim WP-185 / D-18506 behavior. This
        // descriptor reverse-maps to the `captureBystander` legacy keyword, so it
        // narrates via the generic "<timing> effect:" line and MUST NOT self-narrate
        // (that would double-log). Attach one bystander; award it immediately on Fight.
        const attachResult = attachBystanderToVillain(G.piles.bystanders, cardId, G.attachedBystanders);
        G.piles.bystanders = attachResult.bystandersPile;
        G.attachedBystanders = attachResult.attachedBystanders;
        if (timing === 'onFight') {
            // why: the Fight fire site is post-award, so a bystander attached now
            // to a card already in the victory pile would be stranded (never
            // awarded). Award it immediately to preserve tabletop "rescue on
            // defeat" semantics (D-18506). No-op when the pile was empty (nothing
            // was attached).
            const zones = G.playerZones[currentPlayer];
            if (zones) {
                const awardResult = awardAttachedBystanders(cardId, G.attachedBystanders, zones.victory);
                G.attachedBystanders = awardResult.attachedBystanders;
                zones.victory = awardResult.playerVictory;
            }
        }
        // why: WP-316 — a captured bystander narrates via the generic keyword label,
        // not a card name; targets stays [].
        return { targets: [] };
    }
    // why: WP-489 / D-24295 — the COUNTED variant (Abomination: rescue three). Its
    // magnitude-bearing descriptor has NO legacy reverse-map entry (descriptorKey
    // includes magnitude), so it is keyword-less and self-narrates; the generic
    // "Fight effect:" line never fires, and fightVillain's `bystandersRescued` was
    // snapshotted BEFORE effects ran, so there is no double-count. Mirrors
    // rescue-bystanders-current-by-trait-count's attach+award loop; supply-bounded.
    const zones = G.playerZones[currentPlayer];
    let rescued = 0;
    for (let rescueIndex = 0; rescueIndex < rescueCount; rescueIndex++) {
        if (G.piles.bystanders.length === 0) {
            // why: the supply ran dry — stop (rescue is bounded by the Bystander supply).
            break;
        }
        const attachResult = attachBystanderToVillain(G.piles.bystanders, cardId, G.attachedBystanders);
        G.piles.bystanders = attachResult.bystandersPile;
        G.attachedBystanders = attachResult.attachedBystanders;
        if (timing === 'onFight' && zones) {
            // why: award immediately on Fight (post-award fire site) so the rescued
            // bystander is not stranded on a card already in the victory pile (D-18506).
            const awardResult = awardAttachedBystanders(cardId, G.attachedBystanders, zones.victory);
            G.attachedBystanders = awardResult.attachedBystanders;
            zones.victory = awardResult.playerVictory;
        }
        rescued += 1;
    }
    // why: self-narrate the actual count (keyword-less). `G.messages` is hash-
    // excluded (D-24081). Zero rescued (empty supply) is a reachable no-op → `blocked`.
    const label = villainEffectTimingLabel(timing);
    pushLog(G, `${label} effect: rescued ${String(rescued)} Bystander(s).`, rescued > 0 ? 'applied' : 'blocked');
    // why: WP-316 — rescued bystanders narrate by count, not by name; targets stays [].
    return { targets: [] };
}
/**
 * scry-ko-own-deck primitive — look at the top two cards of the current
 * (defeating) player's own deck, KO exactly one (the deterministically-worst
 * card), and leave the other on top in its original position (D-24267 /
 * WP-447). Doombot Legion's Fight: "Look at the top two cards of your deck. KO
 * one of them and put the other back."
 *
 * Self-narrates via `pushLog` naming the KO'd card, because `scry-ko-own-deck`
 * is not a legacy keyword — `descriptorToLegacyKeyword` returns undefined, so
 * the executor records no `VillainEffectResult` for it and the generic
 * `fightVillain.ts` "Fight effect:" line never fires. The returned `targets`
 * (the KO'd ext_id) are for parity with the other handlers; the log line is the
 * user-visible surface.
 */
function villainEffectScryKoOwnDeck(G, currentPlayer, _cardId, _timing, _descriptor, shuffleContext) {
    const zones = G.playerZones[currentPlayer];
    if (!zones)
        return { targets: [] };
    // why: WP-478 / D-24285 — "look at the top two cards" is a reveal, so a short
    // deck reshuffles the discard to top up toward two, per the standard Legendary
    // rule (superseding WP-447's "scry never reshuffles" stance FOR THIS HANDLER).
    // Runs BEFORE the 0/1/≥2 branch so the branch decides on the post-reshuffle deck.
    // No-ops on an empty discard, so a genuinely exhausted deck+discard still falls
    // through to the reachable no-op below.
    if (zones.deck.length < 2) {
        reshuffleDiscardIntoDeck(zones, shuffleContext);
    }
    // why: empty deck (after any reshuffle) is a reachable no-op, never a hollow
    // record — the handler ran, there was simply nothing to look at.
    if (zones.deck.length === 0)
        return { targets: [] };
    // why: WP-470 / D-24282 — a single-card deck still AUTO-KOs (unchanged from
    // WP-447). There is only one card to look at, so there is nothing to choose —
    // parking a choice with one option would freeze the board for a no-decision.
    // Look at 1, KO it, no "other" to return.
    if (zones.deck.length === 1) {
        const soleCard = zones.deck[0];
        const moveResult = moveCardFromZone(zones.deck, [], soleCard);
        if (!moveResult.found)
            return { targets: [] };
        zones.deck = moveResult.from;
        G.ko = koCard(G.ko, soleCard);
        recordOwnedCardKoed(G, currentPlayer, soleCard, 'deck');
        // why: scry-ko self-narrates — the keyword-less descriptor is dropped by the
        // result-recording path (descriptorToLegacyKeyword → undefined), so the
        // generic Fight-effect log line does not fire. `G.messages` is hash-excluded
        // (D-24081), so this adds no replay surface.
        const koName = resolveCardDisplayName(G, soleCard);
        pushLog(G, `Fight effect: KO'd "${koName}" from the top of your deck.`, 'neutral', soleCard);
        return { targets: [soleCard] };
    }
    // why: WP-470 / D-24282 — with ≥2 cards the interactive upgrade PARKS a choice
    // and KOs nothing yet. The player looks at the top two and picks which to KO
    // (the other stays on top); the auto-pick (selectScryKoTarget) is now only the
    // bot/sim default (ai.legalMoves) so par/replay stay byte-identical, while live
    // human play gets real agency (the Jeff-reported missing-agency bug — the
    // auto-resolve KO'd a real hero when both top cards were heroes). The pending
    // state ships WITH its block-all guard + UIState projection + client prompt
    // (project_pending_choice_no_ux_freeze). The top-2 ext_ids are snapshotted onto
    // the pending entry (D-24282): the block-all guard freezes the deck top, so the
    // snapshot cannot drift, and KO-by-ext_id is outcome-identical.
    const revealedCardIds = zones.deck.slice(0, 2);
    if (!G.pendingScryKoChoices)
        G.pendingScryKoChoices = [];
    G.pendingScryKoChoices.push({
        choiceType: 'scry-ko',
        playerID: currentPlayer,
        revealedCardIds,
    });
    // why: self-narrate the park so the log records that the choice opened; the
    // resolve move narrates the actual KO. Outcome 'neutral' — nothing has landed
    // yet. `G.messages` is hash-excluded (D-24081), so this adds no replay surface.
    pushLog(G, 'Fight effect: look at the top two cards of your deck — choose one to KO.', 'neutral');
    // why: WP-316 — pending: true marks the parked interactive KO; no card is KO'd
    // yet, so no target name is known. targets stays []. (This result is dropped by
    // the recording path anyway — scry-ko is keyword-less — but the shape is kept
    // parallel to the ko-hero parker for consistency.)
    return { targets: [], pending: true };
}
/**
 * Selects the deterministically-worst card to KO from the revealed scry cards.
 *
 * Priority (D-24267): (1) the first Wound in reveal order, else (2) the first
 * starting S.H.I.E.L.D. card (Trooper / Agent), lex-lowest, else (3) the
 * lexically-lowest ext_id among the revealed cards.
 *
 * @param revealed - The looked-at cards (the top `min(2, deck.length)` of the deck).
 * @returns The ext_id to KO, or null when `revealed` is empty.
 */
// why: Wounds are KO-PREFERRED here because this thins the player's own deck —
// removing a Wound from your deck is strictly good. This is the OPPOSITE of
// selectKoHeroTarget (the KO-a-Hero pool), which EXCLUDES Wounds because there a
// Wound is not a Hero and KOing one would invert the penalty into a benefit.
// Different pool, different rule — do NOT merge the two selectors. Membership
// uses closed enums (WOUND_EXT_ID, SHIELD_*_EXT_ID), so the pick reads no
// registry and stays deterministic (no ctx.random).
export function selectScryKoTarget(revealed) {
    for (const candidate of revealed) {
        if (candidate === WOUND_EXT_ID) {
            return candidate;
        }
    }
    let startingSelected = null;
    for (const candidate of revealed) {
        if (candidate === SHIELD_AGENT_EXT_ID || candidate === SHIELD_TROOPER_EXT_ID) {
            if (startingSelected === null || candidate < startingSelected) {
                startingSelected = candidate;
            }
        }
    }
    if (startingSelected !== null) {
        return startingSelected;
    }
    let lowestSelected = null;
    for (const candidate of revealed) {
        if (lowestSelected === null || candidate < lowestSelected) {
            lowestSelected = candidate;
        }
    }
    return lowestSelected;
}
/**
 * gain-attached-hero primitive — a deliberate NO-OP (WP-450 / D-24270).
 *
 * The captured-hero return on defeat ("Fight: Gain that Hero" — Skrull Queen
 * Veranke, Skrull Shapeshifters, Klaw) is already performed GENERICALLY at the
 * fight site by `awardAttachedHeroes` (WP-431), which runs BEFORE this executor
 * and is not gated on the card text. This handler mutates nothing and returns
 * `{ targets: [] }`.
 */
function villainEffectGainAttachedHero(_G, _currentPlayer, _cardId, _timing, _descriptor) {
    // why: D-24270 — the handler exists ONLY to make `Fight: Gain that Hero` a
    // recognized, reachable effect so the D-24266 detector classifies the line
    // applied rather than falsely recording an `unmarked-ability` breadcrumb (the
    // award itself is the generic WP-431 `awardAttachedHeroes` at the fight site).
    // It deliberately performs no mutation — driving the award from here would move
    // a hashed `G` mutation into the executor (an ordering + finalStateHash risk)
    // for no behaviour gain, so that refactor is out of scope. Reachable no-op,
    // never hollow.
    return { targets: [] };
}
/**
 * become-scheme-twist primitive — a deliberate NO-OP in the executor (WP-481 /
 * D-24287).
 *
 * The handler exists ONLY to make Mystique's `Escape: … becomes a Scheme Twist`
 * a recognized, reachable effect so the D-24266 detector classifies the line
 * applied rather than falsely recording an `unmarked-ability` breadcrumb, and the
 * WP-257 hollow detector sees a handler was reached. It deliberately performs no
 * mutation: triggering the twist needs the scheme-twist RULE pipeline
 * (executeRuleHooks('onSchemeTwistRevealed') + applyRuleEffects), which requires
 * the hookRegistry + implementationMap + RevealContext the executor does NOT
 * receive — so the actual twist is fired from the escape fire site in
 * villainDeck.reveal.ts (villainCardEscapeTriggersSchemeTwist), where all three
 * are in scope. Reachable no-op, never hollow.
 */
function villainEffectBecomeSchemeTwist(_G, _currentPlayer, _cardId, _timing, _descriptor) {
    return { targets: [] };
}
/**
 * Whether the given card carries a `become-scheme-twist` onEscape descriptor —
 * i.e. its escape should trigger the active scheme's Scheme Twist (WP-481 /
 * D-24287). Read at the escape fire site (villainDeck.reveal.ts) to decide
 * whether to run the onSchemeTwistRevealed rule pipeline after the escape's
 * villain abilities resolve. Pure lookup over G.villainAbilityHooks; no mutation.
 *
 * @param G - Game state (read-only).
 * @param cardId - The escaped card's zone-instance ext_id.
 * @returns true when an onEscape hook for the card declares `become-scheme-twist`.
 */
export function villainCardEscapeTriggersSchemeTwist(G, cardId) {
    // why: guard against G states / test mocks lacking villainAbilityHooks (mirrors
    // the executeVillainAbilities guard) — getVillainHooksForCard would throw on
    // undefined. No hooks means no become-scheme-twist escape.
    if (!G.villainAbilityHooks || G.villainAbilityHooks.length === 0) {
        return false;
    }
    const hooks = getVillainHooksForCard(G.villainAbilityHooks, cardId, 'onEscape');
    for (const hook of hooks) {
        for (const descriptor of hook.effects ?? []) {
            if (descriptor.primitive === 'become-scheme-twist') {
                return true;
            }
        }
    }
    return false;
}
/**
 * Whether the given card carries ANY onEscape ability of its own — i.e. its
 * escape is governed by card text (Ultron's `reveal-or-wound`, Mystique's
 * `become-scheme-twist`, a captured-bystander escape, …) rather than a plain
 * villain that just slides out of the City.
 *
 * Read at the escape fire site (`villainDeck.reveal.ts`) to gate the WP-015
 * generic per-escape wound (D-24439): a villain whose card text defines its
 * escape resolves THAT ability instead, so the generic baseline wound must not
 * stack on top — that stacking double-wounded the active player whenever an
 * ability-bearing villain (e.g. Ultron) escaped. A plain villain (no onEscape
 * hook) still takes the generic wound as its baseline escape penalty.
 *
 * Pure lookup over `G.villainAbilityHooks`; no mutation. Guards against a G /
 * test mock without hooks (mirrors `villainCardEscapeTriggersSchemeTwist`).
 *
 * @param G - Game state (read-only).
 * @param cardId - The escaped card's zone-instance ext_id.
 * @returns true when the card has at least one onEscape hook carrying an effect.
 */
export function villainCardHasEscapeAbility(G, cardId) {
    if (!G.villainAbilityHooks || G.villainAbilityHooks.length === 0) {
        return false;
    }
    const hooks = getVillainHooksForCard(G.villainAbilityHooks, cardId, 'onEscape');
    for (const hook of hooks) {
        if ((hook.effects ?? []).length > 0) {
            return true;
        }
    }
    return false;
}
/**
 * play-villain-deck-cards primitive — a deliberate NO-OP in the executor (WP-542 /
 * D-24351).
 *
 * Mirrors `villainEffectBecomeSchemeTwist` (WP-481): the handler exists ONLY so the
 * printed "Fight/Ambush: Play the top N cards of the Villain Deck" line is a
 * recognized, reachable effect (the D-24266 detector classifies it applied, the
 * WP-257 hollow detector sees a handler was reached), and it deliberately performs
 * no mutation. Playing villain-deck cards needs `performVillainReveal` +
 * `implementationMap` + a `RevealContext`, which the executor does NOT receive — so
 * the actual reveal fires from the two sites that own the reveal pipeline:
 * `villainDeck.reveal.ts` (The Leader's onAmbush) via `villainCardPlaysVillainDeckCards`
 * + `playTopVillainDeckCards`, and `fightVillain.ts` (Endless Armies' onFight) the
 * same way. Reachable no-op, never hollow.
 *
 * WP-837 / D-24770 — a gated descriptor (The Leader: "Reveal a Strength Hero or play a
 * card") logs the current player's automatic reveal; it still mutates only the
 * hash-excluded log, so the primitive stays a deliberate no-op.
 */
function villainEffectPlayVillainDeckCards(G, currentPlayer, _cardId, timing, descriptor) {
    // why: D-24281 — the reveal costs nothing and the play only hurts, so the current player
    // reveals automatically when able; the same predicate decides the fire site's play count.
    if (currentPlayerRevealsForVillainDeckPlay(G, descriptor, currentPlayer)) {
        const requirement = describeRevealRequirement(descriptor.requireKind, descriptor.requireValue);
        pushLog(G, `${villainEffectTimingLabel(timing)} effect: Player ${currentPlayer} revealed ${articleFor(requirement)} ${requirement} Hero, so no card is played from the Villain Deck.`, 'blocked');
    }
    return { targets: [] };
}
/**
 * Whether the current player reveals past a gated `play-villain-deck-cards` (WP-837 /
 * D-24770): the descriptor carries a trait gate and the player has a matching Hero in
 * hand or still in play.
 *
 * @param G - Game state (read-only).
 * @param descriptor - The `play-villain-deck-cards` descriptor.
 * @param currentPlayer - The player whose turn it is, or undefined.
 * @returns True when the gate is present and the player meets it.
 */
function currentPlayerRevealsForVillainDeckPlay(G, descriptor, currentPlayer) {
    if (descriptor.requireKind === undefined || descriptor.requireValue === undefined) {
        return false;
    }
    if (currentPlayer === undefined) {
        return false;
    }
    const zones = G.playerZones[currentPlayer];
    if (zones === undefined) {
        return false;
    }
    return playerHasHeroMatchingTrait(G, zones, descriptor.requireKind, descriptor.requireValue);
}
/**
 * Picks the indefinite article for a trait label (`an X-Men`, `a Ranged`).
 *
 * @param label - The display label from `describeRevealRequirement`.
 * @returns `an` when the label starts with A, E, I, O, U or X; otherwise `a`.
 */
function articleFor(label) {
    // why: "X-Men" is read "ex-men", so X takes `an` like a vowel.
    if (/^[AEIOUX]/.test(label)) {
        return 'an';
    }
    return 'a';
}
/**
 * Returns the count of top villain-deck cards a card plays at the given timing, or
 * 0 when it carries no `play-villain-deck-cards` descriptor there (WP-542 /
 * D-24351).
 *
 * Read at the two fire sites that own the reveal pipeline — the onAmbush site in
 * villainDeck.reveal.ts (The Leader) and the onFight site in fightVillain.ts
 * (Endless Armies of HYDRA) — to decide whether, and how many times, to call
 * `playTopVillainDeckCards` after the card's abilities resolve. Mirrors
 * `villainCardEscapeTriggersSchemeTwist` (the become-scheme-twist detector), but
 * returns the marked count (from the descriptor's `magnitude`) instead of a
 * boolean. Pure lookup over G.villainAbilityHooks; no mutation.
 *
 * WP-837 / D-24770 — a gated descriptor (`requireKind` + `requireValue`, The Leader)
 * plays nothing when the current player reveals a matching Hero, and nothing when no
 * current player is given (fail closed). An ungated descriptor ignores `currentPlayer`.
 *
 * @param G - Game state (read-only).
 * @param cardId - The triggering card's zone-instance ext_id.
 * @param timing - The timing to inspect ('onAmbush' | 'onFight' | 'onEscape').
 * @param currentPlayer - The player whose turn it is (needed only for a gated play).
 * @returns The play count N (default 1 when a descriptor omits `magnitude`), or 0.
 */
export function villainCardPlaysVillainDeckCards(G, cardId, timing, currentPlayer) {
    // why: guard against G states / test mocks lacking villainAbilityHooks (mirrors
    // the executeVillainAbilities + villainCardEscapeTriggersSchemeTwist guard) —
    // getVillainHooksForCard would throw on undefined. No hooks means no play.
    if (!G.villainAbilityHooks || G.villainAbilityHooks.length === 0) {
        return 0;
    }
    const hooks = getVillainHooksForCard(G.villainAbilityHooks, cardId, timing);
    for (const hook of hooks) {
        for (const descriptor of hook.effects ?? []) {
            if (descriptor.primitive === 'play-villain-deck-cards') {
                // why: WP-837 / D-24770 — one predicate with the handler's log line (D-24281
                // auto-reveal); a gated read with no current player fails closed.
                const isGated = descriptor.requireKind !== undefined && descriptor.requireValue !== undefined;
                if (isGated && currentPlayer === undefined) {
                    return 0;
                }
                if (isGated && currentPlayerRevealsForVillainDeckPlay(G, descriptor, currentPlayer)) {
                    return 0;
                }
                // why: the parser always sets `magnitude` (the `:N` count); a malformed
                // hand-built test hook lacking it falls back to 1 rather than returning 0
                // (a marked line always plays at least one card).
                return descriptor.magnitude ?? 1;
            }
        }
    }
    return 0;
}
/**
 * Maps a villain ability timing to its human log-label ('Fight' / 'Ambush' /
 * 'Escape') for a self-narrated effect line.
 *
 * The three fire sites hardcode "Fight effect:" / "Ambush effect:" / "Escape
 * effect:" per site; several keyword-less handlers self-narrate from one handler
 * that fires at more than one timing (reveal-or-wound at all three; the WP-489
 * gain-wound:each-other / counted capture-bystander / city-gate lines), so they
 * need the label from the timing they received.
 *
 * @param timing - The timing that fired ('onFight' | 'onAmbush' | 'onEscape').
 * @returns The capitalized label matching the per-site convention.
 */
function villainEffectTimingLabel(timing) {
    if (timing === 'onAmbush') {
        return 'Ambush';
    }
    if (timing === 'onFight') {
        return 'Fight';
    }
    // why: only 'onEscape' remains — the VillainAbilityTiming union is closed to
    // the three values, so this is total without a default-throw.
    return 'Escape';
}
/**
 * Returns whether a player HAS a Hero — in hand OR in play — whose trait matches
 * the predicate.
 *
 * Scans the passed card ext_ids (the player's hand + in-play zones) via the
 * setup-time `G.cardTraits` snapshot: a `team` predicate needs a card whose
 * `trait.team` equals the (normalized) value, a `hero-class` predicate needs
 * `trait.heroClass`.
 *
 * @param trait - The card's trait entry (the off-play view for a hand / HQ card), or undefined.
 * @param kind - The predicate kind ('team' | 'hero-class').
 * @param value - The normalized trait slug to match.
 * @returns True when the trait matches the predicate.
 */
// why: D-24290 — the per-card trait match, extracted at the third caller
// (playerHasHeroMatchingTrait, listPlayerHeroesMatchingTrait, and the
// ko-heroes-current-by-trait handler) per the duplicate-first / abstract-on-third
// rule (§Abstraction). A `team` predicate matches `trait.team`; a `hero-class`
// predicate matches `trait.heroClass`. `value` is normalized at parse time, so
// `===` is casing/whitespace-safe.
function cardTraitMatches(trait, kind, value) {
    if (trait === undefined) {
        return false;
    }
    if (kind === 'team') {
        return trait.team === value;
    }
    // why: the kind union is closed to 'team' | 'hero-class', so the remaining case
    // is 'hero-class' — total without a default branch.
    // why: WP-703 / D-24523 — a dual-class card matches on either printed class.
    return trait.heroClass === value || trait.heroClass2 === value;
}
/**
 * Returns whether a card IN PLAY matches the trait predicate, counting its effective
 * Hero Class (printed hc / hc2 OR a class granted when played) and its effective team
 * (printed team OR a team granted when played).
 *
 * @param G - Game state (reads G.cardTraits, G.cardSizeChangingClasses, G.cardCopiedClasses and G.cardCopiedTeams).
 * @param playedCardId - The in-play card being tested.
 * @param kind - The predicate kind ('team' | 'hero-class').
 * @param value - The normalized trait slug to match.
 * @returns True when the played card matches the predicate.
 */
// why: D-24724 — a played card counts as each class it was granted on play: Rogue's Copy
// Powers ("This card is both Covert and the color you copy", G.cardCopiedClasses since
// D-24730) and Size-Changing (G.cardSizeChangingClasses) both grant one, and
// cardHasClassWhenPlayed is the single effective-class source
// (D-24074). Reading raw G.cardTraits here undercounted Maestro (live match kIZGg6MXhua).
// The hand stays on offPlayCardTraits (a grant applies only in play).
// why: D-24726 — the team half of the same fix: Copy Powers is a full duplicate and also
// counts as the copied Hero's team (D-24391, G.cardCopiedTeams), read through
// cardHasTeamWhenPlayed. Baron Zemo rescued 3, not 4, with Copy Powers copying an Avenger
// (live match xW_Kmg0UF8s, 27.2.16). Deliberately NOT cardCountsAsTeamMember: the basic
// S.H.I.E.L.D.-token widening stays local to the Destroyer KO (D-24296).
function playedCardTraitMatches(G, playedCardId, kind, value) {
    if (kind === 'hero-class') {
        return cardHasClassWhenPlayed(G, playedCardId, value);
    }
    // why: the kind union is closed to 'team' | 'hero-class', so the remaining case is 'team'.
    return cardHasTeamWhenPlayed(G, playedCardId, value);
}
// why: D-24281 (AMENDED 2026-07-31) — scans HAND + IN-PLAY, matching the D-24076
// defeat-requirement's `playerMeetsDefeatRequirement` scope. WP-469 originally
// shipped this HAND-ONLY ("the printed reveal is a hand action"), but the
// Fight/Ambush/Escape effect resolves AFTER the play phase, so a Hero the player
// already played this turn sits in `inPlay`, not `hand` — hand-only then wounded a
// player who plainly HAS a qualifying Hero (live Magneto match, 2026-07-31: fought
// Sabretooth having already played Wolverine → wrongly wounded). Counting in-play
// too makes "you have an X-Men Hero" satisfy the reveal, as the operator ruled.
// Discard + deck stay excluded (only hand + play are "revealable"). No `.reduce()`.
// why: WP-772 / D-24604 — the two zones are scanned separately because only the HAND is off
// play: a split card in hand counts as both halves' classes (rules v23 p.49, via the split-face
// map), while a played split card counts only as its chosen face.
/**
 * Returns whether a player has a Hero — in hand OR in play — matching the trait predicate.
 *
 * @param G - Game state (reads G.cardTraits and the split-face map).
 * @param zones - The player's zones (hand + inPlay are scanned).
 * @param kind - The predicate kind ('team' | 'hero-class').
 * @param value - The normalized trait slug to match.
 * @returns True when the player has at least one matching Hero in hand or in play.
 */
function playerHasHeroMatchingTrait(G, zones, kind, value) {
    for (const handCardId of zones.hand) {
        if (cardTraitMatches(offPlayCardTraits(G, handCardId), kind, value)) {
            return true;
        }
    }
    // why: WP-780 / D-24619 — a rules-facing Hero trait / count read, so a card Penumbra played both-sides counts as both faces.
    // why: WP-803 / D-24692 — a "Heroes you have" read (hand + still in play), so a card that went Out of Time is excluded: rules v23 L3443-3447 (a played card that moved elsewhere doesn't count as "Your Heroes" or "Heroes you Have") + L3457-3470 ("Reveal a Hero" / "KO one of your Heroes" can't pick it).
    for (const playedCardId of playedCardIdsStillInPlay(G, zones.inPlay)) {
        if (playedCardTraitMatches(G, playedCardId, kind, value)) {
            return true;
        }
    }
    return false;
}
/**
 * Returns WHICH of a player's Heroes — in hand OR in play — match the trait predicate
 * (the count sibling of `playerHasHeroMatchingTrait`, D-24290). The count is the
 * list's length.
 *
 * Same hand+in-play scope and same `G.cardTraits` snapshot as its boolean sibling;
 * used by `rescue-bystanders-current-by-trait-count` (Baron Zemo) and
 * `ko-heroes-current-count-by-trait` (Maestro) to size the effect by the number of
 * matching Heroes rather than a mere has/has-not.
 *
 * @param G - Game state (reads G.cardTraits and the split-face map).
 * @param zones - The player's zones (hand + inPlay are scanned).
 * @param kind - The predicate kind ('team' | 'hero-class').
 * @param value - The normalized trait slug to match.
 * @returns The matching Heroes across hand + in-play, hand first, in zone order.
 */
// why: D-24729 — returns the counted cards, not just the number, so the Zemo / Maestro
// log lines can name what they counted. The game log never lists the cards a player left
// in hand, so without the names a live-verify cannot tell a Copy Powers copy from a
// matching Hero left unplayed. The scan itself is unchanged.
function listPlayerHeroesMatchingTrait(G, zones, kind, value) {
    const counted = [];
    // why: WP-772 / D-24604 — hand (off play) reads the split-face map's both-halves view; inPlay
    // reads the chosen face only (same zone split as playerHasHeroMatchingTrait).
    for (const handCardId of zones.hand) {
        if (cardTraitMatches(offPlayCardTraits(G, handCardId), kind, value)) {
            counted.push({ cardId: handCardId, zone: 'hand' });
        }
    }
    // why: WP-780 / D-24619 — a rules-facing Hero trait / count read, so a card Penumbra played both-sides counts as both faces.
    // why: WP-803 / D-24692 — a "Heroes you have" read (hand + still in play), so a card that went Out of Time is excluded: rules v23 L3443-3447 (a played card that moved elsewhere doesn't count as "Your Heroes" or "Heroes you Have") + L3457-3470 ("Reveal a Hero" / "KO one of your Heroes" can't pick it).
    for (const playedCardId of playedCardIdsStillInPlay(G, zones.inPlay)) {
        if (playedCardTraitMatches(G, playedCardId, kind, value)) {
            counted.push({ cardId: playedCardId, zone: 'played' });
        }
    }
    return counted;
}
/**
 * Names one counted Hero with where it was counted from, for a trait-count log line.
 *
 * A hand card reads `Name (hand)`; a played card whose printed trait matches reads
 * `Name (played)`. A played card that matches only through a trait granted on play reads
 * `Name (played, copied avengers)` for a team (Copy Powers, the only team grant) or
 * `Name (played, granted strength)` for a class (Copy Powers or Size-Changing).
 *
 * @param G - Game state (reads G.cardTraits and G.cardDisplayData).
 * @param counted - The counted Hero and its zone.
 * @param kind - The predicate kind ('team' | 'hero-class').
 * @param value - The normalized trait slug that was matched.
 * @returns The labeled card name.
 */
// why: D-24729 — a printed match and a granted match are told apart by re-testing the
// printed G.cardTraits row: cardHasTeamWhenPlayed / cardHasClassWhenPlayed check that row
// first and fall back to the grant, so "matched but not by print" means "by grant". The
// copied card itself is not in G; the Copy Powers play line ("… copied X.") names it.
function describeTraitCountedHero(G, counted, kind, value) {
    const name = resolveCardDisplayName(G, counted.cardId);
    if (counted.zone === 'hand') {
        return `${name} (hand)`;
    }
    if (cardTraitMatches(G.cardTraits[counted.cardId], kind, value)) {
        return `${name} (played)`;
    }
    if (kind === 'team') {
        return `${name} (played, copied ${value})`;
    }
    return `${name} (played, granted ${value})`;
}
/**
 * Joins the counted Heroes into the `: A (hand), B (played)` tail of a trait-count line.
 *
 * @param G - Game state.
 * @param countedHeroes - The counted Heroes, in scan order.
 * @param kind - The predicate kind ('team' | 'hero-class').
 * @param value - The normalized trait slug that was matched.
 * @returns The `: names` tail, or an empty string when nothing was counted.
 */
function describeTraitCountedHeroes(G, countedHeroes, kind, value) {
    if (countedHeroes.length === 0) {
        return '';
    }
    const names = countedHeroes.map((counted) => describeTraitCountedHero(G, counted, kind, value));
    return `: ${names.join(', ')}`;
}
/**
 * reveal-or-wound primitive — each player either reveals (from hand OR play) a Hero
 * whose trait matches the descriptor's predicate, or — only when they have no match —
 * gains a Wound (D-24281 / WP-469; amended 2026-07-31 to count in-play Heroes).
 * Sabretooth's "Fight: Each player reveals an X-Men Hero or gains a Wound." and the
 * core siblings (Frost Giant, Ymir, Ultron, Zzzax) across Fight / Ambush / Escape timings.
 *
 * Each player, starting with the current player and going clockwise (D-24780), is tested with the hand+in-play
 * trait predicate: a player with no match gains one Wound at once; the players with a
 * match are queued on `G.pendingRevealOrWoundChoices` and each chooses after the move
 * whether to reveal or gain the Wound (WP-845 / D-24779, rules v23 L3196-3200).
 * Deterministic. Self-narrates via `pushLog` (like scry-ko, reveal-or-wound is not a
 * legacy keyword — `descriptorToLegacyKeyword` returns undefined, so no
 * `VillainEffectResult` is recorded and the generic `<timing> effect:` line never
 * fires). Returns `{ targets: [] }`; the log line is the user-visible surface.
 */
// why: WP-651 / D-24463 — the shield-block `threatKind` for a reveal-or-wound
// AVOIDED, keyed by the timing that fired. Total over the closed
// `VillainAbilityTiming` union (a new timing would fail this `Record` at compile
// time). onAmbush → 'ambush' (WP-646); onFight → 'fight'; onEscape → 'escape'
// (the villain still escapes — only the Wound is dodged, hence the composer's
// "…penalty was blocked" narrative for 'escape').
const STRIKE_BLOCK_THREAT_KIND_BY_TIMING = {
    onAmbush: 'ambush',
    onFight: 'fight',
    onEscape: 'escape',
};
/**
 * Composes the tail clause naming players who would have gained a Wound but the
 * supply was empty (D-24686). Empty string when there are none, so a normal
 * "gained a Wound" line reads exactly as before.
 */
function composeEmptyWoundSupplyClause(emptySupplyPlayerIds) {
    if (emptySupplyPlayerIds.length === 0) {
        return '';
    }
    const names = emptySupplyPlayerIds.map((playerId) => `Player ${playerId}`).join(', ');
    return `; the Wound supply ran out before ${names}`;
}
function villainEffectRevealOrWound(G, currentPlayer, cardId, timing, descriptor) {
    const requireKind = descriptor.requireKind;
    const requireValue = descriptor.requireValue;
    // why: defensive — a well-formed reveal-or-wound descriptor always carries both
    // predicate fields (parseParameterizedEffect sets them together, D-24281).
    // A malformed hook lacking them cannot evaluate the predicate, so no player is
    // wounded and the handler no-ops (never wounds everyone on an empty predicate).
    // Reachable only via a hand-built test hook, never from the parser.
    if (requireKind === undefined || requireValue === undefined) {
        return { targets: [] };
    }
    // why: rules v23 L2247 — start with the current player, then clockwise (D-24780, amends
    // D-24281's sorted order). The queued revealers are re-sorted ascending by
    // enqueueRevealOrWoundChoice, so WP-845's apply stays ascending (D-24501).
    const woundedPlayerIds = [];
    const gainedPlayerIds = [];
    const divingBlockPendingPlayerIds = [];
    const emptySupplyPlayerIds = [];
    const revealingPlayerIds = [];
    for (const playerId of seatsInTurnOrderFrom(Object.keys(G.playerZones), currentPlayer)) {
        const zones = G.playerZones[playerId];
        if (!zones) {
            continue;
        }
        if (playerHasHeroMatchingTrait(G, zones, requireKind, requireValue)) {
            // why: WP-845 / D-24779 (amends D-24281 Semantics) — rules v23 "Revealing a Card"
            // (L3196-3200) lets a player who HAS a matching Hero (hand or play) decline and take the
            // Wound instead, so the player is queued for a choice after the move. The predicate is
            // fixed now, at effect time. Their WP-646 / WP-651 strikeBlocked moves to the apply,
            // pushed only when they Reveal.
            revealingPlayerIds.push(playerId);
            continue;
        }
        // why: no matching Hero in hand or in play — gain one Wound. Empty pile is a reachable
        // no-op (mirrors gain-wound:each): the player is NOT counted as wounded, and is
        // tracked so the narration says the supply ran out rather than "revealed"
        // (D-24686).
        if (G.piles.wounds.length === 0) {
            emptySupplyPlayerIds.push(playerId);
            continue;
        }
        // why: WP-682 / D-24499 — gainWoundForPlayer chokepoint so a Master-Strike Wound
        // to a Diving-Block holder (often a NON-active seat) gets the reveal window (WP-684).
        if (gainWoundWithOutcome(G, playerId) === 'diving-block-pending') {
            divingBlockPendingPlayerIds.push(playerId);
        }
        else {
            gainedPlayerIds.push(playerId);
        }
        if (playerId === currentPlayer) {
            // why: woundsDrawn projects the CURRENT player's wounds only (UI economy) —
            // bump it only for the current player, parity with gain-wound:each and the
            // escape-wound path; a non-current wounded player must not move it.
            G.turnEconomy.woundsDrawn += 1;
        }
        woundedPlayerIds.push(playerId);
    }
    // why: self-narrate ONE line with the pinned templates (D-24281). reveal-or-wound
    // is keyword-less so the result-recording path drops it; without this push the
    // effect would land silently. `G.messages` is hash-excluded (D-24081), so this
    // adds no replay surface. Outcome colour is honest per the WP-434 contract: the
    // wound landed on ≥1 player → `applied`; every player revealed (the villain
    // effect touched no one) → `blocked`.
    const label = villainEffectTimingLabel(timing);
    // why: D-24679 — name the trait checked ("had no Ranged Hero"), so a reader can tell
    // from the log alone what the player needed (match BvcATHgYM5k, Ymir's Ambush).
    const requirement = describeRevealRequirement(requireKind, requireValue);
    const emptySupplyClause = composeEmptyWoundSupplyClause(emptySupplyPlayerIds);
    if (divingBlockPendingPlayerIds.length > 0) {
        // why: D-24673 — a Diving Block reveal is pending, so that Wound is not gained yet;
        // the reveal / decline line that follows gives the outcome.
        pushLog(G, `${label} effect: ${String(woundedPlayerIds.length)} player(s) had no ${requirement} Hero: ${composeWoundOutcomeClause(gainedPlayerIds, divingBlockPendingPlayerIds)}${emptySupplyClause}.`, 'applied');
    }
    else if (woundedPlayerIds.length > 0) {
        const names = woundedPlayerIds.map((playerId) => `Player ${playerId}`).join(', ');
        pushLog(G, `${label} effect: ${String(woundedPlayerIds.length)} player(s) had no ${requirement} Hero and gained a Wound (${names})${emptySupplyClause}.`, 'applied');
    }
    else if (emptySupplyPlayerIds.length > 0) {
        // why: D-24686 — nobody gained a Wound because the supply is empty, not because
        // every player revealed (live match sDBnFxiigDL, Ymir's Ambush at 37.1.2, where no
        // Hero in the pool was Ranged).
        const names = emptySupplyPlayerIds.map((playerId) => `Player ${playerId}`).join(', ');
        pushLog(G, `${label} effect: the Wound supply is empty, so no Wound was gained (${names} had no ${requirement} Hero).`, 'blocked');
    }
    else if (revealingPlayerIds.length === 0) {
        // why: WP-845 / D-24779 — reachable only with no seats at all now: a player who could
        // reveal is queued below and has not chosen yet, so "every player revealed" would be false.
        // why: "an" before a vowel sound — the vowels plus "X" (read "ex"), as in the printed
        // "reveals an X-Men Hero".
        const article = /^[AEIOUX]/.test(requirement) ? 'an' : 'a';
        pushLog(G, `${label} effect: every player revealed ${article} ${requirement} Hero.`, 'blocked');
    }
    if (revealingPlayerIds.length > 0) {
        queueVillainRevealOrWoundChoice(G, currentPlayer, cardId, timing, requirement, revealingPlayerIds);
    }
    return { targets: [] };
}
/**
 * Queues the players who can reveal for a reveal-or-gain-a-Wound choice after the move, and
 * logs who will choose (WP-845 / D-24779). The entry carries the timing's shield-block class
 * so the apply can push each revealer's `strikeBlocked` (WP-646 / WP-651).
 *
 * @param G - Game state (queue appended, log appended).
 * @param currentPlayer - The turn's current player.
 * @param cardId - The Villain whose effect fired.
 * @param timing - The timing that fired (selects the shield-block class).
 * @param requirement - The trait label (e.g. `X-Men`).
 * @param revealingPlayerIds - The players who can reveal, in seat order.
 */
function queueVillainRevealOrWoundChoice(G, currentPlayer, cardId, timing, requirement, revealingPlayerIds) {
    enqueueRevealOrWoundChoice(G, {
        sourceCardId: cardId,
        currentPlayerID: currentPlayer,
        revealOptionLabel: `Reveal a matching ${requirement} Hero`,
        revealingSeats: [...revealingPlayerIds],
        strikeBlockThreatKind: STRIKE_BLOCK_THREAT_KIND_BY_TIMING[timing],
    });
    pushLog(G, `${villainEffectTimingLabel(timing)} effect: ${formatRevealOrWoundPlayerNames(revealingPlayerIds)} can reveal a matching ${requirement} Hero and will choose whether to reveal it or gain a Wound.`, 'neutral');
}
// why: D-24679 — display labels for the team slugs whose printed name is not a simple
// title-casing of the slug. Hero classes and other teams title-case cleanly.
const REVEAL_TEAM_LABELS = {
    'x-men': 'X-Men',
    shield: 'S.H.I.E.L.D.',
    hydra: 'HYDRA',
    'x-force': 'X-Force',
    'x-factor': 'X-Factor',
};
/**
 * Names the trait a reveal-or-wound checks, for its log line.
 *
 * @param kind - The predicate kind ('team' | 'hero-class').
 * @param value - The normalized trait slug, e.g. `ranged` or `x-men`.
 * @returns A display label, e.g. `Ranged`, `X-Men`, `S.H.I.E.L.D.`.
 */
function describeRevealRequirement(kind, value) {
    if (kind === 'team') {
        const teamLabel = REVEAL_TEAM_LABELS[value];
        if (teamLabel !== undefined) {
            return teamLabel;
        }
    }
    const words = [];
    for (const word of value.split('-')) {
        words.push(word.length > 0 ? word[0].toUpperCase() + word.slice(1) : word);
    }
    return words.join(' ');
}
/**
 * draw-cards-current primitive — the current (defeating) player draws `drawCount`
 * cards via the shared `drawCardsIntoHand` path (Enchantress "Fight: Draw three
 * cards.", D-24290 / WP-485).
 *
 * Self-narrates via `pushLog`: like `scry-ko` / `reveal-or-wound`,
 * draw-cards-current is keyword-less (`descriptorToLegacyKeyword` returns
 * undefined), so the executor records no `VillainEffectResult` and the generic
 * `<timing> effect:` line never fires — this push is the user-visible surface.
 * Returns `{ targets: [] }` (drawn cards narrate by count, not by name).
 */
function villainEffectDrawCardsCurrent(G, currentPlayer, _cardId, _timing, descriptor, shuffleContext) {
    const drawCount = descriptor.drawCount;
    // why: EC-520 — guard BOTH optional inputs defensively rather than loosen
    // `drawCardsIntoHand`'s required `count` / `ShuffleProvider` params (that helper
    // is out of the allowlist). A malformed hook lacking `drawCount` (reachable only
    // via a hand-built test hook — the parser always sets it) no-ops; an absent
    // `shuffleContext` no-ops because a draw can reshuffle the discard and we never
    // reach for a non-deterministic fallback (determinism invariant). The Fight fire
    // site always threads `{ random }`, so live play always has one.
    if (drawCount === undefined) {
        return { targets: [] };
    }
    if (shuffleContext === undefined) {
        return { targets: [] };
    }
    const zones = G.playerZones[currentPlayer];
    if (!zones) {
        return { targets: [] };
    }
    const handSizeBefore = zones.hand.length;
    drawCardsIntoHand(zones, drawCount, shuffleContext);
    const drawn = zones.hand.length - handSizeBefore;
    // why: D-24732 — the defeating player's Fight draw is an extra card drawn this turn.
    G.turnEconomy.cardsDrawn += drawn;
    // why: D-24290 — all three Tier-A primitives are Fight-timed (the markers are
    // authored under `.fight`), so the label is hardcoded "Fight effect:" as the
    // scry-ko handler does for the Fight-only Doombot; a draw that moved 0 cards
    // (empty deck + discard) is honestly `blocked`, else `applied`.
    pushLog(G, `Fight effect: drew ${String(drawn)} card(s).`, drawn > 0 ? 'applied' : 'blocked');
    return { targets: [] };
}
/**
 * Reads the cost of a card revealed from the top of a deck (WP-760 / D-24589).
 *
 * @param G - Current game state (read-only).
 * @param cardId - The revealed card.
 * @returns The printed cost; 0 for a Wound; undefined for an uncosted non-Wound card.
 */
function resolveRevealedDeckTopCost(G, cardId) {
    const cardStats = G.cardStats[cardId];
    if (cardStats !== undefined) {
        return cardStats.cost;
    }
    // why: D-24583 — a Wound has no printed cost and no G.cardStats entry; an uncosted
    // Wound costs 0 (rules v23 L2861-2862), the same rule hero reveals use. A local copy
    // of the hero module's private resolver (duplicate first; the villain module does not
    // import hero internals).
    if (cardId === WOUND_EXT_ID) {
        return 0;
    }
    return undefined;
}
/**
 * reveal-top-draw-if-cost-lte primitive — the current (defeating) player reveals the
 * top card of their deck and draws it if it costs `magnitude` or less; otherwise it
 * stays on top (The Fallen's Patriarch: "Fight: Reveal the top card of your deck. If
 * it costs 3 or less, draw it.", WP-760 / D-24589).
 *
 * Self-narrates via `pushLog`: the primitive is keyword-less (no legacy reverse-map
 * entry), so the generic `<timing> effect:` line never fires.
 */
function villainEffectRevealTopDrawIfCostLte(G, currentPlayer, _cardId, _timing, descriptor, shuffleContext) {
    const costCeiling = descriptor.magnitude;
    if (costCeiling === undefined) {
        return { targets: [] };
    }
    const zones = G.playerZones[currentPlayer];
    if (!zones) {
        return { targets: [] };
    }
    // why: D-24285 — a reveal on an empty deck reshuffles the discard first
    // (reshuffleDiscardIntoDeck no-ops on an empty discard or a missing shuffle source,
    // so an exhausted deck + discard falls through to the logged no-op below).
    if (zones.deck.length === 0) {
        reshuffleDiscardIntoDeck(zones, shuffleContext);
    }
    const topCardId = zones.deck[0];
    if (topCardId === undefined) {
        pushLog(G, 'Fight effect: no card to reveal — your deck and discard pile are empty.', 'blocked');
        return { targets: [] };
    }
    const topName = resolveCardDisplayName(G, topCardId);
    const cost = resolveRevealedDeckTopCost(G, topCardId);
    if (cost === undefined || cost > costCeiling) {
        pushLog(G, `Fight effect: revealed "${topName}" (costs more than ${String(costCeiling)}) — left on top of your deck.`, 'blocked', topCardId);
        return { targets: [] };
    }
    // why: the card moves deck → hand directly, NOT through the hero-effect draw path, so
    // a turn's draw lock does not apply — the WP-731 precedent for villain-driven draws
    // for the active player. Deck top only; no reshuffle can occur past the reveal above.
    const moveResult = moveCardFromZone(zones.deck, zones.hand, topCardId);
    if (!moveResult.found) {
        return { targets: [] };
    }
    zones.deck = moveResult.from;
    zones.hand = moveResult.to;
    // why: D-24732 — "draw it" is a real draw by the current player (only the draw lock is
    // waived above), so it counts as an extra card drawn this turn.
    G.turnEconomy.cardsDrawn += 1;
    pushLog(G, `Fight effect: revealed "${topName}" (cost ${String(cost)}) — drew it.`, 'applied', topCardId);
    return { targets: [topCardId] };
}
/**
 * ko-up-to-from-discard-current primitive — the current (defeating) player may KO up
 * to `magnitude` cards from their own discard pile (The Fallen's Salomé: "Fight: KO up
 * to two cards from your discard pile.", WP-760 / D-24589).
 *
 * Parks the existing PendingKoDiscardChoice (WP-693 / D-24510: block-all guard, UIState
 * projection, client prompt, bot default `{ cardIds: [] }`) with `sourceCardId`, and
 * returns `{ pending: true }`. An empty discard is a logged no-op (nothing parks).
 */
function villainEffectKoUpToFromDiscardCurrent(G, currentPlayer, cardId, timing, descriptor) {
    const maxCount = descriptor.magnitude;
    if (maxCount === undefined) {
        return { targets: [] };
    }
    const zones = G.playerZones[currentPlayer];
    if (!zones) {
        return { targets: [] };
    }
    const sourceName = resolveCardDisplayName(G, cardId);
    if (descriptor.greyFilter !== undefined) {
        return parkGreyKoUpToFromDiscard(G, currentPlayer, cardId, timing, maxCount, descriptor.greyFilter);
    }
    if (zones.discard.length === 0) {
        pushLog(G, `Fight effect: your discard pile is empty (${sourceName}); nothing to KO.`, 'blocked');
        return { targets: [] };
    }
    // why: `sourceCardId` names the parking card for the resolve log (display text is never
    // stored in G). The queue is created lazily at the park site, so an untriggered match
    // leaves the field undefined and the hash oracles stay byte-stable.
    if (!G.pendingKoDiscardChoices) {
        G.pendingKoDiscardChoices = [];
    }
    const entry = {
        choiceType: 'ko-from-discard',
        playerID: currentPlayer,
        maxCount,
        sourceCardId: cardId,
    };
    G.pendingKoDiscardChoices.push(entry);
    pushLog(G, `Fight effect: KO up to ${String(maxCount)} cards from your discard pile (${sourceName}).`, 'neutral');
    return { targets: [], pending: true };
}
/**
 * The filtered branch of ko-up-to-from-discard-current (WP-836 / D-24769): the current
 * player may KO up to `maxCount` grey Heroes from their discard pile (co2e Destroyer
 * "KO up to two grey Heroes", Frost Giant Warrior / Ymir "You may KO a grey Hero").
 *
 * Parks the PendingKoDiscardChoice with `greyFilter` when at least one discard card
 * passes the filter; otherwise logs a `blocked` no-op and parks nothing.
 *
 * @param G - Game state (the pending queue is lazily created on a park).
 * @param currentPlayer - The defeating player.
 * @param cardId - The villain whose Fight parked the choice.
 * @param timing - The timing that fired (names the log line).
 * @param maxCount - The most cards the player may KO.
 * @param greyFilter - The grey-Hero filter the KO is limited to.
 * @returns `{ pending: true }` on a park, else `{ targets: [] }`.
 */
function parkGreyKoUpToFromDiscard(G, currentPlayer, cardId, timing, maxCount, greyFilter) {
    const zones = G.playerZones[currentPlayer];
    if (!zones) {
        return { targets: [] };
    }
    const label = villainEffectTimingLabel(timing);
    const sourceName = resolveCardDisplayName(G, cardId);
    // why: "up to" / "may" — the choice parks whenever an eligible card exists (the Salomé
    // shape); with none, the prompt would offer nothing, so it is a logged no-op instead.
    const hasEligibleCard = zones.discard.some((discardCardId) => matchesGreyHeroFilter(G, discardCardId, greyFilter));
    if (!hasEligibleCard) {
        pushLog(G, `${label} effect: no ${greyFilter} Heroes in your discard pile (${sourceName}); nothing to KO.`, 'blocked');
        return { targets: [] };
    }
    if (!G.pendingKoDiscardChoices) {
        G.pendingKoDiscardChoices = [];
    }
    const entry = {
        choiceType: 'ko-from-discard',
        playerID: currentPlayer,
        maxCount,
        sourceCardId: cardId,
        greyFilter,
    };
    G.pendingKoDiscardChoices.push(entry);
    pushLog(G, `${label} effect: KO up to ${String(maxCount)} ${greyFilter} Hero(es) from your discard pile (${sourceName}).`, 'neutral');
    return { targets: [], pending: true };
}
/**
 * gain-recruit-current-count-by-trait primitive — the current (defeating) player gains
 * +1 recruit for each of their Heroes matching the trait (co2e Abomination "Fight: You
 * get +1 Recruit for each of your [hc:strength] Heroes.", WP-836 / D-24769).
 *
 * "Your Heroes" is the hand + the cards played this turn still in play, read through the
 * Maestro / Baron Zemo counter. Keyword-less, so it self-narrates via `pushLog`.
 */
function villainEffectGainRecruitCurrentCountByTrait(G, currentPlayer, _cardId, timing, descriptor) {
    const kind = descriptor.requireKind;
    const value = descriptor.requireValue;
    const zones = G.playerZones[currentPlayer];
    if (kind === undefined || value === undefined || !zones) {
        return { targets: [] };
    }
    // why: rules v23 L3436-3447 — "your Heroes" = hand + still in play (D-24529 / D-24692),
    // the same counter Maestro and Baron Zemo read, so the three can never count differently.
    const countedHeroes = listPlayerHeroesMatchingTrait(G, zones, kind, value);
    const label = villainEffectTimingLabel(timing);
    const heroCount = countedHeroes.length;
    if (heroCount === 0) {
        pushLog(G, `${label} effect: you have no ${value} Heroes, so no recruit was gained.`, 'blocked');
        return { targets: [] };
    }
    G.turnEconomy.recruit += heroCount;
    const names = describeTraitCountedHeroes(G, countedHeroes, kind, value);
    pushLog(G, `${label} effect: gained +${String(heroCount)} recruit for your ${String(heroCount)} ${value} Hero(es)${names}.`, 'applied');
    return { targets: [] };
}
/**
 * draw-cards-current-count-by-trait primitive — the current (defeating) player counts
 * their Heroes matching the trait, then draws that many cards (co2e Beetle "Fight: Count
 * your [hc:covert] Heroes, then draw that many cards.", WP-836 / D-24769).
 *
 * Keyword-less, so it self-narrates via `pushLog`. Without a shuffle source it no-ops
 * silently (the `draw-cards-current` precedent: a draw may reshuffle the discard).
 */
function villainEffectDrawCardsCurrentCountByTrait(G, currentPlayer, _cardId, timing, descriptor, shuffleContext) {
    const kind = descriptor.requireKind;
    const value = descriptor.requireValue;
    const zones = G.playerZones[currentPlayer];
    if (kind === undefined || value === undefined || !zones) {
        return { targets: [] };
    }
    // why: "Count …, then draw" — the count (and the names) are taken before the draw, so a
    // drawn Covert Hero never counts itself.
    const countedHeroes = listPlayerHeroesMatchingTrait(G, zones, kind, value);
    const heroCount = countedHeroes.length;
    const label = villainEffectTimingLabel(timing);
    if (heroCount === 0) {
        pushLog(G, `${label} effect: you have no ${value} Heroes, so no cards were drawn.`, 'blocked');
        return { targets: [] };
    }
    if (shuffleContext === undefined) {
        return { targets: [] };
    }
    const names = describeTraitCountedHeroes(G, countedHeroes, kind, value);
    const handSizeBefore = zones.hand.length;
    drawCardsIntoHand(zones, heroCount, shuffleContext);
    const drawn = zones.hand.length - handSizeBefore;
    // why: D-24732 — the defeating player's Fight draw is an extra card drawn this turn.
    G.turnEconomy.cardsDrawn += drawn;
    pushLog(G, `${label} effect: drew ${String(drawn)} card(s) for your ${String(heroCount)} ${value} Hero(es)${names}.`, drawn > 0 ? 'applied' : 'blocked');
    return { targets: [] };
}
/**
 * reveal-top-ko-grey-or-draw-by-class primitive — the current (defeating) player reveals
 * the top card of their deck: a grey Hero is KO'd, a card with one of the listed Hero
 * Classes is drawn, anything else stays on top (co2e Savage Land Mutates "Fight: Reveal
 * the top card of your deck. If it's grey, KO it. If it's [hc:strength] or
 * [hc:instinct], draw it.", WP-836 / D-24769).
 *
 * Keyword-less, so it self-narrates via `pushLog`, each line referencing the revealed card.
 */
function villainEffectRevealTopKoGreyOrDrawByClass(G, currentPlayer, _cardId, timing, descriptor, shuffleContext) {
    const drawHeroClasses = descriptor.drawHeroClasses;
    const zones = G.playerZones[currentPlayer];
    if (drawHeroClasses === undefined || !zones) {
        return { targets: [] };
    }
    const label = villainEffectTimingLabel(timing);
    // why: D-24285 — a reveal on an empty deck reshuffles the discard first (a no-op on an
    // empty discard or a missing shuffle source, so an exhausted deck falls through below).
    if (zones.deck.length === 0) {
        reshuffleDiscardIntoDeck(zones, shuffleContext);
    }
    const topCardId = zones.deck[0];
    if (topCardId === undefined) {
        pushLog(G, `${label} effect: no card to reveal — your deck and discard pile are empty.`, 'blocked');
        return { targets: [] };
    }
    const topName = resolveCardDisplayName(G, topCardId);
    // why: the three outcomes are disjoint — a grey card has no class, so it can never also be
    // Strength or Instinct. A Wound is not grey (rules v23 L3404-3407) and has no class, so it
    // stays on top: "reveal" with no destination leaves the card there (L3201-3207).
    if (isGreyHero(G, topCardId)) {
        zones.deck = zones.deck.slice(1);
        G.ko = koCard(G.ko, topCardId);
        recordOwnedCardKoed(G, currentPlayer, topCardId, 'deck');
        pushLog(G, `${label} effect: revealed "${topName}" (grey) — KO'd it.`, 'applied', topCardId);
        return { targets: [topCardId] };
    }
    const topTraits = offPlayCardTraits(G, topCardId);
    const drawnClass = drawHeroClasses.find((heroClass) => cardTraitMatches(topTraits, 'hero-class', heroClass));
    if (drawnClass === undefined) {
        pushLog(G, `${label} effect: revealed "${topName}" — left on top of your deck.`, 'blocked', topCardId);
        return { targets: [] };
    }
    // why: "draw it" is a real draw (deck → hand, counted in cardsDrawn, D-24732), moved
    // directly rather than through the hero-effect draw path, so the draw lock does not
    // apply — the Patriarch / WP-731 precedent for villain-driven draws.
    const moveResult = moveCardFromZone(zones.deck, zones.hand, topCardId);
    if (!moveResult.found) {
        return { targets: [] };
    }
    zones.deck = moveResult.from;
    zones.hand = moveResult.to;
    G.turnEconomy.cardsDrawn += 1;
    pushLog(G, `${label} effect: revealed "${topName}" (${drawnClass}) — drew it.`, 'applied', topCardId);
    return { targets: [topCardId] };
}
/**
 * override-next-hand-size primitive — set the current (defeating) player's next
 * `onBegin` hand-fill target to the descriptor `magnitude` (the core spider-foes
 * Doctor Octopus villain Fight: "draw eight cards instead of six", D-24307 /
 * WP-503).
 *
 * Writes the WP-497-owned shared `G.handSizeOverrides[currentPlayer]`; the
 * play-phase `onBegin` fill (game.ts) consumes and clears it on the fighting
 * player's next turn. This handler adds NO new `G` field and NO second
 * consumption point (WP-497 owns both).
 *
 * Self-narrates via `pushLog`: like `draw-cards-current`, override-next-hand-size
 * is keyword-less (`descriptorToLegacyKeyword` returns undefined), so the executor
 * records no `VillainEffectResult` and the generic `<timing> effect:` line never
 * fires — this push is the user-visible surface. Returns `{ targets: [] }` (the
 * override touches no card).
 */
function villainEffectOverrideNextHandSize(G, currentPlayer, _cardId, timing, descriptor) {
    const targetHandSize = descriptor.magnitude;
    // why: EC-538 — a malformed hook lacking `magnitude` (reachable only via a
    // hand-built test hook — the parser always sets it) no-ops rather than writing an
    // undefined override, which the WP-497 `onBegin` fill would then read as `??
    // HAND_SIZE` (a silent wrong fill). Guard it here.
    if (targetHandSize === undefined) {
        return { targets: [] };
    }
    // why: D-24307 — the villain-side WRITER of the WP-497-owned field. Lazy-init the
    // container with WP-497's exact idiom (never seeded in buildInitialGameState);
    // WP-497's game.ts `onBegin` reads `handSizeOverrides[player] ?? HAND_SIZE` and
    // deletes the entry after one fill. NO new `G` field, NO second consumption site.
    if (G.handSizeOverrides === undefined) {
        G.handSizeOverrides = {};
    }
    G.handSizeOverrides[currentPlayer] = targetHandSize;
    // why: self-narrate (keyword-less; the D-24266 unmarked-ability breadcrumb is
    // removed by marking the card). `G.messages` is hash-excluded (D-24081), so this
    // adds no replay surface. The label is derived from the fired timing (Doc Ock is
    // Fight-timed) for correctness at any fire site.
    const label = villainEffectTimingLabel(timing);
    pushLog(G, `${label} effect: your next hand draws ${String(targetHandSize)} cards instead of ${String(HAND_SIZE)}.`, 'applied');
    return { targets: [] };
}
/**
 * add-next-hand-size primitive — increase the current (defeating) player's next
 * `onBegin` hand-fill target by `magnitude` EXTRA cards (Savage Land Mutates "Fight:
 * When you draw a new hand of cards at the end of this turn, draw an extra card.",
 * D-24352 / WP-543).
 *
 * The ADDITIVE sibling of `override-next-hand-size` (WP-503): it writes
 * `(G.handSizeOverrides[currentPlayer] ?? HAND_SIZE) + magnitude` rather than the
 * absolute `= magnitude`, so defeating two Savage Land Mutates in one turn stacks to a
 * next hand of `HAND_SIZE + 2` (8). Reuses the WP-497-owned shared
 * `G.handSizeOverrides[currentPlayer]`; the play-phase `onBegin` fill (game.ts) consumes
 * and clears it on the fighting player's next turn, reading the accumulated absolute
 * value unchanged — this handler adds NO new `G` field and NO second consumption point.
 *
 * Self-narrates via `pushLog`: like `override-next-hand-size`, add-next-hand-size is
 * keyword-less (`descriptorToLegacyKeyword` returns undefined), so the executor records
 * no `VillainEffectResult` and the generic `<timing> effect:` line never fires — this
 * push is the user-visible surface. Returns `{ targets: [] }` (the bonus touches no card).
 */
function villainEffectAddNextHandSize(G, currentPlayer, _cardId, timing, descriptor) {
    const extraCards = descriptor.magnitude;
    // why: EC-578 — a malformed hook lacking `magnitude` (reachable only via a
    // hand-built test hook — the parser always sets it) no-ops rather than adding an
    // undefined bonus (which would poison the accumulator with NaN).
    if (extraCards === undefined) {
        return { targets: [] };
    }
    // why: D-24352 — lazy-init the WP-497-owned container with WP-497's exact idiom
    // (never seeded in buildInitialGameState). This is the ADDITIVE writer: read the
    // player's current next-hand target (or HAND_SIZE if none set yet this turn) and add
    // `extraCards`, so multiple Savage Land defeats in one turn accumulate. Contrast
    // villainEffectOverrideNextHandSize, which writes the absolute `= magnitude`. NO new
    // `G` field, NO second consumption site (WP-497's game.ts onBegin owns consumption).
    if (G.handSizeOverrides === undefined) {
        G.handSizeOverrides = {};
    }
    const nextHandSize = (G.handSizeOverrides[currentPlayer] ?? HAND_SIZE) + extraCards;
    G.handSizeOverrides[currentPlayer] = nextHandSize;
    // why: self-narrate (keyword-less). `G.messages` is hash-excluded (D-24081), so this
    // adds no replay surface. The label is derived from the fired timing (Savage Land is
    // Fight-timed) for correctness at any fire site.
    const label = villainEffectTimingLabel(timing);
    pushLog(G, `${label} effect: your next hand draws ${String(nextHandSize)} cards (${extraCards > 0 ? '+' : ''}${String(extraCards)} extra).`, 'applied');
    return { targets: [] };
}
/**
 * gain-recruit-current primitive — the current (defeating) player gains `magnitude`
 * recruit points this turn (Hand Ninjas "Fight: You get +1 recruit.", D-24350 /
 * WP-541).
 *
 * Adds to `G.turnEconomy.recruit` (the per-turn recruit accumulator playCard also
 * feeds and recruitHero spends). `magnitude` is the recruit count parsed from the
 * `:N` token (default 1). Self-narrates via `pushLog`: like `draw-cards-current`,
 * gain-recruit-current is keyword-less (`descriptorToLegacyKeyword` returns
 * undefined), so the executor records no `VillainEffectResult` and the generic
 * `<timing> effect:` line never fires — this push is the user-visible surface.
 * Returns `{ targets: [] }` (a recruit gain touches no card).
 */
function villainEffectGainRecruitCurrent(G, _currentPlayer, _cardId, timing, descriptor) {
    // why: EC-576 — the parser always sets `magnitude` (default 1); a malformed
    // hand-built test hook lacking it falls back to 1 rather than adding NaN to the
    // recruit accumulator.
    const recruitGain = descriptor.magnitude ?? 1;
    G.turnEconomy.recruit += recruitGain;
    // why: self-narrate (keyword-less). `G.messages` is hash-excluded (D-24081), so
    // this adds no replay surface. The label is derived from the fired timing (Hand
    // Ninjas is Fight-timed) for correctness at any fire site.
    const label = villainEffectTimingLabel(timing);
    pushLog(G, `${label} effect: gained +${String(recruitGain)} recruit.`, 'applied');
    return { targets: [] };
}
/**
 * gain-recruit-current-per-hero-class primitive — the current (defeating) player
 * gains +1 recruit for each distinct Hero Class they have (Captain Zolandia "Fight:
 * You get +1 Recruit for each Hero Class you have.", D-24750 / WP-817).
 *
 * Adds the count to `G.turnEconomy.recruit`. Keyword-less, so it self-narrates via
 * `pushLog`; a count of 0 logs a `blocked` line and adds nothing. Returns
 * `{ targets: [] }` (a recruit gain touches no card).
 */
function villainEffectGainRecruitCurrentPerHeroClass(G, currentPlayer, _cardId, timing) {
    // why: D-24529 / D-24694 — "Hero Classes you have" is the hero side's counter over the
    // cards in hand and the cards played this turn that are still in play (rules v23
    // L3436-3446); reusing it means Captain America's Avengers Assemble! and Zolandia can
    // never count differently.
    const heroClassCount = countDistinctHeroClassesYouHave(G, currentPlayer);
    const label = villainEffectTimingLabel(timing);
    if (heroClassCount === 0) {
        pushLog(G, `${label} effect: you have no Hero Classes in hand or play, so no recruit was gained.`, 'blocked');
        return { targets: [] };
    }
    G.turnEconomy.recruit += heroClassCount;
    pushLog(G, `${label} effect: gained +${String(heroClassCount)} recruit for the ${String(heroClassCount)} Hero Class(es) you have.`, 'applied');
    return { targets: [] };
}
/**
 * discard-hero-each-player-by-cost primitive — records that every player owes a
 * discard of a Hero whose cost is one of `descriptor.eligibleCosts` (Primus "Escape:
 * each player discards a Hero that costs 2, 3, 5, or 7.", D-24750 / WP-817).
 *
 * Mutates only the lazy `G.pendingEscapeEffectDiscards` queue; the escape opener
 * (villainEscapeProcedure.ts) opens the every-player seat choice after the move.
 * Keyword-less, self-narrates. Returns `{ targets: [] }`.
 */
function villainEffectDiscardHeroEachPlayerByCost(G, _currentPlayer, cardId, timing, descriptor) {
    const label = villainEffectTimingLabel(timing);
    const eligibleCosts = descriptor.eligibleCosts;
    if (eligibleCosts === undefined || eligibleCosts.length === 0) {
        pushLog(G, `${label} effect: no discard costs were given; no effect.`, 'blocked');
        return { targets: [] };
    }
    // why: D-24750 — the executor has no `events`, so it cannot park the multi-seat choice
    // itself; it records the owed discard for the escape opener, which drains it after
    // every owed escape procedure (lazy-init here, never in Game.setup).
    if (G.pendingEscapeEffectDiscards === undefined) {
        G.pendingEscapeEffectDiscards = [];
    }
    G.pendingEscapeEffectDiscards.push({ escapedCardId: cardId, eligibleCosts: [...eligibleCosts] });
    pushLog(G, `${label} effect: each player must discard a Hero that costs ${formatEligibleCostList(eligibleCosts)}.`, 'applied');
    return { targets: [] };
}
/**
 * discard-hero-each-player-by-trait primitive — records that every player owes a
 * discard of a Hero with the descriptor's trait (co2e Mystique "Ambush: Each player
 * discards an X-Men Hero.", D-24770 / WP-837).
 *
 * Mutates only the lazy `G.pendingEscapeEffectDiscards` queue; the escape opener opens
 * the every-player seat choice after the move. Keyword-less, self-narrates.
 */
function villainEffectDiscardHeroEachPlayerByTrait(G, _currentPlayer, cardId, timing, descriptor) {
    return recordOwedTraitDiscard(G, cardId, timing, descriptor, false);
}
/**
 * discard-hero-or-wound-each-player-by-trait primitive — the same owed discard, where
 * each player may gain a Wound instead (co2e Frost Giant Warrior "Ambush: Each player
 * discards a Ranged Hero or gains a Wound.", D-24770 / WP-837).
 */
function villainEffectDiscardHeroOrWoundEachPlayerByTrait(G, _currentPlayer, cardId, timing, descriptor) {
    return recordOwedTraitDiscard(G, cardId, timing, descriptor, true);
}
/**
 * Records an owed every-player trait discard on WP-817's queue and narrates it (shared by
 * the two trait-discard primitives, D-24770).
 *
 * @param G - Game state (mutated: the lazy queue and the hash-excluded log).
 * @param cardId - The Villain whose effect owes the discard.
 * @param timing - The timing that fired.
 * @param descriptor - The descriptor carrying `requireKind` + `requireValue`.
 * @param hasWoundAlternative - Whether a player may gain a Wound instead.
 * @returns `{ targets: [] }`.
 */
function recordOwedTraitDiscard(G, cardId, timing, descriptor, hasWoundAlternative) {
    const label = villainEffectTimingLabel(timing);
    const requireKind = descriptor.requireKind;
    const requireValue = descriptor.requireValue;
    if (requireKind === undefined || requireValue === undefined) {
        pushLog(G, `${label} effect: no Hero trait was given; no effect.`, 'blocked');
        return { targets: [] };
    }
    const requirementLabel = describeRevealRequirement(requireKind, requireValue);
    // why: D-24770 — the executor has no `events`, so like Primus's Escape (D-24750) the
    // handler records the owed discard and the turn.onMove opener parks it after the move.
    // One FIFO keeps rule order (an escape in the same reveal records first).
    if (G.pendingEscapeEffectDiscards === undefined) {
        G.pendingEscapeEffectDiscards = [];
    }
    G.pendingEscapeEffectDiscards.push({
        escapedCardId: cardId,
        eligibleCosts: [],
        traitFilter: { requireKind, requireValue, requirementLabel, effectLabel: label, hasWoundAlternative },
    });
    let woundClause = '';
    if (hasWoundAlternative) {
        woundClause = ' or gain a Wound';
    }
    pushLog(G, `${label} effect: each player must discard ${articleFor(requirementLabel)} ${requirementLabel} Hero${woundClause}.`, 'applied');
    return { targets: [] };
}
/**
 * The card-name keys of a card (WP-837 / D-24770): the card slug of its base id, and of
 * both faces' base ids for a split card.
 *
 * @param G - Game state (read-only; the split-face map).
 * @param cardId - A Hero card instance id (`{set}/{hero}/{card}#{n}`).
 * @returns The card slugs; empty for an id without three `/` segments.
 */
// why: a card's name key is its card slug (the third `/` segment), so reprints of one
// printed name under different sets match (core vs co2e Cyclops); a split card counts as
// both faces' names off play (rules v23 L2773-2778).
function cardNameKeysOf(G, cardId) {
    const baseIds = [];
    const pair = resolveSplitFacePair(G, cardId);
    if (pair === null) {
        baseIds.push(cardId.split('#')[0]);
    }
    else {
        baseIds.push(pair.faceA.split('#')[0]);
        baseIds.push(pair.faceB.split('#')[0]);
    }
    const keys = [];
    for (const baseId of baseIds) {
        const segments = baseId.split('/');
        if (segments.length === 3) {
            keys.push(segments[2]);
        }
    }
    return keys;
}
/**
 * Whether any of a card's name keys is in the HQ key set (D-24770).
 *
 * @param G - Game state (read-only).
 * @param cardId - The hand card to test.
 * @param hqKeys - The union of the HQ Heroes' card-name keys.
 * @returns True when the card shares a card name with an HQ Hero.
 */
function cardSharesNameWithHq(G, cardId, hqKeys) {
    for (const key of cardNameKeysOf(G, cardId)) {
        if (hqKeys.has(key)) {
            return true;
        }
    }
    return false;
}
/**
 * discard-hand-matching-hq-card-names-each-player primitive — every player discards each
 * hand card that shares a card name with a Hero in the HQ (co2e Juggernaut's Ambush, first
 * half, D-24770 / WP-837). Auto-resolves; keyword-less, self-narrates.
 */
function villainEffectDiscardHandMatchingHqCardNamesEachPlayer(G, currentPlayer, _cardId, timing, _descriptor) {
    const label = villainEffectTimingLabel(timing);
    const hqKeys = new Set();
    for (const hqCardId of G.hq) {
        if (hqCardId === null || hqCardId === undefined) {
            continue;
        }
        for (const key of cardNameKeysOf(G, hqCardId)) {
            hqKeys.add(key);
        }
    }
    if (hqKeys.size === 0) {
        pushLog(G, `${label} effect: the HQ is empty, so no player discards.`, 'blocked');
        return { targets: [] };
    }
    let hasAnyDiscard = false;
    // why: D-24780 — start with the current player, then clockwise (rules v23 L2247): the
    // discards fire return-on-discard / teleport reactions, whose queues fill in loop order.
    for (const seat of seatsInTurnOrderFrom(Object.keys(G.playerZones), currentPlayer)) {
        const zones = G.playerZones[seat];
        if (zones === undefined) {
            continue;
        }
        const discardedNames = [];
        for (const handCardId of [...zones.hand]) {
            if (cardSharesNameWithHq(G, handCardId, hqKeys) && discardFromHand(G, seat, handCardId)) {
                discardedNames.push(resolveCardDisplayName(G, handCardId));
            }
        }
        if (discardedNames.length > 0) {
            hasAnyDiscard = true;
            pushLog(G, `${label} effect: Player ${seat} discarded ${discardedNames.join(', ')} (same card name as a Hero in the HQ).`, 'applied');
        }
    }
    if (!hasAnyDiscard) {
        pushLog(G, `${label} effect: no player had a card with the same card name as a Hero in the HQ.`, 'blocked');
    }
    return { targets: [] };
}
/**
 * hq-heroes-to-hero-deck-bottom primitive — every HQ Hero goes to the bottom of the Hero
 * Deck in slot order, then the HQ refills (co2e Juggernaut's Ambush, second half,
 * D-24770 / WP-837). Auto-resolves; keyword-less, self-narrates.
 */
function villainEffectHqHeroesToHeroDeckBottom(G, _currentPlayer, _cardId, timing, _descriptor) {
    const label = villainEffectTimingLabel(timing);
    const movedNames = [];
    // why: rules v23 L749-753 — move every Hero first (a snapshot), then refill; refilling
    // as each slot empties would draw a Hero just sent to the bottom when the deck is short.
    // `G.hqHaunters` is never touched: a haunter stays on its space (rules v23 L1539-1541).
    for (let hqIndex = 0; hqIndex < G.hq.length; hqIndex++) {
        const hqCardId = G.hq[hqIndex];
        if (hqCardId === null || hqCardId === undefined) {
            continue;
        }
        G.heroDeck.push(hqCardId);
        G.hq[hqIndex] = null;
        movedNames.push(resolveCardDisplayName(G, hqCardId));
    }
    if (movedNames.length === 0) {
        pushLog(G, `${label} effect: the HQ is empty; no Hero was put on the bottom of the Hero Deck.`, 'blocked');
        return { targets: [] };
    }
    for (let hqIndex = 0; hqIndex < G.hq.length; hqIndex++) {
        if (G.hq[hqIndex] !== null && G.hq[hqIndex] !== undefined) {
            continue;
        }
        const result = refillHqSlot(G.hq, hqIndex, G.heroDeck);
        G.hq = result.hq;
        G.heroDeck = result.heroDeck;
    }
    pushLog(G, `${label} effect: put ${String(movedNames.length)} Hero(es) from the HQ on the bottom of the Hero Deck (${movedNames.join(', ')}); the HQ refilled.`, 'applied');
    return { targets: [] };
}
/**
 * gain-officer-current primitive — the current (defeating) player gains one
 * S.H.I.E.L.D. Officer, moved from the shared Officer supply pile to their discard
 * (HYDRA Kidnappers "Fight: You may gain a S.H.I.E.L.D. Officer.", D-24350 / WP-541).
 *
 * Auto-resolved: HYDRA Kidnappers' "may gain an Officer" is a pure benefit with no
 * downside, so it auto-takes with no interactive choice (D-24350). Mirrors the
 * `gainWound` supply-pile move (pile[0] is the top card, removed via `slice(1)`).
 * Self-narrates via `pushLog`: keyword-less (`descriptorToLegacyKeyword` returns
 * undefined), so the executor records no `VillainEffectResult` and the generic
 * `<timing> effect:` line never fires — this push is the user-visible surface.
 * Returns `{ targets: [] }` (the Officer is a generic supply token narrated by the
 * log line, not a named card).
 */
function villainEffectGainOfficerCurrent(G, currentPlayer, _cardId, timing, _descriptor) {
    const label = villainEffectTimingLabel(timing);
    const zones = G.playerZones[currentPlayer];
    // why: an empty Officer pile (or a missing current-player zone in a narrow test
    // mock) is a logged no-op, never a throw — the rule is a beneficial "may", so
    // having nothing to gain simply does nothing.
    if (!zones || G.piles.officers.length === 0) {
        pushLog(G, `${label} effect: no S.H.I.E.L.D. Officer to gain.`, 'blocked');
        return { targets: [] };
    }
    // why: pile[0] is the top card (the locked supply-pile convention gainWound uses);
    // move it to the current player's discard. All Officers share SHIELD_OFFICER_EXT_ID.
    const officerId = G.piles.officers[0];
    G.piles.officers = G.piles.officers.slice(1);
    zones.discard = [...zones.discard, officerId];
    pushLog(G, `${label} effect: gained a S.H.I.E.L.D. Officer.`, 'applied');
    return { targets: [] };
}
/**
 * Counts the Wounds in a player's hand + discard pile (the zones Ymir's Fight
 * names). A missing zone counts as zero.
 */
function countWoundsInHandAndDiscard(G, playerId) {
    const zones = G.playerZones[playerId];
    if (!zones) {
        return 0;
    }
    let woundCount = 0;
    for (const cardId of zones.hand) {
        if (cardId === WOUND_EXT_ID) {
            woundCount += 1;
        }
    }
    for (const cardId of zones.discard) {
        if (cardId === WOUND_EXT_ID) {
            woundCount += 1;
        }
    }
    return woundCount;
}
/**
 * Picks the player Ymir's "Choose a player" resolves to: the player with the most
 * Wounds in hand + discard (D-24684, amending D-24329). Ties go to the current
 * player, then to the lowest seat id — deterministic, no player-selection UI.
 */
function selectPlayerWithMostWounds(G, currentPlayer) {
    // why: D-24684 — the D-24329 collapse to the fighting player wasted the effect in
    // co-op when the fighter had no Wounds and a teammate did (live 2p match
    // r8_2EFHd3Ah, 36.2.37: "KO'd 0" while the bot held Wounds). KO'ing Wounds is pure
    // upside for whoever holds them, so a rational co-op chooser picks the player
    // with the most. Seeding with the current player makes them win every tie.
    let chosenPlayer = currentPlayer;
    let chosenCount = countWoundsInHandAndDiscard(G, currentPlayer);
    const playerIds = Object.keys(G.playerZones).sort();
    for (const playerId of playerIds) {
        const woundCount = countWoundsInHandAndDiscard(G, playerId);
        if (woundCount > chosenCount) {
            chosenPlayer = playerId;
            chosenCount = woundCount;
        }
    }
    return chosenPlayer;
}
/**
 * ko-wounds-current-hand-and-discard primitive — the chosen player KOs every Wound
 * from their own hand + discard pile (Ymir, Frost Giant King "Fight: Choose a
 * player. That player KOs any number of Wounds from their hand and discard pile.",
 * D-24329 / WP-516, chooser amended by D-24684).
 *
 * Auto-resolved and deterministic. "Choose a player" resolves to the player with the
 * most Wounds in hand + discard (ties → the current player), and "any number" to
 * KO-all: KO'ing Wounds is pure upside, so there is no player-selection UI and no
 * partial-KO choice. The primitive keeps its WP-516 name (it is a card-data marker);
 * "current" no longer describes the target. Self-narrates via `pushLog` (keyword-less
 * — `descriptorToLegacyKeyword` returns undefined, so no `VillainEffectResult` is
 * recorded and the generic `<timing> effect:` line never fires). Returns the KO'd
 * Wound ext_ids as `targets` for parity with `villainEffectKoHeroesCurrentByTrait`
 * (dropped by the recording path).
 */
function villainEffectKoWoundsCurrentHandAndDiscard(G, currentPlayer, _cardId, timing, _descriptor) {
    const label = villainEffectTimingLabel(timing);
    const chosenPlayer = selectPlayerWithMostWounds(G, currentPlayer);
    const zones = G.playerZones[chosenPlayer];
    // why: single-target — only the chosen player's zones change (unlike the
    // each-player KO helper). A missing zone is a reachable no-op guard.
    if (!zones) {
        return { targets: [] };
    }
    // why: D-24329 — a Wound is the shared synthetic `WOUND_EXT_ID` (`pile-wound`);
    // match by ext_id equality (no registry read, no trait predicate). Scan hand +
    // discard ONLY (the printed zones) — Wounds enter via gainWound (→ discard) or a
    // draw (→ hand) and are never played into inPlay, so inPlay is deliberately not
    // scanned (distinct from ko-heroes-current-by-trait, whose Heroes can sit in-play).
    // Rebuild each zone with a kept-list for...of (no .reduce(), no splice-in-loop).
    const koedWounds = [];
    // why: WP-855 / D-24791 — the zone each KO'd Wound left, index-aligned with koedWounds
    // (pushed in the same two loops), for the owned-KO recorder's source zone.
    const koedWoundZones = [];
    const remainingHand = [];
    for (const cardId of zones.hand) {
        if (cardId === WOUND_EXT_ID) {
            koedWounds.push(cardId);
            koedWoundZones.push('hand');
        }
        else {
            remainingHand.push(cardId);
        }
    }
    const remainingDiscard = [];
    for (const cardId of zones.discard) {
        if (cardId === WOUND_EXT_ID) {
            koedWounds.push(cardId);
            koedWoundZones.push('discard');
        }
        else {
            remainingDiscard.push(cardId);
        }
    }
    zones.hand = remainingHand;
    zones.discard = remainingDiscard;
    // why: KO'd Wounds go to the general KO pile (`G.ko`), NOT back to the
    // `G.piles.wounds` supply — a KO removes them from play (koCard is a
    // destination-only append; the source removal is the zone rebuild above).
    for (let index = 0; index < koedWounds.length; index++) {
        // why: WP-855 / D-24791 — koedWounds and koedWoundZones are pushed together in the scan
        // loops above, so they have the same length and every index read here is defined.
        const woundId = koedWounds[index];
        const woundZone = koedWoundZones[index];
        G.ko = koCard(G.ko, woundId);
        // why: WP-853 / D-24789 — the chosen player owns these Wounds; one count each.
        recordOwnedCardKoed(G, chosenPlayer, woundId, woundZone);
    }
    // why: self-narrate (keyword-less; the D-24266 unmarked-ability breadcrumb is
    // removed by marking the card). `G.messages` is hash-excluded (D-24081). Honest
    // colour per the WP-434 contract: ≥1 Wound KO'd → `applied`; zero Wounds is a
    // reachable no-op → `blocked`. Label derived from the fired timing (Ymir is Fight).
    // The applied line names the chosen player (D-24683 attribution) because it may not
    // be the one who fought.
    if (koedWounds.length === 0) {
        pushLog(G, `${label} effect: no player had a Wound in their hand or discard pile to KO.`, 'blocked');
        return { targets: koedWounds };
    }
    pushLog(G, `${label} effect: Player ${chosenPlayer} KO'd ${String(koedWounds.length)} Wound(s) from their hand and discard pile.`, 'applied');
    return { targets: koedWounds };
}
/**
 * Whether a revealed deck-top card is "cullable" — worth KO'ing for a rational
 * cooperative chooser (D-24332 / WP-519). True iff the card is a Wound or a basic
 * STARTING S.H.I.E.L.D. card (Agent / Trooper).
 *
 * // why: WP-603 / D-24413 — the Melter handler no longer calls this (it now parks an
 * interactive choice); this predicate is now the BOT/SIM default-pick in
 * ai.legalMoves (`keep = !isCullableDeckTopCard(cardId)`), reproducing the WP-519
 * auto-resolve outcome byte-identically so bot/replay/par runs are unchanged. Hence
 * the `export`.
 *
 * This is the `selectScryKoTarget` tiers-1–2 "worst-worthy" set (D-24267): thinning
 * a Wound or a weak starter from any deck is pure deck-building upside. It
 * deliberately EXCLUDES the recruited S.H.I.E.L.D. Officer and every real Hero — a
 * rational player keeps those, and Melter's Fight lets them "put it back" — and it
 * EXCLUDES `selectScryKoTarget`'s tier-3 lex-lowest fallback, which exists only
 * because scry-ko MUST KO one of two cards; Melter's keep-option means an
 * un-cullable top is simply left in place. Closed-enum membership (no registry read,
 * no trait predicate), so the decision stays deterministic (no `ctx.random`).
 *
 * @param cardId - The revealed deck-top card's ext_id.
 * @returns True when the card is a Wound or a basic starting S.H.I.E.L.D. card.
 */
export function isCullableDeckTopCard(cardId) {
    return (cardId === WOUND_EXT_ID ||
        cardId === SHIELD_AGENT_EXT_ID ||
        cardId === SHIELD_TROOPER_EXT_ID);
}
/**
 * ko-cullable-each-deck-top primitive — each player reveals the top card of their
 * deck and the FIGHTING player is prompted to KO or keep each one (Melter, Masters of
 * Evil — "Fight: Each player reveals the top card of their deck. For each card, you
 * choose to KO it or put it back.", D-24413 / WP-603).
 *
 * // why: D-24413 supersedes D-24332 — Melter's Fight is no longer a deterministic
 * cullable auto-resolve. It reveals every player's deck top (sorted, reshuffle-on-empty
 * per D-24285), snapshots each `{ ownerPlayerID, cardId }`, and parks ONE
 * PendingMelterKoChoice for the fighting (active) player carrying the whole snapshot —
 * KOing nothing at park time. The player then resolves each card KO-or-keep via
 * resolveMelterKoChoice. The `ko-cullable-each-deck-top` primitive token is RETAINED
 * (renaming would churn the marker + card-data); `isCullableDeckTopCard` is now the
 * bot/sim default-pick in ai.legalMoves (keep = !cullable), so bot/replay/par runs
 * reproduce the WP-519 outcome byte-identically — only live human play gets the prompt.
 * Follows the WP-470 / D-24282 scry-ko SNAPSHOT discipline: the reveal never removes a
 * card, so "keep" is a no-op and the block-all guard freezes every deck top. Keyword-
 * less — `descriptorToLegacyKeyword` returns undefined, so no `VillainEffectResult` is
 * recorded and the generic `<timing> effect:` line never fires; it self-narrates.
 * Returns `{ targets: [], pending: true }` when it parks, `{ targets: [] }` on the
 * reachable no-op (no player had a revealable deck top).
 */
function villainEffectKoCullableEachDeckTop(G, currentPlayer, _cardId, timing, _descriptor, shuffleContext) {
    const revealedTops = [];
    // why: D-18902 — sorted player-id iteration for replay determinism, matching the
    // each-player wound / reveal-or-wound paths. "Each player" is faithful: every
    // player's deck top is revealed, not only the fighting player's.
    for (const playerId of Object.keys(G.playerZones).sort()) {
        const zones = G.playerZones[playerId];
        if (!zones) {
            continue;
        }
        // why: D-24285 — "reveals the top card" is a reveal, so an empty deck reshuffles
        // the player's discard into the deck first (the Legendary reveal-reshuffle rule,
        // the scry-ko precedent). reshuffleDiscardIntoDeck no-ops on an empty discard or
        // an absent shuffleContext, so a genuinely exhausted deck + discard contributes no
        // revealed card (no reveal, no choice for that player — never a hollow).
        if (zones.deck.length === 0) {
            reshuffleDiscardIntoDeck(zones, shuffleContext);
        }
        if (zones.deck.length === 0) {
            continue;
        }
        // why: SNAPSHOT only — the card is NOT removed. The block-all guard freezes every
        // deck top while the choice is pending, so this snapshot cannot drift; the resolve
        // move KOs by owner+ext_id (a "keep" then leaves the card exactly where it is).
        revealedTops.push({ ownerPlayerID: playerId, cardId: zones.deck[0] });
    }
    const label = villainEffectTimingLabel(timing);
    if (revealedTops.length === 0) {
        // why: reachable no-op — every deck (and discard) was exhausted, so nothing was
        // revealed and no choice is parked. Self-narrate (keyword-less); G.messages is
        // hash-excluded (D-24081). `blocked` per the WP-434 contract (nothing landed).
        pushLog(G, `${label} effect: no player had a card to reveal.`, 'blocked');
        return { targets: [] };
    }
    // why: WP-603 / D-24413 — park ONE choice for the fighting player carrying the whole
    // snapshot; lazily initialize the queue at the park site (never in Game.setup).
    if (!G.pendingMelterKoChoices)
        G.pendingMelterKoChoices = [];
    G.pendingMelterKoChoices.push({
        choiceType: 'melter-ko',
        playerID: currentPlayer,
        revealedTops,
    });
    // why: self-narrate the park so the log records the choice opened; the resolve move
    // narrates each KO/keep. Outcome `neutral` — nothing has landed yet.
    pushLog(G, `${label} effect: revealed ${String(revealedTops.length)} deck top(s) — choose to KO or keep each.`, 'neutral');
    // why: WP-316 / D-24102 — pending: true marks the parked interactive choice; no card
    // is KO'd yet, so targets stays []. (Dropped by the recording path anyway — the
    // primitive is keyword-less — but the shape is kept parallel to the other parkers.)
    return { targets: [], pending: true };
}
// why: D-24296 — the basic S.H.I.E.L.D. cards (starting Agents + Troopers, and the
// recruited S.H.I.E.L.D. Officer) ARE team S.H.I.E.L.D. physically, so the Destroyer
// "KO all your [team:shield] Heroes" is meant to wipe them — but they are synthetic
// game-component cards with no `G.cardTraits` entry (cardTraits is built only from
// registry hero entries, buildCardTraits.ts), so the generic team predicate misses
// them and the Fight KO'd zero (live Loki/Thor 2p, 2026-08-03). This set names the
// three teamless basic-S.H.I.E.L.D. ext_ids for the KO handler ONLY.
const BASIC_SHIELD_EXT_IDS = new Set([
    SHIELD_AGENT_EXT_ID,
    SHIELD_TROOPER_EXT_ID,
    SHIELD_OFFICER_EXT_ID,
]);
/**
 * Whether a card matches the ko-heroes-current-by-trait predicate — the shared
 * `cardTraitMatches`, WIDENED so a `team:shield` predicate also matches the three
 * teamless basic-S.H.I.E.L.D. cards (D-24296).
 *
 * Deliberately local to the Destroyer KO handler: the shared `cardTraitMatches`,
 * `playerHasHeroMatchingTrait`, and `listPlayerHeroesMatchingTrait` are LEFT
 * UNCHANGED, so the corpus-wide `[team:shield]` synergies and Baron Zemo's
 * rescue-by-count are unaffected (narrow fix — operator ruling 2026-08-03).
 *
 * @param isTraitMatch - Whether the card already matches the predicate (the zone's own read).
 * @param cardId - The card being tested.
 * @param kind - The predicate kind ('team' | 'hero-class').
 * @param value - The normalized trait slug to match.
 * @returns True if the card matches the trait, or is a basic S.H.I.E.L.D. card
 *   under a `team:shield` predicate.
 */
// why: D-24724 / D-24726 — the caller passes the zone's own match (off-play traits for the hand, the
// effective class and team for in-play), so this helper adds only the basic-S.H.I.E.L.D. widening.
function koHeroMatchesTraitOrBasicShield(isTraitMatch, cardId, kind, value) {
    if (isTraitMatch) {
        return true;
    }
    // why: only a team:shield predicate rescues the teamless basic-S.H.I.E.L.D. cards;
    // a hero-class predicate (or any other team value) never matches them.
    return kind === 'team' && value === 'shield' && BASIC_SHIELD_EXT_IDS.has(cardId);
}
/**
 * ko-heroes-current-by-trait primitive — KO EVERY current-player Hero matching the
 * trait predicate, from hand + in-play (the Destroyer "Fight: KO all your
 * [team:shield] Heroes.", D-24290 / WP-485).
 *
 * Auto-resolved (KO all matching — no player choice) and deterministic. Scans hand
 * then in-play in array order; a card matching the predicate is KO'd, the rest stay.
 * Self-narrates via `pushLog` (keyword-less — no `VillainEffectResult`); returns the
 * KO'd ext_ids as `targets` for parity (dropped by the recording path).
 */
function villainEffectKoHeroesCurrentByTrait(G, currentPlayer, _cardId, _timing, descriptor) {
    const requireKind = descriptor.requireKind;
    const requireValue = descriptor.requireValue;
    // why: defensive — a well-formed descriptor always carries both predicate fields
    // (parseParameterizedEffect sets them together). A malformed hook lacking them
    // cannot evaluate the predicate, so nothing is KO'd (reachable only via a
    // hand-built test hook, never from the parser).
    if (requireKind === undefined || requireValue === undefined) {
        return { targets: [] };
    }
    const zones = G.playerZones[currentPlayer];
    if (!zones) {
        return { targets: [] };
    }
    // why: operator ruling 2026-08-01 — "your Heroes" includes Heroes played this
    // turn, which sit in `inPlay` (the Fight effect resolves after the play phase),
    // so the KO scans hand + in-play. This is the Destroyer's own scan, NOT the
    // per-player KO helper (koOneHeroForPlayer), whose one-card starter-first
    // selection is wrong for a "KO ALL matching" effect.
    const targets = [];
    // why: D-24693 — the zone each KO'd card left, index-aligned with targets, for the log.
    const targetZones = [];
    const remainingHand = [];
    // why: WP-772 / D-24604 — the hand is off play, so a split card there matches on either half's
    // class (via the split-face map); the in-play loop below keeps the chosen face only.
    for (const cardId of zones.hand) {
        const isHandMatch = cardTraitMatches(offPlayCardTraits(G, cardId), requireKind, requireValue);
        if (koHeroMatchesTraitOrBasicShield(isHandMatch, cardId, requireKind, requireValue)) {
            targets.push(cardId);
            targetZones.push('hand');
        }
        else {
            remainingHand.push(cardId);
        }
    }
    const remainingInPlay = [];
    for (const cardId of zones.inPlay) {
        const isInPlayMatch = playedCardTraitMatches(G, cardId, requireKind, requireValue);
        if (koHeroMatchesTraitOrBasicShield(isInPlayMatch, cardId, requireKind, requireValue)) {
            targets.push(cardId);
            targetZones.push('inPlay');
        }
        else {
            remainingInPlay.push(cardId);
        }
    }
    zones.hand = remainingHand;
    zones.inPlay = remainingInPlay;
    for (let index = 0; index < targets.length; index++) {
        // why: WP-855 / D-24791 — targets and targetZones are pushed together in the scan loops
        // above, so they have the same length and every index read here is defined.
        const koedId = targets[index];
        const koedZone = targetZones[index];
        G.ko = koCard(G.ko, koedId);
        recordOwnedCardKoed(G, currentPlayer, koedId, koedZone);
    }
    // why: D-24290 — Fight-timed self-narration (see draw-cards-current). Zero
    // matching Heroes is a reachable no-op (`blocked`), never a hollow record.
    // why: D-24693 — name each KO'd card and its zone (hand or play) when any were KO'd.
    let namedTargets = '';
    if (targets.length > 0) {
        const names = targets.map((koedId, targetIndex) => nameKoTarget(G, koedId, targetZones[targetIndex])).join(', ');
        namedTargets = ` (${names})`;
    }
    pushLog(G, `Fight effect: KO'd ${String(targets.length)} of your ${requireValue} Hero(es)${namedTargets}.`, targets.length > 0 ? 'applied' : 'blocked');
    return { targets };
}
/**
 * ko-heroes-current-count-by-trait primitive — the current player KOs one of their own
 * Heroes per Hero matching the trait predicate (hand + in-play), choosing WHICH Heroes
 * die (core radiation Maestro "Fight: For each of your [hc:strength] Heroes, KO one of
 * your Heroes.", D-24353 / WP-544).
 *
 * The trait supplies only the COUNT — contrast the sibling `ko-heroes-current-by-trait`
 * (Destroyer), which KOs the Heroes that MATCH. Because the targets are the player's free
 * choice, this reuses the WP-242 / WP-492 `ko-hero` current-player interactive park in
 * full: forced steps auto-KO, a genuine choice parks ONE `PendingKoHeroChoice` resolved by
 * the existing `resolveKoHeroChoice` move. Deterministic — no `ctx.random`; the choice
 * defers to the player, not to RNG. Self-narrates via `pushLog` (keyword-less — no
 * `VillainEffectResult`).
 */
function villainEffectKoHeroesCurrentCountByTrait(G, currentPlayer, _cardId, _timing, descriptor) {
    const requireKind = descriptor.requireKind;
    const requireValue = descriptor.requireValue;
    // why: defensive — same guard as ko-heroes-current-by-trait (a hand-built test hook
    // could omit the predicate; the parser always sets both fields together).
    if (requireKind === undefined || requireValue === undefined) {
        return { targets: [] };
    }
    const zones = G.playerZones[currentPlayer];
    if (!zones) {
        return { targets: [] };
    }
    // why: D-24353 — the trait supplies the COUNT only; the KO targets are the player's
    // free choice below (contrast `ko-heroes-current-by-trait`, which KOs the matching
    // Heroes). The scan covers hand + in-play because "your Heroes" includes Heroes played
    // this turn — they sit in `inPlay`, since the Fight effect resolves after the play
    // phase (the operator ruling precedent from `villainEffectKoHeroesCurrentByTrait`).
    const countedHeroes = listPlayerHeroesMatchingTrait(G, zones, requireKind, requireValue);
    const owedFromTrait = countedHeroes.length;
    // why: D-24729 — named BEFORE the KO loop below moves any counted card to the KO pile.
    const countedNames = describeTraitCountedHeroes(G, countedHeroes, requireKind, requireValue);
    // why: D-24353 — this loop + park block is the `villainEffectKoHero` `target: 'current'`
    // body DUPLICATED verbatim with a trait-derived count in place of `descriptor.magnitude`.
    // Maestro is the SECOND count-source for the current-player KO park (magnitude is the
    // first), so duplicate-first / abstract-on-third (`.claude/rules/code-style.md`
    // §Abstraction) says duplicate: refactoring `villainEffectKoHero` into a shared helper
    // would disturb the byte-pinned WP-242 / WP-492 park-shape tests for no benefit. Extract
    // only when a THIRD count-source appears.
    const targets = [];
    // why: D-24693 — the zone each auto-KO took from, for the self-narrated line.
    const targetZones = [];
    let owed = owedFromTrait;
    while (owed > 0) {
        const eligible = buildKoEligibleTargets(zones);
        if (eligible.length === 0)
            break; // why: no heroes left to KO.
        // why: a genuine choice of WHICH heroes to KO exists only when the player has MORE
        // KO-able heroes than the count owed (they get to spare some) AND ≥ 2 distinct options
        // exist. Otherwise the KO is forced (every hero dies, or all copies are identical), so
        // auto-resolve it with no prompt.
        if (countKoableHeroes(zones) > owed && eligible.length >= 2)
            break;
        const koedId = koSingleTarget(G, zones, eligible[0], currentPlayer);
        if (koedId === null)
            break; // why: defensive — an unexpected move miss stops progress.
        targets.push(koedId);
        targetZones.push(eligible[0].zone);
        owed -= 1;
    }
    let parked = false;
    if (owed > 0 && countKoableHeroes(zones) > owed && buildKoEligibleTargets(zones).length >= 2) {
        if (!G.pendingKoHeroChoices)
            G.pendingKoHeroChoices = [];
        const entry = { choiceType: 'ko-hero', playerID: currentPlayer };
        // why: WP-492 / D-24298 — OMIT `remaining` when owed === 1 (absent ≡ 1), mirroring the
        // `ko-hero` magnitude path exactly so both count-sources park the identical shape.
        if (owed >= 2) {
            entry.remaining = owed;
        }
        G.pendingKoHeroChoices.push(entry);
        parked = true;
    }
    // why: D-24353 — keyword-less (no LEGACY_VILLAIN_KEYWORD_TO_DESCRIPTOR entry →
    // `descriptorToLegacyKeyword` returns undefined → no `VillainEffectResult` and no generic
    // Fight-effect line), so it MUST self-narrate. Zero matching Heroes is a reachable no-op
    // (`blocked`), never a hollow record. `G.messages` is hash-excluded (D-24081).
    if (parked) {
        pushLog(G, `Fight effect: KO ${String(owed)} of your Heroes (one per your ${requireValue} Hero${countedNames}) — choose which.`, 'neutral');
    }
    else if (targets.length > 0) {
        const names = targets.map((koedId, targetIndex) => nameKoTarget(G, koedId, targetZones[targetIndex])).join(', ');
        pushLog(G, `Fight effect: KO'd ${String(targets.length)} of your Heroes (${names}) — one per your ${requireValue} Hero${countedNames}.`, 'applied');
    }
    else {
        // why: D-24359 — this is the ONLY reachable blocked state: owedFromTrait === 0.
        // Every Hero the trait counts is itself a legal KO target (the eligibility scan
        // excludes only the wound token, which never carries a cardTraits entry), so an
        // owed count of 1 or more always resolves to an auto-KO or a park. Naming the
        // trait tells the player WHY nothing happened, per the D-24290 sibling's
        // `KO'd 0 of your {trait} Hero(es).` precedent — the bare "no Heroes to KO" read
        // as "you had nothing left to lose" when the truth was "you had no Strength
        // Heroes" (live log, match NPyIIWIjd1Q, 2026-08-15).
        pushLog(G, `Fight effect: no ${requireValue} Heroes — nothing to KO.`, 'blocked');
    }
    // why: WP-316 / D-24102 — pending: true marks a parked interactive KO (targets stays
    // whatever was auto-KO'd before the park — none, since a park is immediate).
    return parked ? { targets, pending: true } : { targets };
}
/**
 * rescue-bystanders-current-by-trait-count primitive — the current player rescues
 * one Bystander per Hero matching the trait predicate (hand + in-play), bounded by
 * the Bystander supply (Baron Zemo "Fight: For each of your [team:avengers] Heroes,
 * rescue a Bystander.", D-24290 / WP-485).
 *
 * Auto-resolved and deterministic. Self-narrates via `pushLog` (keyword-less — no
 * `VillainEffectResult`); returns `{ targets: [] }` (rescued Bystanders narrate by
 * count, not by name — the capture-bystander precedent).
 */
function villainEffectRescueBystandersCurrentByTraitCount(G, currentPlayer, cardId, _timing, descriptor) {
    const requireKind = descriptor.requireKind;
    const requireValue = descriptor.requireValue;
    // why: defensive — same guard as ko-heroes-current-by-trait (a hand-built test
    // hook could omit the predicate; the parser never does).
    if (requireKind === undefined || requireValue === undefined) {
        return { targets: [] };
    }
    const zones = G.playerZones[currentPlayer];
    if (!zones) {
        return { targets: [] };
    }
    const countedHeroes = listPlayerHeroesMatchingTrait(G, zones, requireKind, requireValue);
    const rescueCount = countedHeroes.length;
    // why: D-24290 — "rescue a Bystander" = award one from the supply to the current
    // player's Victory Pile, reusing the `capture-bystander` onFight player-award
    // mechanism (attach one to the triggering card, then award it). At the Fight fire
    // site the card's attached-bystander entry was already awarded + cleared (Step 3b,
    // fightVillain.ts) BEFORE this handler runs, so each iteration attaches exactly
    // one and awards exactly one. `attachBystanderToVillain` no-ops on an empty
    // supply, so the rescue is naturally bounded by the Bystander supply.
    let rescued = 0;
    for (let rescueIndex = 0; rescueIndex < rescueCount; rescueIndex++) {
        if (G.piles.bystanders.length === 0) {
            // why: the supply ran dry — stop (rescue is bounded by the Bystander supply).
            break;
        }
        const attachResult = attachBystanderToVillain(G.piles.bystanders, cardId, G.attachedBystanders);
        G.piles.bystanders = attachResult.bystandersPile;
        G.attachedBystanders = attachResult.attachedBystanders;
        const awardResult = awardAttachedBystanders(cardId, G.attachedBystanders, zones.victory);
        G.attachedBystanders = awardResult.attachedBystanders;
        zones.victory = awardResult.playerVictory;
        rescued += 1;
    }
    // why: D-24290 — Fight-timed self-narration (see draw-cards-current). Zero
    // matching Heroes, or an empty supply, is a reachable no-op: it logs `blocked`
    // with the reason (the Xavier's Nemesis / Electromagnetic Bubble wording), never a
    // "rescued 0 Bystander(s)" line.
    if (rescueCount === 0) {
        pushLog(G, `Fight effect: Player ${currentPlayer} has no ${requireValue} Hero in hand or play, so no Bystander is rescued.`, 'blocked');
        return { targets: [] };
    }
    if (rescued === 0) {
        pushLog(G, `Fight effect: Player ${currentPlayer} has ${requireValue} Heroes but the Bystander supply is empty, so no Bystander is rescued.`, 'blocked');
        return { targets: [] };
    }
    // why: D-24729 — name the counted Heroes and where each was counted from. When the supply
    // ran out first, say so, so a list longer than the rescue doesn't read as a miscount.
    const countedNames = describeTraitCountedHeroes(G, countedHeroes, requireKind, requireValue);
    let supplyClause = '';
    if (rescued < rescueCount) {
        supplyClause = `; the Bystander supply ran out after ${String(rescued)}`;
    }
    pushLog(G, `Fight effect: rescued ${String(rescued)} Bystander(s) (one per your ${requireValue} Hero${countedNames}${supplyClause}).`, 'applied');
    return { targets: [] };
}
/**
 * Counts the Heroes in the HQ whose trait matches the predicate (D-24334 / WP-521).
 *
 * The engine's first HQ-by-trait scan: it iterates the five `G.hq` slots, skips the
 * empty (`null`) ones, and counts those whose `G.cardTraits` entry matches via the
 * shared `cardTraitMatches`. Deliberately separate from the player-zone scanners
 * (`listPlayerHeroesMatchingTrait`), which read hand + in-play — this reads the HQ.
 *
 * @param G - Game state (reads G.hq, G.cardTraits and the split-face map).
 * @param kind - The predicate kind ('team' | 'hero-class').
 * @param value - The normalized trait slug to match.
 * @returns How many non-empty HQ slots hold a Hero matching the trait.
 */
function countHqHeroesByTrait(G, kind, value) {
    let count = 0;
    for (const slot of G.hq) {
        // why: an empty HQ slot holds no Hero; synthetic cards never sit in the HQ and
        // an `[team:…]` predicate never matches them, so no BASIC_SHIELD-style widening
        // (D-24296) is needed here.
        if (slot === null) {
            continue;
        }
        // why: WP-772 / D-24604 — the HQ is off play, so a split card there matches on either
        // half's class (rules v23 p.49), resolved through the split-face map.
        if (cardTraitMatches(offPlayCardTraits(G, slot), kind, value)) {
            count += 1;
        }
    }
    return count;
}
/**
 * capture-bystanders-plus-per-hq-hero-by-trait primitive — capture ONE Bystander onto
 * the triggering villain, then ONE more for each HQ Hero matching the trait predicate
 * (co2e Baron Zemo Ambush — "captures a Bystander. Then he captures another Bystander
 * for each [team:avengers] Hero in the HQ.", D-24334 / WP-521).
 *
 * Auto-resolved and deterministic. The base count is fixed at 1; the additional
 * captures scale with `countHqHeroesByTrait`. Bystanders are **attached only** (no
 * award) — this fires at Ambush, and Baron Zemo's attached Bystanders are awarded to
 * the defeating player on his defeat (`defeatCityVillainCore` Step 3b, D-18506); this
 * is the same attach-at-Ambush / award-on-defeat split the `capture-bystander` handler
 * uses (contrast the Fight-timed `rescue-bystanders-current-by-trait-count`, which
 * attaches AND awards each iteration). Supply-bounded on `G.piles.bystanders`.
 * Self-narrates via `pushLog` (keyword-less).
 */
function villainEffectCaptureBystandersPlusPerHqHeroByTrait(G, _currentPlayer, cardId, timing, descriptor) {
    const requireKind = descriptor.requireKind;
    const requireValue = descriptor.requireValue;
    // why: defensive — same guard as the other trait-predicate handlers (a hand-built
    // test hook could omit the predicate; the parser never does). No predicate → no
    // per-HQ-hero count, so capture nothing rather than guess.
    if (requireKind === undefined || requireValue === undefined) {
        return { targets: [] };
    }
    // why: D-24334 — base 1 ("captures a Bystander") + one per HQ Hero matching the
    // trait ("another Bystander for each [team:avengers] Hero in the HQ").
    const captureCount = 1 + countHqHeroesByTrait(G, requireKind, requireValue);
    let captured = 0;
    for (let captureIndex = 0; captureIndex < captureCount; captureIndex++) {
        // why: supply-bounded — stop once the shared Bystander pile is empty (a reachable
        // no-op tail, never a hollow record). attachBystanderToVillain also no-ops an
        // empty supply, but breaking keeps the narrated count honest.
        if (G.piles.bystanders.length === 0) {
            break;
        }
        const attachResult = attachBystanderToVillain(G.piles.bystanders, cardId, G.attachedBystanders);
        G.piles.bystanders = attachResult.bystandersPile;
        G.attachedBystanders = attachResult.attachedBystanders;
        // why: ATTACH-ONLY at Ambush — do NOT award now. Baron Zemo's attached Bystanders
        // are awarded to the defeating player at his defeat (D-18506); awarding here would
        // double-award (unlike the Fight-timed rescue-bystanders handler above).
        captured += 1;
    }
    // why: self-narrate the actual count (keyword-less → descriptorToLegacyKeyword
    // returns undefined, no VillainEffectResult, generic "<timing> effect:" line never
    // fires). `G.messages` is hash-excluded (D-24081). Zero captured (empty supply at
    // the start) is a reachable no-op → `blocked`; the base 1 makes `applied` the norm.
    const label = villainEffectTimingLabel(timing);
    pushLog(G, `${label} effect: captured ${String(captured)} Bystander(s) (1 + one per ${requireValue} Hero in the HQ).`, captured > 0 ? 'applied' : 'blocked');
    return { targets: [] };
}
/**
 * Returns the HQ slot index of the highest-cost Hero matching the trait predicate,
 * or null when no non-empty HQ slot matches (D-24335 / WP-522).
 *
 * Scans the five `G.hq` slots left-to-right, skipping empty (`null`) slots and slots
 * whose Hero does not match the `{ kind, value }` trait predicate (via the shared
 * `cardTraitMatches`). Among the matches, the highest `G.cardStats[id]?.cost ?? 0`
 * wins; ties resolve to the RIGHTMOST index — the same selection determinism as
 * `captureHeroFromHq`'s `highestCost` selector (WP-214).
 *
 * @param G - Game state (read-only here; only `G.hq`, `G.cardTraits`, `G.cardStats`).
 * @param kind - The predicate kind ('team' | 'hero-class').
 * @param value - The normalized trait slug to match.
 * @returns The selected HQ slot index, or null when no HQ Hero matches.
 */
function selectHqHeroIndexByTraitHighestCost(G, kind, value) {
    let selectedIndex = null;
    let highestCost = -1;
    for (let hqIndex = 0; hqIndex < G.hq.length; hqIndex++) {
        const slot = G.hq[hqIndex];
        if (slot === null || slot === undefined) {
            continue;
        }
        // why: WP-772 / D-24604 — the HQ is off play, so a split card there matches on either
        // half's class (rules v23 p.49), resolved through the split-face map.
        if (!cardTraitMatches(offPlayCardTraits(G, slot), kind, value)) {
            continue;
        }
        const heroCost = G.cardStats[slot]?.cost ?? 0;
        // why: D-24335 — highest cost wins; a tie (equal cost) resolves to the RIGHTMOST
        // index (hqIndex > selectedIndex), mirroring captureHeroFromHq's highestCost
        // selector so HQ selection determinism is identical across both call sites.
        if (heroCost > highestCost || (heroCost === highestCost && hqIndex > (selectedIndex ?? -1))) {
            highestCost = heroCost;
            selectedIndex = hqIndex;
        }
    }
    return selectedIndex;
}
/**
 * Selects the HQ slot index of the highest-cost Hero currently in the HQ, or null
 * when the HQ holds no Hero. Ties resolve to the RIGHTMOST index — the same
 * selection determinism as `captureHeroFromHq`'s `highestCost` selector (WP-214)
 * and `selectHqHeroIndexByTraitHighestCost`, but with NO trait filter: Paibok gives
 * "a Hero in the HQ" (any class/team, D-24343). Shared by the each-player handler's
 * non-current auto-gain and (via the resolve move) the bot default pick.
 *
 * @param G - Game state (read-only; only `G.hq` and `G.cardStats`).
 * @returns The selected HQ slot index, or null when no HQ Hero exists.
 */
export function selectHighestCostHqIndex(G) {
    let selectedIndex = null;
    let highestCost = -1;
    for (let hqIndex = 0; hqIndex < G.hq.length; hqIndex++) {
        const slot = G.hq[hqIndex];
        if (slot === null || slot === undefined) {
            continue;
        }
        const heroCost = G.cardStats[slot]?.cost ?? 0;
        // why: D-24343 — highest cost wins; a tie (equal cost) resolves to the RIGHTMOST
        // index, mirroring captureHeroFromHq's highestCost selector so HQ selection
        // determinism is identical across every HQ-selection call site.
        if (heroCost > highestCost || (heroCost === highestCost && hqIndex > (selectedIndex ?? -1))) {
            highestCost = heroCost;
            selectedIndex = hqIndex;
        }
    }
    return selectedIndex;
}
/**
 * Removes the HQ Hero at `hqIndex`, refills the vacated slot from `G.heroDeck`, and
 * pushes the removed Hero onto `playerId`'s discard pile — the shared "gain an HQ
 * Hero" mutation for `give-hq-hero-each-player` (WP-532 / D-24343).
 *
 * @param G - Game state (mutated: `G.hq`, `G.heroDeck`, the recipient's discard).
 * @param playerId - The recipient whose discard the Hero enters.
 * @param hqIndex - The HQ slot to take (expected to hold a non-null Hero).
 * @returns The gained Hero ext_id, or null when the slot was empty.
 */
function gainHqHeroToPlayerDiscard(G, playerId, hqIndex) {
    const heroId = G.hq[hqIndex];
    if (heroId === null || heroId === undefined) {
        return null;
    }
    // why: vacate the selected slot, then refill it from G.heroDeck — the refillHqSlot
    // contract (leaves the slot null when the reservoir is empty).
    G.hq[hqIndex] = null;
    const refillResult = refillHqSlot(G.hq, hqIndex, G.heroDeck);
    G.hq = refillResult.hq;
    G.heroDeck = refillResult.heroDeck;
    // why: D-24327 — "gain" routes to the recipient's DISCARD, never the victory pile.
    const zones = G.playerZones[playerId];
    if (zones) {
        zones.discard.push(heroId);
    }
    return heroId;
}
/**
 * give-hq-hero-each-player primitive — every player gains one Hero from the HQ into
 * their discard (Paibok the Power Skrull Fight — "Choose a Hero in the HQ for each
 * player. Each player gains that Hero.", D-24343 / WP-532).
 *
 * Non-current players resolve FIRST (sorted) — each auto-gains the highest-cost HQ
 * Hero + refill; THEN the current (fighting) player either parks an interactive choice
 * (≥ 2 HQ Heroes), auto-gains the sole remaining Hero (exactly 1), or no-ops (0).
 * Keyword-less → self-narrates via `pushLog`; returns `{ targets: [] }` (plus
 * `pending: true` when the current player parked).
 */
function villainEffectGiveHqHeroEachPlayer(G, currentPlayer, _cardId, timing, _descriptor) {
    const label = villainEffectTimingLabel(timing);
    // why: D-24343 / D-24284 — the operator-locked current-parks / others-auto split (the
    // human fighting player picks their own Hero; bot allies + non-current players get the
    // highest-cost Hero). This is a design choice, NOT an engine limitation — a
    // current-player-multi-pick rendering was available on the same queue but deliberately
    // not taken. Non-current players resolve FIRST, sorted (Object.keys().sort() = lexical
    // ascending, D-18902), each gaining the highest-cost HQ Hero + refill, so the current
    // player later picks from the HQ that remains (a single deterministic sequence).
    const gainedNames = [];
    for (const playerId of Object.keys(G.playerZones).sort()) {
        if (playerId === currentPlayer) {
            continue;
        }
        const hqIndex = selectHighestCostHqIndex(G);
        if (hqIndex === null) {
            // why: the HQ holds no Hero — a reachable no-op for this player (never a hollow).
            continue;
        }
        const gainedId = gainHqHeroToPlayerDiscard(G, playerId, hqIndex);
        if (gainedId !== null) {
            gainedNames.push(`Player ${playerId} gained ${resolveCardDisplayName(G, gainedId)}`);
        }
    }
    // why: count the non-null HQ Heroes that remain for the current player. ≥ 2 → a genuine
    // "which Hero" choice → park; exactly 1 → the gain is forced → auto-resolve; 0 → no-op.
    const currentHqIndices = [];
    for (let hqIndex = 0; hqIndex < G.hq.length; hqIndex++) {
        const slot = G.hq[hqIndex];
        if (slot !== null && slot !== undefined) {
            currentHqIndices.push(hqIndex);
        }
    }
    let parked = false;
    if (currentHqIndices.length >= 2) {
        // why: D-24343 / D-24284 — the current (human) player picks interactively; park one
        // give-hq-hero entry. A bot-driven current player is auto-resolved by ai.legalMoves
        // (highest-cost), so the queue never deadlocks. Lazily create the FIFO queue at the
        // park site (never in Game.setup) so an undefined field keeps the hash oracles stable.
        if (!G.pendingGiveHqHeroChoices) {
            G.pendingGiveHqHeroChoices = [];
        }
        const entry = { choiceType: 'give-hq-hero', playerID: currentPlayer };
        G.pendingGiveHqHeroChoices.push(entry);
        parked = true;
    }
    else if (currentHqIndices.length === 1) {
        // why: exactly one HQ Hero remains — the gain is forced, so auto-resolve it (no prompt).
        const gainedId = gainHqHeroToPlayerDiscard(G, currentPlayer, currentHqIndices[0]);
        if (gainedId !== null) {
            gainedNames.push(`Player ${currentPlayer} gained ${resolveCardDisplayName(G, gainedId)}`);
        }
    }
    // why: self-narrate (keyword-less). G.messages is hash-excluded (D-24081). Report the
    // auto-gains that landed, plus the interactive prompt when the current player parked;
    // an all-empty HQ (nobody gained, no park) is a blocked no-op.
    if (gainedNames.length > 0) {
        pushLog(G, `${label} effect: ${gainedNames.join('; ')} from the HQ.`, 'applied');
    }
    if (parked) {
        pushLog(G, `${label} effect: Player ${currentPlayer} — choose a Hero in the HQ to gain.`, 'neutral');
    }
    else if (gainedNames.length === 0) {
        pushLog(G, `${label} effect: no Hero in the HQ to gain; no effect.`, 'blocked');
    }
    // why: WP-316 — targets stays [] (gains narrate by recipient, not a card-name surface);
    // pending: true marks the parked interactive choice for the current player.
    return parked ? { targets: [], pending: true } : { targets: [] };
}
/**
 * give-hq-hero-by-trait-to-current primitive — remove the highest-cost HQ Hero matching
 * the trait predicate, refill the vacated HQ slot from `G.heroDeck`, and give the Hero to
 * the current (fighting) player's discard (co2e Ultron Fight — "Choose a [hc:tech] Hero
 * from the HQ. Either KO that Hero or choose a player to gain it.", D-24335 / WP-522).
 *
 * The printed KO-or-gift double-choice auto-resolves to the gift, to the current player.
 * Self-narrates via `pushLog` (keyword-less → descriptorToLegacyKeyword returns undefined,
 * so no VillainEffectResult is recorded and the generic "<timing> effect:" line never
 * fires); the log line names the Hero + recipient. Returns `{ targets: [] }`.
 */
function villainEffectGiveHqHeroByTraitToCurrent(G, currentPlayer, _cardId, timing, descriptor) {
    const label = villainEffectTimingLabel(timing);
    const requireKind = descriptor.requireKind;
    const requireValue = descriptor.requireValue;
    // why: defensive — same guard as the other trait-predicate handlers (a hand-built test
    // hook could omit the predicate; the parser never does). No predicate → match no HQ Hero
    // rather than guess.
    if (requireKind === undefined || requireValue === undefined) {
        return { targets: [] };
    }
    // why: D-24335 — the printed KO-or-gift collapse. In co-op a gift gives a player a free
    // Hero AND refills the HQ, strictly dominating a KO (which only refills), so a rational
    // cooperative chooser always gifts and never KOs — the KO branch is not implemented (the
    // WP-519 Melter / WP-516 Ymir dominated-option collapse).
    const selectedIndex = selectHqHeroIndexByTraitHighestCost(G, requireKind, requireValue);
    if (selectedIndex === null) {
        // why: no HQ Hero matches the predicate — a reachable no-op (never a hollow record).
        pushLog(G, `${label} effect: no ${requireValue} Hero in the HQ; no effect.`, 'blocked');
        return { targets: [] };
    }
    const heroId = G.hq[selectedIndex];
    // why: vacate the selected slot, then refill it from G.heroDeck — the captureHeroFromHq
    // refill contract (leaves the slot null when the reservoir is empty).
    G.hq[selectedIndex] = null;
    const refillResult = refillHqSlot(G.hq, selectedIndex, G.heroDeck);
    G.hq = refillResult.hq;
    G.heroDeck = refillResult.heroDeck;
    // why: D-24327 — "gain" routes to the recipient's DISCARD (never the victory pile);
    // D-24335 — the recipient is the current (fighting) player (the collapsed "choose a
    // player"). No other player's zones are touched.
    const zones = G.playerZones[currentPlayer];
    if (zones) {
        zones.discard.push(heroId);
    }
    // why: self-narrate (keyword-less). `G.messages` is hash-excluded (D-24081). Naming the
    // Hero + recipient is the user-visible surface; outcome `applied` (a Hero was gifted).
    pushLog(G, `${label} effect: gave ${resolveCardDisplayName(G, heroId)} from the HQ to Player ${currentPlayer}'s discard.`, 'applied');
    return { targets: [] };
}
/**
 * Collects the City indices whose occupant is a Villain — a `villain` or a `henchman`
 * (not empty) — in ascending index order (D-24336 / WP-523; henchmen per D-24607).
 *
 * "Two Villains in the city" counts `villain`- and `henchman`-classified occupants
 * (rules v23 §"Henchmen Are Villains/Adversaries"). An empty slot (`null`) or an
 * unclassified occupant is skipped. Feeds the frontmost-rearmost swap rule below.
 *
 * @param G - Game state (read-only here; only `G.city` + `G.villainDeckCardTypes`).
 * @returns The villain-occupied City indices in ascending order.
 */
function collectCityVillainIndices(G) {
    const indices = [];
    const cardTypes = G.villainDeckCardTypes;
    for (let cityIndex = 0; cityIndex < G.city.length; cityIndex++) {
        const occupant = G.city[cityIndex];
        if (occupant === null || occupant === undefined) {
            continue;
        }
        // why: D-24607 (supersedes D-24336's henchman exclusion) — rules v23 "Henchman Villain
        // cards are indeed Villains", so both types are swap candidates. Classify by the
        // villain-deck card-type map; an absent classification does NOT count (production always
        // builds the map; a narrow test mock that omits it simply holds no villains here).
        const occupantType = cardTypes?.[occupant];
        if (occupantType === 'villain' || occupantType === 'henchman') {
            indices.push(cityIndex);
        }
    }
    return indices;
}
/**
 * swap-two-city-villains primitive — swap the City spaces of two Villains (co2e Whirlwind
 * Ambush "Two Villains in the city swap spaces.", D-24336 / WP-523). The engine's FIRST City
 * board-position manipulation.
 *
 * Rule B (locked, operator-confirmed): with at least two villain-occupied City spaces, swap
 * the LOWEST-index (entrance side) with the HIGHEST-index (escape side) — the largest
 * positional displacement, the "disrupt the board" reading. The card names no chooser, so
 * the engine picks deterministically (no `ctx.random`). Henchmen are Villains and swap too
 * (D-24607, superseding D-24336's exclusion); Whirlwind
 * itself is eligible (it is pushed into the City before its Ambush fires). Fewer than two
 * villain-occupied spaces is a reachable no-op. `cityIndex` is unused — the Ambush fire site
 * passes undefined and the swap is space-relative, reading `G.city` directly (not the WP-489
 * location gate).
 *
 * Self-narrates via `pushLog` (keyword-less → no reverse-map, no VillainEffectResult, the
 * generic "<timing> effect:" line never fires). The swap exchanges two `CardExtId` strings
 * between `G.city` indices — no card object enters the City. Returns `{ targets: [] }`.
 */
function villainEffectSwapTwoCityVillains(G, _currentPlayer, _cardId, timing, _descriptor) {
    const label = villainEffectTimingLabel(timing);
    const villainIndices = collectCityVillainIndices(G);
    if (villainIndices.length < 2) {
        // why: D-24336 — "Two Villains" needs at least two villain-occupied City spaces; a
        // sparser board is a reachable no-op (never a hollow record).
        pushLog(G, `${label} effect: fewer than two Villains in the City; no swap.`, 'blocked');
        return { targets: [] };
    }
    // why: D-24336 — Rule B: swap the lowest-index (entrance side) villain-occupied space with
    // the highest-index (escape side) one — the maximum positional displacement. Direct
    // index assignment mirrors the defeat null-out write (fightVillain.ts); the City stores
    // CardExtId strings, so this exchanges two strings, never card objects.
    const lowIndex = villainIndices[0];
    const highIndex = villainIndices[villainIndices.length - 1];
    const lowOccupant = G.city[lowIndex];
    const highOccupant = G.city[highIndex];
    G.city[lowIndex] = highOccupant;
    G.city[highIndex] = lowOccupant;
    // why: self-narrate (keyword-less). `G.messages` is hash-excluded (D-24081). Name the two
    // Villains + their spaces; outcome `applied` (a swap happened).
    const lowSpace = citySpaceNameForIndex(lowIndex) ?? String(lowIndex);
    const highSpace = citySpaceNameForIndex(highIndex) ?? String(highIndex);
    pushLog(G, `${label} effect: ${resolveCardDisplayName(G, lowOccupant)} (${lowSpace}) and ${resolveCardDisplayName(G, highOccupant)} (${highSpace}) swapped City spaces.`, 'applied');
    return { targets: [] };
}
/**
 * haunt-hq-hero primitive — the Haunting Villain leaves the City and haunts an HQ Hero
 * chosen by the descriptor's selector (WP-757 / D-24587, rulebook v23 p.27).
 *
 * Only occupied, UNHAUNTED slots are eligible ("a Hero can't be Haunted by two
 * Villains at once"). With no eligible slot, or when the Villain is no longer in the
 * City, it is a logged no-op and the Villain stays where it is. Otherwise the Villain's
 * City space is nulled and it is recorded as the slot's haunter, so it can't be fought
 * until a player exorcises the Hero. Attached bystanders / captured heroes stay keyed
 * to the Villain's card id. Keyword-less: self-narrates with exactly one log line, and
 * no composed "Ambush effect:" line is written for it.
 *
 * @param G - Game state (mutated: `G.city`, `G.hqHaunters`).
 * @param _currentPlayer - Unused (Haunt targets the shared HQ).
 * @param cardId - The haunting Villain's copy-indexed instance ext_id.
 * @param timing - The firing timing (labels the log line).
 * @param descriptor - Carries the `selector`.
 * @returns The haunted Hero as the target, or no targets on a no-op.
 */
function villainEffectHauntHqHero(G, _currentPlayer, cardId, timing, descriptor) {
    const label = villainEffectTimingLabel(timing);
    const villainName = resolveCardDisplayName(G, cardId);
    const selector = descriptor.selector;
    if (selector !== 'rightmost' && selector !== 'leftmost' && selector !== 'cost-lte-3') {
        pushLog(G, `${label} effect: ${villainName} has no valid Haunt selector; nothing happens.`, 'blocked');
        return { targets: [] };
    }
    const cityIndex = G.city.indexOf(cardId);
    if (cityIndex < 0) {
        // why: D-24587 — Haunt moves the Villain from the City; a Villain no longer in the
        // City has nothing to tuck beneath a Hero (a reachable no-op, never a hollow record).
        pushLog(G, `${label} effect: ${villainName} is not in the City; no Hero is haunted.`, 'blocked');
        return { targets: [] };
    }
    const hqIndex = selectUnhauntedHqIndex(G, selector);
    if (hqIndex === null) {
        // why: D-24587 — no occupied, unhaunted HQ slot matches: the Haunt fizzles and the
        // Villain stays in the City, fightable as normal.
        pushLog(G, `${label} effect: no unhaunted Hero in the HQ to haunt; ${villainName} stays in the City.`, 'blocked');
        return { targets: [] };
    }
    const heroId = G.hq[hqIndex];
    if (!hauntHqSlot(G, hqIndex, { kind: 'villain', cardId })) {
        pushLog(G, `${label} effect: ${villainName} could not haunt HQ slot ${String(hqIndex)}; it stays in the City.`, 'blocked');
        return { targets: [] };
    }
    // why: D-24587 — the haunter is tucked beneath the Hero, OUT of the City. Nulling the
    // space is what makes it unfightable (fightVillain only fights G.city) and keeps the
    // card-uniqueness invariant (it now lives only in G.hqHaunters).
    G.city[cityIndex] = null;
    pushLog(G, `${label} effect: ${villainName} haunts ${resolveCardDisplayName(G, heroId)} (HQ slot ${String(hqIndex)}); that Hero can't be recruited until exorcised.`, 'threat');
    return { targets: [heroId] };
}
/**
 * Whether a player's Victory Pile holds a villain of the target group OTHER than
 * the just-defeated/escaped card (WP-494 / D-24299).
 *
 * Matches on the FULL anchored ext_id prefix `${setAbbr}-villain-${group}-` — a bare
 * `.includes('-villain-')` would false-match villain-deck bystanders
 * (`bystander-villain-deck-NN`, which carry the substring but start `bystander-`) and
 * would not scope to the group. The fought card is excluded ("*another*").
 *
 * @param victory - The player's Victory Pile ext_ids.
 * @param groupPrefix - The anchored prefix `${setAbbr}-villain-${group}-`.
 * @param foughtCardId - The Viper being resolved (excluded from the scan).
 * @returns True when the pile holds another group villain.
 */
function victoryPileHasOtherGroupVillain(victory, groupPrefix, foughtCardId) {
    for (const victoryId of victory) {
        if (victoryId !== foughtCardId && victoryId.startsWith(groupPrefix)) {
            return true;
        }
    }
    return false;
}
/**
 * gain-wound-unless-victory-villain-group primitive — each player gains a Wound
 * UNLESS their Victory Pile holds another villain of the descriptor's group (Viper
 * "Fight/Escape: Each player without another HYDRA Villain in their Victory Pile
 * gains a Wound.", D-24299 / WP-494).
 *
 * Auto-resolved (no player choice) and deterministic — the reveal-or-wound skeleton
 * with the predicate swapped from a hand/play hero-trait to a Victory-Pile
 * villain-group membership test. Self-narrates via `pushLog` (keyword-less —
 * `descriptorToLegacyKeyword` returns undefined, so no `VillainEffectResult` is
 * recorded and the generic `<timing> effect:` line never fires). Returns
 * `{ targets: [] }`.
 */
function villainEffectGainWoundUnlessVictoryVillainGroup(G, currentPlayer, cardId, timing, descriptor) {
    const group = descriptor.victoryVillainGroup;
    // why: defensive — a well-formed descriptor always carries the group (the parser
    // sets it, D-24299). A malformed hook lacking it cannot evaluate the predicate, so
    // no player is wounded (never wounds everyone on an empty predicate). Reachable
    // only via a hand-built test hook, never from the parser.
    if (group === undefined) {
        return { targets: [] };
    }
    // why: D-24299 Path B — derive the villain group ext_id prefix from the fought
    // card's OWN ext_id rather than a villain-group `G` map (a new hashed setup field
    // would re-pin every committed fixture). The `-villain-` infix is unambiguous
    // (`setAbbr` has no hyphens); the match anchors on the FULL prefix
    // `${setAbbr}-villain-${group}-` (a bare `.includes('-villain-')` would false-match
    // villain-deck bystanders `bystander-villain-deck-NN`). A `cardId` lacking the infix
    // (a non-villain fire site) → no wound.
    const villainInfix = '-villain-';
    const infixIndex = cardId.indexOf(villainInfix);
    if (infixIndex < 0) {
        return { targets: [] };
    }
    const setAbbr = cardId.slice(0, infixIndex);
    const groupPrefix = `${setAbbr}${villainInfix}${group}-`;
    // why: rules v23 L2247 — start with the current player, then clockwise (D-24780).
    const woundedPlayerIds = [];
    const gainedPlayerIds = [];
    const divingBlockPendingPlayerIds = [];
    const emptySupplyPlayerIds = [];
    for (const playerId of seatsInTurnOrderFrom(Object.keys(G.playerZones), currentPlayer)) {
        const zones = G.playerZones[playerId];
        if (!zones) {
            continue;
        }
        if (victoryPileHasOtherGroupVillain(zones.victory, groupPrefix, cardId)) {
            // why: the player HAS another group villain in their Victory Pile — no Wound.
            continue;
        }
        // why: no other group villain — gain one Wound. Empty pile is a reachable no-op
        // (mirrors gain-wound:each): the player is NOT counted as wounded, and is
        // tracked so the narration says the supply ran out (D-24686).
        if (G.piles.wounds.length === 0) {
            emptySupplyPlayerIds.push(playerId);
            continue;
        }
        // why: WP-682 / D-24499 — gainWoundForPlayer chokepoint so Diving Block sees this
        // group-villain Wound source (NON-active recipients via the WP-684 wave).
        if (gainWoundWithOutcome(G, playerId) === 'diving-block-pending') {
            divingBlockPendingPlayerIds.push(playerId);
        }
        else {
            gainedPlayerIds.push(playerId);
        }
        if (playerId === currentPlayer) {
            // why: woundsDrawn projects the CURRENT player's wounds only (UI economy) —
            // parity with reveal-or-wound / gain-wound:each; a non-current wounded player
            // must not move it.
            G.turnEconomy.woundsDrawn += 1;
        }
        woundedPlayerIds.push(playerId);
    }
    // why: self-narrate ONE line (keyword-less — the result-recording path drops it).
    // `G.messages` is hash-excluded (D-24081). Honest colour per the WP-434 contract:
    // a wound landed → `applied`; every player was safe → `blocked`.
    const label = villainEffectTimingLabel(timing);
    const emptySupplyClause = composeEmptyWoundSupplyClause(emptySupplyPlayerIds);
    if (divingBlockPendingPlayerIds.length > 0) {
        // why: D-24673 — a Diving Block reveal is pending, so that Wound is not gained yet.
        pushLog(G, `${label} effect: ${String(woundedPlayerIds.length)} player(s) had no other ${group} Villain in their Victory Pile: ${composeWoundOutcomeClause(gainedPlayerIds, divingBlockPendingPlayerIds)}${emptySupplyClause}.`, 'applied');
    }
    else if (woundedPlayerIds.length > 0) {
        const names = woundedPlayerIds.map((playerId) => `Player ${playerId}`).join(', ');
        pushLog(G, `${label} effect: ${String(woundedPlayerIds.length)} player(s) had no other ${group} Villain in their Victory Pile and gained a Wound (${names})${emptySupplyClause}.`, 'applied');
    }
    else if (emptySupplyPlayerIds.length > 0) {
        // why: D-24686 — the supply is empty; do not claim every player was safe.
        const names = emptySupplyPlayerIds.map((playerId) => `Player ${playerId}`).join(', ');
        pushLog(G, `${label} effect: the Wound supply is empty, so no Wound was gained (${names} had no other ${group} Villain in their Victory Pile).`, 'blocked');
    }
    else {
        pushLog(G, `${label} effect: every player had another ${group} Villain in their Victory Pile.`, 'blocked');
    }
    return { targets: [] };
}
// why: D-24023 — the ImplementationMap keyed by primitive (mirrors WP-251's
// HERO_EFFECT_HANDLERS). Full Record over the 15 primitives; the drift test
// asserts the key set equals VILLAIN_EFFECT_PRIMITIVES. Replaces the former
// 10-arm switch on VillainEffectKeyword. `scry-ko-own-deck` appended by WP-447
// (D-24267); `gain-attached-hero` (no-op) appended by WP-450 (D-24270);
// `reveal-or-wound` appended by WP-469 (D-24281); `become-scheme-twist` (no-op —
// the twist fires from the escape fire site) appended by WP-481 (D-24287);
// `draw-cards-current`, `ko-heroes-current-by-trait`, and
// `rescue-bystanders-current-by-trait-count` (auto-resolve) appended by WP-485
// (D-24290); `gain-wound-unless-victory-villain-group` (auto-resolve, conditional
// each-player wound on a Victory-Pile group predicate) appended by WP-494 (D-24299).
// `override-next-hand-size` (auto-resolve — writes the WP-497 `handSizeOverrides`
// field) appended by WP-503 (D-24307 — the core spider-foes Doctor Octopus villain
// Fight: draw 8 next hand instead of 6). `ko-cullable-each-deck-top` (auto-resolve —
// each player reveals their deck top, KO the cullable ones, keep real Heroes) appended
// by WP-519 (D-24332 — Melter, Masters of Evil Fight). `capture-bystanders-plus-per-hq-hero-by-trait`
// (auto-resolve — attach 1 Bystander + 1 per HQ Hero matching a trait; attach-only at
// Ambush, award on defeat) appended by WP-521 (D-24334 — co2e Baron Zemo Ambush).
// `ko-wounds-current-hand-and-discard`
// (auto-resolve — KO the current player's Wounds from hand + discard) appended by
// WP-516 (D-24329 — Ymir, Frost Giant King Fight). `give-hq-hero-by-trait-to-current`
// (auto-resolve — remove the highest-cost HQ Hero matching a trait and give it to the
// current player's discard, refill the slot) appended by WP-522 (D-24335 — co2e Ultron Fight).
// why: `swap-two-city-villains` (auto-resolve — swap the lowest- and highest-index
// villain-occupied City spaces, henchmen included per D-24607, fewer than two is a no-op)
// appended by WP-523 (D-24336 — co2e Whirlwind Ambush; the first City board-position manipulation).
// why: `gain-recruit-current` (auto-resolve — the current player gains N recruit) and
// `gain-officer-current` (auto-resolve — the current player gains one S.H.I.E.L.D. Officer
// from the supply, empty pile → logged no-op) appended by WP-541 (D-24350 — Hand Ninjas +
// HYDRA Kidnappers, the first slice of the Core villain/henchman Fight-reward batch).
// why: `add-next-hand-size` (auto-resolve — ADDITIVELY raises the current player's next-hand
// fill target, accumulating across defeats) appended by WP-543 (D-24352 — Savage Land Mutates
// "draw an extra card"; the additive sibling of the absolute override-next-hand-size).
// why: `play-villain-deck-cards` (DELIBERATE NO-OP — the real reveal fires from the onAmbush /
// onFight fire sites, the WP-481 secondary-fire-site pattern) appended by WP-542 (D-24351 —
// Endless Armies of HYDRA Fight + The Leader Ambush "Play the top N cards of the Villain Deck").
// why: `ko-heroes-current-count-by-trait` (INTERACTIVE — trait-sized count, free-choice KO
// targets; reuses the `ko-hero` current-player park + `resolveKoHeroChoice`) appended by
// WP-544 (D-24353 — core radiation Maestro "For each of your [hc:strength] Heroes, KO one of
// your Heroes"). Distinct from `ko-heroes-current-by-trait`, which KOs the MATCHING Heroes.
/** Villain effect handlers keyed by primitive. Single dispatch source. */
const VILLAIN_EFFECT_HANDLERS = {
    'ko-hero': villainEffectKoHero,
    'gain-wound': villainEffectGainWound,
    'capture-hq-hero': villainEffectCaptureHqHero,
    'hero-deck-top-to-escape': villainEffectHeroDeckTopToEscape,
    'capture-bystander': villainEffectCaptureBystander,
    'scry-ko-own-deck': villainEffectScryKoOwnDeck,
    'gain-attached-hero': villainEffectGainAttachedHero,
    'reveal-or-wound': villainEffectRevealOrWound,
    'become-scheme-twist': villainEffectBecomeSchemeTwist,
    'draw-cards-current': villainEffectDrawCardsCurrent,
    'ko-heroes-current-by-trait': villainEffectKoHeroesCurrentByTrait,
    'rescue-bystanders-current-by-trait-count': villainEffectRescueBystandersCurrentByTraitCount,
    'gain-wound-unless-victory-villain-group': villainEffectGainWoundUnlessVictoryVillainGroup,
    'override-next-hand-size': villainEffectOverrideNextHandSize,
    'ko-wounds-current-hand-and-discard': villainEffectKoWoundsCurrentHandAndDiscard,
    'ko-cullable-each-deck-top': villainEffectKoCullableEachDeckTop,
    'capture-bystanders-plus-per-hq-hero-by-trait': villainEffectCaptureBystandersPlusPerHqHeroByTrait,
    'give-hq-hero-by-trait-to-current': villainEffectGiveHqHeroByTraitToCurrent,
    'swap-two-city-villains': villainEffectSwapTwoCityVillains,
    'give-hq-hero-each-player': villainEffectGiveHqHeroEachPlayer,
    'gain-recruit-current': villainEffectGainRecruitCurrent,
    'gain-officer-current': villainEffectGainOfficerCurrent,
    'add-next-hand-size': villainEffectAddNextHandSize,
    'play-villain-deck-cards': villainEffectPlayVillainDeckCards,
    'ko-heroes-current-count-by-trait': villainEffectKoHeroesCurrentCountByTrait,
    // why: WP-757 / D-24587 — the Haunt keyword (The Fallen's Ambush); keyword-less,
    // self-narrating. Moves the Villain out of the City into G.hqHaunters.
    'haunt-hq-hero': villainEffectHauntHqHero,
    // why: WP-760 / D-24589 — The Fallen's Patriarch (reveal-top-draw-if-cost-lte:3) and
    // Salomé (ko-up-to-from-discard-current:2) Fights; both keyword-less, self-narrating.
    'reveal-top-draw-if-cost-lte': villainEffectRevealTopDrawIfCostLte,
    'ko-up-to-from-discard-current': villainEffectKoUpToFromDiscardCurrent,
    // why: WP-816 / D-24749 — Executioner's Escape; keyword-less, self-narrating.
    'ko-bystander-each-victory-pile': villainEffectKoBystanderEachVictoryPile,
    // why: WP-817 / D-24750 — Captain Zolandia's Fight and Primus's Escape; both
    // keyword-less, self-narrating. Primus's Escape only records the owed discard.
    'gain-recruit-current-per-hero-class': villainEffectGainRecruitCurrentPerHeroClass,
    'discard-hero-each-player-by-cost': villainEffectDiscardHeroEachPlayerByCost,
    // why: WP-836 / D-24769 — co2e Abomination, Beetle and Savage Land Mutates Fights; all
    // keyword-less, self-narrating.
    'gain-recruit-current-count-by-trait': villainEffectGainRecruitCurrentCountByTrait,
    'draw-cards-current-count-by-trait': villainEffectDrawCardsCurrentCountByTrait,
    'reveal-top-ko-grey-or-draw-by-class': villainEffectRevealTopKoGreyOrDrawByClass,
    // why: WP-837 / D-24770 — co2e Mystique, Frost Giant Warrior and Juggernaut Ambushes;
    // all keyword-less, self-narrating. The two trait discards only record the owed discard.
    'discard-hero-each-player-by-trait': villainEffectDiscardHeroEachPlayerByTrait,
    'discard-hero-or-wound-each-player-by-trait': villainEffectDiscardHeroOrWoundEachPlayerByTrait,
    'discard-hand-matching-hq-card-names-each-player': villainEffectDiscardHandMatchingHqCardNamesEachPlayer,
    'hq-heroes-to-hero-deck-bottom': villainEffectHqHeroesToHeroDeckBottom,
};
/**
 * Applies one villain effect descriptor deterministically by dispatching to its
 * primitive handler.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param currentPlayer - The active player id.
 * @param cardId - The triggering villain/henchman card-instance ext_id.
 * @param timing - The timing that fired (changes capture-bystander behavior).
 * @param descriptor - The parameterized effect descriptor to apply.
 * @returns The handler's `VillainEffectApplication` (its targets + optional
 *   pending flag) when an in-vocab primitive handler ran (regardless of whether
 *   mutation guards short-circuited inside it); `null` only when no handler
 *   exists for the primitive (the former out-of-vocab default). WP-200 D-20003
 *   carries forward: drives the reverse-mapped results array — only descriptors
 *   whose handler ran are recorded (post-safe-skip contract).
 */
function applyVillainEffect(G, currentPlayer, cardId, timing, descriptor, shuffleContext, cityIndex) {
    const handler = VILLAIN_EFFECT_HANDLERS[descriptor.primitive];
    if (handler === undefined) {
        // why: out-of-vocabulary primitives safe-skip silently — moves never throw,
        // no console output, no message push (matches the WP-022 hero-effects
        // precedent). Reachable only via a malformed hook; the parser validates
        // markers before building descriptors. Returning null excludes the
        // descriptor from the executor's results[] (post-safe-skip contract).
        return null;
    }
    // why: WP-478 / D-24285 — forward the shuffle source so a deck-reveal handler
    // (scry) can reshuffle the discard on exhaustion; the other handlers ignore it.
    // WP-489 / D-24295 — forward the fought City index for signature uniformity (the
    // location gate already ran in the executor loop; no handler reads it today).
    return handler(G, currentPlayer, cardId, timing, descriptor, shuffleContext, cityIndex);
}
// ---------------------------------------------------------------------------
// Shared per-player KO resolver
// ---------------------------------------------------------------------------
/**
 * KOs one of the given player's Heroes: from in play first, then from the hand.
 * Never takes a card from the discard pile (D-24687).
 *
 * Shared per-player resolver called by BOTH the `koHeroCurrentPlayer` and
 * `koHeroEachPlayer` effect cases — the latter iterates every player in
 * `Object.keys(G.playerZones).sort()` order and calls this helper once per
 * player. There is no duplicated KO logic anywhere else in the executor
 * (D-18902); the per-player KO semantics live here and nowhere else.
 *
 * Originally introduced as `koHeroForCurrentPlayer` by WP-185 with a
 * misleading name (the parameter is any player id, not specifically the
 * current player); WP-189 renamed it to make the shared-helper intent
 * obvious and added the `koHeroEachPlayer` second call site.
 *
 * The resolver performs the `koCard` mutation itself — callers MUST NOT
 * post-process or modify its output. Both branches reach byte-identical
 * post-state on a single-player G (pinned by the shared-resolver parity
 * test).
 *
 * @param G - Game state (mutated under Immer draft).
 * @param playerId - The target player id (any player; for the
 *   `koHeroCurrentPlayer` case this is `ctx.currentPlayer`, for the
 *   `koHeroEachPlayer` case it is each entry of the sorted player-ids
 *   iteration).
 * @returns The KO'd card and the zone it left, or null when nothing was KO'd.
 */
// why: deterministic auto-resolution over "your Heroes" — rules v23 L721–L725
// (D-24687, which supersedes the zone priority of D-18503 / D-20603). inPlay
// goes first because a Hero played this turn has already produced its Recruit
// and Attack (L723–L725), so KO'ing it costs nothing more this turn; for any
// other player inPlay is empty and the pick comes from the hand. The discard
// pile is never read. Within each zone the D-20602 rule applies via
// selectKoHeroTarget: starting SHIELD cards (`starting-shield-trooper`,
// `starting-shield-agent`) ahead of everything else, with ext_id lexical
// ascending as the tie-break. Starting-first preserves replay determinism and
// remains NOT VP-based — the starter set is a closed enum, no registry read.
// This function owns the `koCard` mutation site for the per-player KO —
// callers delegate to it and do not post-process its output (D-18902
// mutation-location lock; single source of truth for KO targeting + replay
// determinism).
// why: D-24693 — returns the zone with the card so the log can say where it came from.
function koOneHeroForPlayer(G, playerId) {
    const zones = G.playerZones[playerId];
    if (!zones)
        return null;
    // why: inPlay first (D-24687; the D-20603 turn-1 case still holds — the
    // autoplay flow plays every hand card before the spend phase that fights, so
    // on turn 1 the bot's Heroes all sit in inPlay). A played Hero has already
    // produced its Recruit and Attack (rules v23 L723–L725). Starter-first within
    // the zone (D-20602, via selectKoHeroTarget).
    const inPlayTarget = selectKoHeroTarget(zones.inPlay);
    if (inPlayTarget !== null) {
        const moveResult = moveCardFromZone(zones.inPlay, [], inPlayTarget);
        if (moveResult.found) {
            zones.inPlay = moveResult.from;
            G.ko = koCard(G.ko, inPlayTarget);
            // why: WP-853 / D-24789 — playerId is the seat losing the Hero (each-player loops).
            recordOwnedCardKoed(G, playerId, inPlayTarget, 'inPlay');
            // why: WP-316 — return the KO'd ext_id (and its zone, D-24693) for the log target.
            return { zone: 'inPlay', cardId: inPlayTarget };
        }
        // why: inPlay wins over hand once an inPlay hero is chosen.
        return null;
    }
    // why: the hand is the other half of "your Heroes" (D-24687) and the only one
    // a non-current player has, since their inPlay is empty off-turn.
    const handTarget = selectKoHeroTarget(zones.hand);
    if (handTarget !== null) {
        const moveResult = moveCardFromZone(zones.hand, [], handTarget);
        if (moveResult.found) {
            zones.hand = moveResult.from;
            G.ko = koCard(G.ko, handTarget);
            recordOwnedCardKoed(G, playerId, handTarget, 'hand');
            // why: WP-316 — return the KO'd ext_id (and its zone, D-24693) for the log target.
            return { zone: 'hand', cardId: handTarget };
        }
    }
    // why: WP-316 — no eligible hero in hand or play (or the move missed); no KO,
    // no target. The discard pile is never read: a Hero there is not one of "your
    // Heroes" (D-24687 supersedes the D-18503 / D-20603 zone priority).
    return null;
}
/**
 * KOs up to `magnitude` heroes from ONE named source zone of a single player,
 * with NO cross-zone fallback (D-24280). Returns the KO'd ext_ids in KO order.
 *
 * The source-zone-restricted sibling of `koOneHeroForPlayer`: used for the
 * each-player `ko-hero` effect when the printed text names a single zone
 * (Juggernaut's "from their discard pile" / "from their hand"). Reuses
 * `selectKoHeroTarget` each iteration (Wounds excluded, starter S.H.I.E.L.D.
 * first, then ext_id lex-asc) and re-selects over the shortened zone, so
 * `magnitude` KOs the `magnitude` worst heroes in that zone.
 *
 * @param G - Game state (mutated under Immer draft).
 * @param playerId - The target player id (any player; the each-player caller
 *   iterates `Object.keys(G.playerZones).sort()`).
 * @param zone - The single source zone to KO from ('discard' or 'hand').
 * @param magnitude - The maximum number of heroes to KO from that zone.
 * @returns The KO'd card ext_ids in KO order (WP-316 log targets).
 */
// why: D-24280 — unlike koOneHeroForPlayer (hand + inPlay, D-24687), this KOs ONLY
// from `zones[zone]` because the printed effect is source-restricted — it cannot
// reach the player's other zones, so a zone with fewer than `magnitude` heroes
// yields fewer KOs and an empty zone yields none, a reachable no-op (never
// hollow). Owns its own koCard mutation site so callers do not post-process
// (D-18902 mutation-location lock). No `ctx.random`, no `.reduce()`.
function koHeroesFromZoneForPlayer(G, playerId, zone, magnitude, greyFilter) {
    const zones = G.playerZones[playerId];
    if (!zones)
        return [];
    const koedIds = [];
    for (let iteration = 0; iteration < magnitude; iteration++) {
        // why: D-24769 — a filtered KO (Destroyer, 'non-grey') picks only among the cards
        // that pass the filter; absent, today's starter-first pick is unchanged.
        const target = greyFilter === undefined
            ? selectKoHeroTarget(zones[zone])
            : selectKoHeroTargetByGreyFilter(G, zones[zone], greyFilter);
        if (target === null) {
            // why: the zone holds no more heroes — stop (no fallback to another zone).
            break;
        }
        const moveResult = moveCardFromZone(zones[zone], [], target);
        if (!moveResult.found) {
            break;
        }
        zones[zone] = moveResult.from;
        G.ko = koCard(G.ko, target);
        // why: WP-853 / D-24789 — playerId is the seat losing the card (each-player loops).
        recordOwnedCardKoed(G, playerId, target, zone);
        koedIds.push(target);
    }
    return koedIds;
}
/**
 * Selects the highest-priority hero card ext_id in a zone, or null.
 *
 * Priority (D-20602 amends D-18503):
 *   1. Starting SHIELD cards (Trooper / Agent) — KO the worst cards first
 *      so auto-resolution acts as deck-thinning, not as a penalty.
 *   2. Among non-starting non-wound cards, fall back to ext_id lexical
 *      ascending (the original D-18503 tie-break).
 *
 * @param zone - A player's hand or discard zone.
 * @returns The KO target ext_id, or null when the zone has no hero card.
 */
// why: a "hero card" for KO purposes is any card that is NOT a wound token.
// Wounds are not Heroes — KO-a-Hero must not remove a wound (that would
// invert the penalty into a benefit). Starting SHIELD cards (cost 0, +1
// attack OR +1 recruit each) are the worst cards in any deck; preferring
// them first turns the auto-resolution into deck-thinning instead of
// silently KO-ing the player's best recruited heroes (recruited ext_ids
// like 'core/spider-man/...' sort lex-before 'starting-shield-...' under
// pure lex-asc, so the pre-D-20602 heuristic always picked good cards).
// Membership check uses a closed enum, so the rule remains NOT VP-based
// and reads no registry at runtime (D-18503 carries forward).
// Exported (WP-801 / D-24687) so the bot's discard-scoped Juggernaut branch
// picks within the discard pile directly; the body is unchanged.
export function selectKoHeroTarget(zone) {
    let startingSelected = null;
    let otherSelected = null;
    for (const candidate of zone) {
        if (candidate === WOUND_EXT_ID)
            continue;
        if (candidate === SHIELD_AGENT_EXT_ID || candidate === SHIELD_TROOPER_EXT_ID) {
            if (startingSelected === null || candidate < startingSelected) {
                startingSelected = candidate;
            }
        }
        else {
            if (otherSelected === null || candidate < otherSelected) {
                otherSelected = candidate;
            }
        }
    }
    return startingSelected ?? otherSelected;
}
/**
 * Selects the auto-KO target in a zone for a grey-filtered KO, or null (WP-836 / D-24769).
 *
 * Only cards passing `matchesGreyHeroFilter` are candidates. `'grey'` reuses
 * `selectKoHeroTarget` (starter-first, then lexical); `'non-grey'` takes the lowest-cost
 * candidate, ties broken by the lexically smallest ext_id.
 *
 * @param G - Game state (reads G.cardTraits, G.cardStats and the split-face map).
 * @param zoneCards - The zone's cards (not mutated).
 * @param greyFilter - The grey-Hero filter the KO is limited to.
 * @returns The KO target ext_id, or null when no card passes the filter.
 */
export function selectKoHeroTargetByGreyFilter(G, zoneCards, greyFilter) {
    const candidates = [];
    for (const candidate of zoneCards) {
        if (matchesGreyHeroFilter(G, candidate, greyFilter)) {
            candidates.push(candidate);
        }
    }
    if (greyFilter === 'grey') {
        return selectKoHeroTarget(candidates);
    }
    // why: D-24769 — "each player KOs a non-grey Hero" is each player's choice; the rational
    // pick is the cheapest one. The D-24644 starter-first rule cannot apply because starters
    // are grey. The bot and every other seat take this same pick (D-24009 parity).
    let selected = null;
    let selectedCost = 0;
    for (const candidate of candidates) {
        const cost = offPlayCardStats(G, candidate)?.cost ?? 0;
        if (selected === null || cost < selectedCost || (cost === selectedCost && candidate < selected)) {
            selected = candidate;
            selectedCost = cost;
        }
    }
    return selected;
}
// ---------------------------------------------------------------------------
// Interactive KO-a-Hero helpers (WP-242)
// ---------------------------------------------------------------------------
/**
 * Builds the deduped eligible KO-a-Hero targets across a player's allowed zones.
 *
 * Visits the zones in the fixed order discard, hand, inPlay, skipping any zone
 * not allowed — with no `allowedZones` the allowed zones are YOUR_HEROES_KO_ZONES
 * (hand + inPlay, D-24687). Each allowed zone is read in array index order,
 * emitting one target per non-wound card. Deduped by `(zone, cardId)` keeping the
 * first occurrence: two copies of the same ext_id in the same zone collapse to one
 * option (KOing either is outcome-identical), but the same ext_id in two
 * different zones stays two options (a hand-KO and an inPlay-KO differ).
 *
 * Used by the parker (count), the resolve move's forced remainder, and the
 * WP-243 projection. Fresh recompute every call — no snapshot (D-24007).
 *
 * @param zones - The player's card zones.
 * @param allowedZones - The zones the KO may take from; absent means "your Heroes".
 * @param greyFilterRead - An optional grey-Hero filter (D-24769); cards failing it are skipped.
 * @returns The eligible KO targets in deterministic scan order.
 */
export function buildKoEligibleTargets(zones, allowedZones, greyFilterRead) {
    // why: per-zone (zone, cardId) dedupe — the same ext_id can legitimately
    // appear twice within one zone (e.g., two starting-shield-agent in discard);
    // KOing any copy of that ext_id in that zone is outcome-identical, so one
    // option is shown per (zone, cardId) rather than N identical entries. Dedupe
    // is per-zone: the same ext_id in two zones stays two distinct options.
    const targets = [];
    const seen = new Set();
    const orderedZones = ['discard', 'hand', 'inPlay'];
    for (const zoneName of orderedZones) {
        // why: WP-577 — an optional zone allowlist restricts eligibility (Red Skull's
        // strike is hand-only). An absent allowlist means "your Heroes", hand + inPlay
        // (D-24687 amends the WP-577 / D-24386 default): the discard pile is skipped.
        if (!(allowedZones ?? YOUR_HEROES_KO_ZONES).includes(zoneName))
            continue;
        for (const cardId of zones[zoneName]) {
            // why: a wound is never a "hero" for KO purposes (D-18503 carries
            // forward); wounds are excluded even when sitting in an otherwise-valid zone.
            if (cardId === WOUND_EXT_ID)
                continue;
            if (greyFilterRead !== undefined &&
                !matchesGreyHeroFilter(greyFilterRead.gameState, cardId, greyFilterRead.greyFilter)) {
                continue;
            }
            const key = `${zoneName}:${cardId}`;
            if (seen.has(key))
                continue;
            seen.add(key);
            targets.push({ zone: zoneName, cardId });
        }
    }
    return targets;
}
/**
 * Counts a player's PHYSICAL KO-able heroes — every non-wound card in the allowed
 * zones (absent `allowedZones` ⇒ YOUR_HEROES_KO_ZONES, hand + inPlay, D-24687),
 * WITHOUT the per-(zone, cardId) dedupe `buildKoEligibleTargets` applies
 * (WP-492 / D-24298).
 *
 * The magnitude-N current-player KO (Whirlwind) parks an interactive choice only
 * when the player has MORE KO-able heroes than the count owed (a genuine choice of
 * WHICH to spare); when the physical count is ≤ the count owed the KO is forced
 * (every hero dies), so it auto-resolves with no prompt. Distinct from the deduped
 * option count: two identical copies are one option but two physical heroes.
 *
 * @param zones - The player's card zones.
 * @param allowedZones - The zones the KO may take from; absent means "your Heroes".
 * @param greyFilterRead - An optional grey-Hero filter (D-24769); cards failing it are not counted.
 * @returns The number of non-wound cards in the allowed zones.
 */
export function countKoableHeroes(zones, allowedZones, greyFilterRead) {
    let count = 0;
    const orderedZones = ['discard', 'hand', 'inPlay'];
    for (const zoneName of orderedZones) {
        // why: WP-577 — an optional zone allowlist restricts the count to the KO's
        // allowed zones (Red Skull is hand-only). An absent allowlist means "your
        // Heroes", hand + inPlay (D-24687 amends the WP-577 / D-24386 default).
        if (!(allowedZones ?? YOUR_HEROES_KO_ZONES).includes(zoneName))
            continue;
        for (const cardId of zones[zoneName]) {
            // why: a wound is never a "hero" for KO purposes (D-18503) — excluded here too.
            if (cardId === WOUND_EXT_ID)
                continue;
            if (greyFilterRead !== undefined &&
                !matchesGreyHeroFilter(greyFilterRead.gameState, cardId, greyFilterRead.greyFilter)) {
                continue;
            }
            count += 1;
        }
    }
    return count;
}
/**
 * Selects the single default KO target for an unqualified "KO one of your Heroes".
 *
 * Runs selectKoHeroTarget over inPlay, then hand, returning the first non-null
 * pick with its zone, or null when neither zone has an eligible hero. Never
 * reads the discard pile (D-24687).
 *
 * This is the bot default pick for a zoneless pending KO, and it is the
 * determinism anchor: it reuses the unchanged selectKoHeroTarget in the same
 * zone order as koOneHeroForPlayer, so the bot's KO target equals the
 * auto-resolution's (D-24009 parity).
 *
 * @param zones - The player's card zones.
 * @returns The default KO target, or null when no eligible hero exists.
 */
export function selectDefaultKoTarget(zones) {
    // why: inPlay first, then hand — the koOneHeroForPlayer order. A played Hero has
    // already produced its Recruit and Attack (rules v23 L723–L725), and the discard
    // tier is gone (D-24687 supersedes D-18503 / D-20603). selectKoHeroTarget owns the
    // within-zone starter-first tie-break (D-20602); reusing it verbatim keeps the bot
    // KO target equal to the auto-resolution's (D-24009 parity).
    const inPlayTarget = selectKoHeroTarget(zones.inPlay);
    if (inPlayTarget !== null) {
        return { zone: 'inPlay', cardId: inPlayTarget };
    }
    const handTarget = selectKoHeroTarget(zones.hand);
    if (handTarget !== null) {
        return { zone: 'hand', cardId: handTarget };
    }
    return null;
}
/**
 * KOs a single target card out of the named zone (the auto-1 mutation).
 *
 * The two-line mutation resolveKoHeroChoice also performs: moveCardFromZone the
 * cardId out of the zone, then koCard it on `found`. One copy here, one in the
 * move (§16.1 duplicate-twice — a third appearance would justify extracting it).
 *
 * @param G - Game state (mutated under Immer draft).
 * @param zones - The player's card zones (the source zone is shortened in place).
 * @param target - The { zone, cardId } to KO.
 * @param ownerPlayerID - The player whose zones these are.
 * @returns The KO'd card ext_id (WP-316 log target), or null when the move
 *   found no matching card.
 */
function koSingleTarget(G, zones, target, 
// why: WP-853 / D-24789 — PlayerZones carries no owner id, so the caller threads it
// in for the owned-KO count (Absorb Energies).
ownerPlayerID) {
    const moveResult = moveCardFromZone(zones[target.zone], [], target.cardId);
    if (moveResult.found) {
        zones[target.zone] = moveResult.from;
        G.ko = koCard(G.ko, target.cardId);
        recordOwnedCardKoed(G, ownerPlayerID, target.cardId, target.zone);
        return target.cardId;
    }
    return null;
}
//# sourceMappingURL=villainEffects.execute.js.map